import { AlertTriangle, ChevronDown, Eye, EyeOff } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axiosInstance from "../api/axiosInstance";
import {
  REVEALABLE_PII_FIELDS,
  revealAccountPii,
  revealMemberPii,
} from "../api/revealPiiAPI";
import {
  getBeneficiaryShareTotalErrorsFromForm,
  isBeneficiaryShareTotalComplete,
  isBeneficiaryShareTotalOver100,
} from "../api/updateHouseholdAPI";
import {
  DEFAULT_DIVIDEND_OPTION,
  DEPOSIT_METHOD_OPTIONS,
  DIVIDEND_OPTIONS,
  ESTATE_ACCOUNT_TYPES,
  FREQUENCY_OPTIONS,
  getAccountTypesForRegistration,
  getLiquidityNeedsLabel,
  getLiquidityTimeHorizonLabel,
  getTaxClassificationOptions,
  INHERITED_ACCOUNT_TYPES,
  LIQUIDITY_NEEDS_MAP,
  LIQUIDITY_NEEDS_OPTIONS,
  LIQUIDITY_TIME_HORIZON_MAP,
  LIQUIDITY_TIME_HORIZON_OPTIONS,
  PROCEEDS_OPTIONS,
  REGISTRATION_OPTIONS,
  SECURITY_PURCHASES_OPTIONS,
  TRUST_TAX_ID_TYPE_OPTIONS,
  TYPE_OF_TRANSFER_OPTIONS,
  WITHDRAWAL_METHOD_OPTIONS
} from "../constants/accountConstants";
import { deliveringFirmOptions } from "../constants/deliveringFirms";
import {
  ACCOUNT_TIME_HORIZON_MAP,
  ACCOUNT_TIME_HORIZON_OPTIONS,
  DECISION_MAKING_OPTIONS,
  getAccountRiskTimeHorizonLabel,
  NIGO_REASON_LABELS,
  PRIMARY_OBJECTIVE_OPTIONS,
  PRIMARY_USE_OPTIONS,
  RISK_TOLERANCE_OPTIONS,
  SOURCE_OF_FUNDS_OPTIONS,
  SOURCE_OF_WEALTH_OPTIONS,
  TAX_BRACKET_MAP,
  TAX_BRACKET_OPTIONS,
} from "../constants/householdConstants";
import {
  EMPLOYMENT_STATUS_OPTIONS,
  GENDER_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  normalizeEmploymentInfoForStatus,
  RELATIONSHIP_OPTIONS,
  shouldDisableEmploymentDetails,
  SUFFIX_OPTIONS,
  TITLE_OPTIONS,
} from "../constants/membersConstants";
import { COUNTRY_OPTIONS } from "../constants/projectConstants";
import { useConfirm } from "../context/ConfirmContext";
import { useNotifications } from "../context/NotificationContext";
import { useToast } from "../context/ToastContext";
import { US_STATES } from "../data/usStatesAndCities";
import { runRelationshipPrefill, useRelationshipPrefill } from "../hooks/useRelationshipPrefill";
import {
  buildAdvisorBankAccountsUpdate,
  canonicalBankAccountType,
  emptyBankOps,
  formatBankLabel,
  getMissingBankAccountsMap,
  hasUnsavedBankChanges,
  isBankLinkedToAccount,
  resolveVisibleBanks,
  stageFieldEdit,
  stageLinkExisting,
  stageUnlink,
} from "../utils/bankAccounts";
import {
  ALWAYS_EXPERIENCE_KEYS,
  computeBrokerageFlags,
  CONDITIONAL_EXPERIENCE_KEYS,
  showAccountRiskDetails,
  showAnnualExpenseRange,
  showBankingEstimates,
  showExperienceField,
  showGoldmanQualifiers,
  showHouseholdSourceOfWealth,
  showIdInfo,
  showIncomeDetails,
  showLiquidityRequirements,
  showMaritalStatus,
  showSourceOfFunds,
  showSpecialExpenseRange,
  showStatutoryTrust,
  showUseOfFunds
} from "../utils/brokerageFields";
import {
  getDateConstraintError,
  getDatePickerBoundsForConstraint,
} from "../utils/dateFieldConstraints";
import { resolveStateName } from "../utils/stateUtils";
import {
  formatAccountContextLabel,
  formatAccountName,
  formatCurrencyPreservingScale,
  formatDateForDisplay,
  formatDateForInput,
  isMaskedDob,
  isRetirementAccount,
  isSpecialRegistration,
  isTwoOwnerAccount,
} from "../utils/formatters";
import {
  getDestinationIdentifierLabel,
  getDestinationIdentifierOptions,
} from "../utils/destinationIdentifiers";
import { getUnmappedMembers, isCustodialMinorOwner } from "../utils/memberLinks";
import { getMemberMissingPills } from "../utils/memberMissingPills";
import {
  buildTrustInfoUpdatePayload,
  isRevocableTrust,
  finalizeTrusteesForPayload,
  getTrusteeMemberId,
  getTrustGrantorIds,
  isPlaceholderTrusteeRelationship,
} from "../utils/trustInfoHelpers";
import { humanizeError } from "../utils/userFriendlyError";
import {
  countSSNDigits,
  formatNumericInput,
  formatPhoneInput,
  formatSSNInput,
  formatZipInput,
  validateEmail,
  validateLplAccountNumber,
  validatePhoneDigits,
  validateSSNDigits,
  validateZipDigits,
} from "../utils/validationHelpers";
import AddAccountModal from "./AddAccountModal";
import AddBankAccountModal from "./AddBankAccountModal";
import AddContributionModal from "./AddContributionModal";
import AddMemberBeneficiaryTrustedModal from "./AddMemberBeneficiaryTrustedModal";
import AddMemberModal from "./AddMemberModal";
import AddWithdrawalModal from "./AddWithdrawalModal";
import BankAccountAccordion from "./BankAccountAccordion";
import DateInput from "./DateInput";
import DocumentProcessingStatus from "./DocumentProcessingStatus";
import EditBankAccountModal from "./EditBankAccountModal";
import ExistingAddressPicker from "./ExistingAddressPicker";
import { BFS_MAPPING } from "./HouseholdSetupWizard/WizardConstants";
import LinkBankAccountModal from "./LinkBankAccountModal";
import MissingAccountFieldModal from "./MissingAccountFieldModal";
import MissingHouseholdFieldModal from "./MissingHouseholdFieldModal";
import MissingMemberFieldModal from "./MissingMemberFieldModal";
import ResolveNigoAccountsModal from "./ResolveNigoAccountsModal";
import SelectBankAccountModal from "./SelectBankAccountModal";
import SelectMemberTypeModal from "./SelectMemberTypeModal";
import ShareSlider, { SharePresetButtons } from "./ShareSlider";
import StateCitySelect from "./StateCitySelect";
import MemberSelect from "./shared/MemberSelect";

const UnsavedChangesBanner = React.memo(({ message, className = "" }) => {
  return (
    <div
      className={`bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 ${className}`}
    >
      <div className="flex items-start gap-2">
        <svg
          className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <p>{message}</p>
      </div>
    </div>
  );
});

UnsavedChangesBanner.displayName = "UnsavedChangesBanner";

function advisorHouseholdUnsavedMessage(householdInfoSubTab) {
  switch (householdInfoSubTab) {
    case "qualifyingQuestions":
      return "These qualifying question changes are unsaved. Click Save to apply changes.";
    case "investmentExperience":
      return "These investment experience changes are unsaved. Click Save to apply changes.";
    case "feeStructure":
      return "These fee structure changes are unsaved. Click Save to apply changes.";
    case "incomeDetails":
    default:
      return "These income and household detail changes are unsaved. Click Save to apply changes.";
  }
}

function advisorMembersUnsavedMessage(membersSubTab, memberSection) {
  const who =
    membersSubTab === "owners"
      ? "this account owner"
      : "this additional member";
  const area = {
    personal: "personal details",
    contact: "contact details",
    employment: "employment details",
    id: "ID details",
  }[memberSection] || "details";
  return `These changes to ${who}'s ${area} are unsaved. Click Save to apply changes.`;
}

function advisorAccountUnsavedMessage(sectionKey) {
  switch (sectionKey) {
    case "investment_strategy":
      return "These investment strategy changes are unsaved. Click Save to apply changes.";
    case "transfer_details":
      return "These transfer detail changes are unsaved. Click Save to apply changes.";
    case "trust_account":
      return "These trust information changes are unsaved. Click Save to apply changes.";
    case "business_info":
      return "These business info changes are unsaved. Click Save to apply changes.";
    case "inheritance_info":
      return "These inheritance info changes are unsaved. Click Save to apply changes.";
    case "other_info":
      return "These other info changes are unsaved. Click Save to apply changes.";
    case "beneficiaries":
      return "These beneficiary changes are unsaved. Click Save to apply changes.";
    case "trusted_contacts":
      return "These trusted contact changes are unsaved. Click Save to apply changes.";
    case "contributions":
      return "These contribution changes are unsaved. Click Save to apply changes.";
    case "withdrawals":
      return "These withdrawal changes are unsaved. Click Save to apply changes.";
    case "bank_account_details":
      return "This bank account link change is unsaved. Click Save to apply changes.";
    case "basic_details":
    default:
      return "These basic account detail changes are unsaved. Click Save to apply changes.";
  }
}

/** True only if householdFormData has edits that belong to the active Household Info sub-tab. */
function householdSubTabHasUnsavedChanges(form, subTab) {
  if (!form || typeof form !== "object") return false;
  if (subTab === "incomeDetails") {
    return (
      form.annual_income !== undefined ||
      form.estimated_total_net_worth !== undefined ||
      form.estimated_liquid_net_worth !== undefined ||
      form.tax_info !== undefined ||
      form.annual_expense_range !== undefined ||
      form.special_expense_range !== undefined
    );
  }
  if (subTab === "qualifyingQuestions") {
    return form.investor_associations !== undefined;
  }
  if (subTab === "investmentExperience") {
    return form.investor_experience !== undefined;
  }
  if (subTab === "feeStructure") {
    return (
      form.is_fee_standard !== undefined ||
      form.custom_fee_percentage !== undefined
    );
  }
  return false;
}

function collectMemberLeafPaths(obj, prefix = "") {
  if (obj === null || obj === undefined) return [];
  if (typeof obj !== "object") {
    return prefix ? [prefix] : [];
  }
  if (Array.isArray(obj)) {
    const out = [];
    obj.forEach((item, i) => {
      const p = prefix ? `${prefix}.${i}` : String(i);
      out.push(...collectMemberLeafPaths(item, p));
    });
    return out;
  }
  const keys = Object.keys(obj);
  if (keys.length === 0) return [];
  const out = [];
  for (const k of keys) {
    const p = prefix ? `${prefix}.${k}` : k;
    const v = obj[k];
    if (v !== null && typeof v === "object" && !Array.isArray(v)) {
      out.push(...collectMemberLeafPaths(v, p));
    } else if (Array.isArray(v)) {
      out.push(...collectMemberLeafPaths(v, p));
    } else {
      out.push(p);
    }
  }
  return out;
}

function memberChangePathMatchesSection(path, section) {
  if (section === "personal") {
    return (
      path.startsWith("personal_info.") && !path.includes(".address.")
    );
  }
  if (section === "contact") {
    return path.includes("personal_info.address.");
  }
  if (section === "employment") {
    return path.startsWith("employment_info.");
  }
  if (section === "id") {
    return path.startsWith("id_info.");
  }
  return false;
}

/** True only if membersFormData has edits under this member for the active member section tab. */
function memberSectionHasUnsavedChanges(membersFormData, memberId, section) {
  const sub = membersFormData[memberId];
  if (!sub) return false;
  const paths = collectMemberLeafPaths(sub);
  return paths.some((p) => memberChangePathMatchesSection(p, section));
}

/** Same logic as the Bank Account Details inline view: pending link/unlink/edit/create until Save. */
function bankAccountLinkIsPending(account, getAccountFormValue) {
  const ops = getAccountFormValue(`${account.id}.bankAccountsOps`, null);
  return hasUnsavedBankChanges(ops);
}

/** Newly added withdrawal banks are stored globally by bank id; only flag this account if a withdrawal here references one. */
function accountWithdrawalsReferenceNewlyAddedBank({
  accountId,
  account,
  getAccountFormValue,
  pendingNewWithdrawals,
  newlyAddedWithdrawalBankAccounts,
}) {
  const ids = Object.keys(newlyAddedWithdrawalBankAccounts || {});
  if (ids.length === 0) return false;
  const idSet = new Set(ids);
  const base = Array.isArray(account?.withdrawals) ? account.withdrawals : [];
  for (let i = 0; i < base.length; i++) {
    const dest = getAccountFormValue(
      `${accountId}.withdrawals.${i}.destination_identifier`,
      base[i]?.destination_identifier,
    );
    if (dest != null && dest !== "" && idSet.has(String(dest))) return true;
  }
  for (const w of pendingNewWithdrawals[accountId] || []) {
    const d = w?.destination_identifier;
    if (d != null && d !== "" && idSet.has(String(d))) return true;
  }
  return false;
}

const BASIC_ACCOUNT_FORM_KEYS = new Set([
  "owners_info",
  "new_account_number",
  "custodian",
  "registration",
  "type",
  "fee_billed_percentage",
  "fee_based_account",
  "account_value",
  "proceeds",
  "security_purchases",
  "distribution_income",
  "margin",
  "options",
  "check_writing",
]);

/**
 * True only if there are pending edits for this account that belong to the
 * active Accounts sub-section (matches form + side state used at Save).
 */
function advisorAccountSectionHasUnsavedChanges({
  accountId,
  section,
  account,
  accountsFormData,
  getAccountFormValue,
  pendingNewDeposits,
  pendingNewWithdrawals,
  removedDeposits,
  removedWithdrawals,
  removedBeneficiaries,
  removedTrustedContacts,
  newlyAddedWithdrawalBankAccounts,
}) {
  const changes = accountsFormData[accountId] || {};
  const keys = Object.keys(changes);
  const hasKey = (k) => keys.includes(k);

  switch (section) {
    case "basic_details":
      return keys.some((k) => BASIC_ACCOUNT_FORM_KEYS.has(k));
    case "investment_strategy":
      return hasKey("risk_tolerance");
    case "transfer_details":
      return hasKey("transfer_info");
    case "trust_account":
      return hasKey("trust_info");
    case "business_info":
      return hasKey("business_info");
    case "inheritance_info":
      return hasKey("inheritance_info");
    case "other_info":
      return hasKey("other_info");
    case "beneficiaries":
      return (
        hasKey("beneficiaries") ||
        (removedBeneficiaries[accountId] || []).length > 0
      );
    case "trusted_contacts":
      return (
        hasKey("trusted_contacts") ||
        (removedTrustedContacts[accountId] || []).length > 0
      );
    case "contributions":
      return (
        hasKey("deposits") ||
        (pendingNewDeposits[accountId] || []).length > 0 ||
        (removedDeposits[accountId] || []).length > 0
      );
    case "withdrawals":
      return (
        hasKey("withdrawals") ||
        (pendingNewWithdrawals[accountId] || []).length > 0 ||
        (removedWithdrawals[accountId] || []).length > 0 ||
        accountWithdrawalsReferenceNewlyAddedBank({
          accountId,
          account,
          getAccountFormValue,
          pendingNewWithdrawals,
          newlyAddedWithdrawalBankAccounts,
        })
      );
    case "bank_account_details":
      return bankAccountLinkIsPending(account, getAccountFormValue);
    default:
      return false;
  }
}

// DataField component - defined outside to prevent recreation on every render
const DataField = React.memo(
  ({
    label,
    value,
    isEditable = false,
    type = "text",
    min = null,
    max = null,
    isEditing = false,
    options = null,
    allowCustom = false,
    isRange = false,
    rangeData = null,
    dateValue = null,
    fieldPath = null,
    onFieldChange = null,
    commitOnChange = false,
    getFormValue = null,
    disableWheel = false,
    error = null,
    onFieldError = null,
    dateConstraint = null,
    ssnLengthOnly = false,
  }) => {
    const resolveValue = useCallback(
      (path, fallback) => {
        if (!getFormValue || !path) return fallback;
        return getFormValue(path, fallback);
      },
      [getFormValue],
    );

    const resolvedValue = resolveValue(fieldPath, value);
    const resolvedDateValue = resolveValue(fieldPath, dateValue);
    const resolvedRangeStart = resolveValue(
      `${fieldPath}.start`,
      rangeData?.start,
    );
    const resolvedRangeEnd = resolveValue(`${fieldPath}.end`, rangeData?.end);

    const [localValue, setLocalValue] = useState("");
    const [localRangeStart, setLocalRangeStart] = useState("");
    const [localRangeEnd, setLocalRangeEnd] = useState("");
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [revealedValue, setRevealedValue] = useState(null);
    const [revealing, setRevealing] = useState(false);
    const hasLocalChange = useRef(false);
    const dropdownRef = useRef(null);
    const { toast } = useToast();

    const revealTarget = useMemo(() => {
      if (!fieldPath || (type !== "ssn" && type !== "date")) return null;
      const parts = String(fieldPath).split(".");
      if (parts.length !== 3) return null;
      const [entityId, section, field] = parts;
      if (!REVEALABLE_PII_FIELDS[section]?.includes(field)) return null;
      return { entityId, section, field };
    }, [fieldPath, type]);

    const valueIsMasked = useMemo(() => {
      if (type === "ssn") {
        return typeof resolvedValue === "string" && resolvedValue.includes("*");
      }
      if (type === "date") {
        return (
          typeof resolvedDateValue === "string" &&
          isMaskedDob(String(resolvedDateValue))
        );
      }
      return false;
    }, [type, resolvedValue, resolvedDateValue]);

    const showRevealToggle =
      !!revealTarget && (valueIsMasked || revealedValue !== null);

    const performReveal = useCallback(async () => {
      if (!revealTarget) return null;
      try {
        setRevealing(true);
        const { entityId, section, field } = revealTarget;
        const data =
          section === "personal_info"
            ? await revealMemberPii(entityId, field)
            : section === "employment_info"
              ? await revealMemberPii(entityId, field, section)
              : await revealAccountPii(entityId, section, field);
        const value = data?.value ?? "";
        setRevealedValue(value);
        return value;
      } catch (err) {
        toast.error(humanizeError(err, "Couldn't reveal this value."));
        return null;
      } finally {
        setRevealing(false);
      }
    }, [revealTarget, toast]);

    const handleRevealToggle = useCallback(async () => {
      if (revealedValue !== null) {
        setRevealedValue(null);
        return;
      }
      await performReveal();
    }, [revealedValue, performReveal]);

    const revealForPicker = useCallback(async () => {
      if (revealedValue !== null) return revealedValue;
      return performReveal();
    }, [revealedValue, performReveal]);

    const renderRevealButton = (positionClass = "right-0 pr-3") =>
      showRevealToggle ? (
        <button
          type="button"
          onClick={handleRevealToggle}
          disabled={revealing}
          className={`absolute inset-y-0 flex items-center text-gray-400 hover:text-gray-600 disabled:opacity-50 ${positionClass}`}
          tabIndex={-1}
          title={revealedValue !== null ? "Hide" : "Reveal"}
        >
          {revealedValue !== null ? (
            <EyeOff className="w-4 h-4" />
          ) : (
            <Eye className="w-4 h-4" />
          )}
        </button>
      ) : null;

    const deriveValue = useCallback(() => {
      if (type === "number") {
        return resolvedValue !== undefined &&
          resolvedValue !== null &&
          resolvedValue !== "N/A"
          ? formatNumericInput(resolvedValue.toString())
          : "";
      }
      if (type === "date") {
        if (resolvedDateValue && isMaskedDob(String(resolvedDateValue))) {
          return String(resolvedDateValue);
        }
        return resolvedDateValue ? formatDateForInput(resolvedDateValue) : "";
      }
      if (type === "ssn") {
        const raw = resolvedValue !== undefined && resolvedValue !== null ? String(resolvedValue) : "";
        return formatSSNInput(raw);
      }
      if (type === "tel" || type === "phone" || label?.toLowerCase().includes("phone")) {
        const raw = resolvedValue !== undefined && resolvedValue !== null ? String(resolvedValue) : "";
        return formatPhoneInput(raw);
      }
      if (label?.toLowerCase().includes("zip") || label?.toLowerCase().includes("postal")) {
        const raw = resolvedValue !== undefined && resolvedValue !== null ? String(resolvedValue) : "";
        return formatZipInput(raw);
      }
      return resolvedValue !== undefined &&
        resolvedValue !== null &&
        resolvedValue !== "N/A"
        ? resolvedValue
        : "";
    }, [resolvedValue, resolvedDateValue, type, label]);

    const lastFieldPathRef = useRef(fieldPath);
    useEffect(() => {
      if (lastFieldPathRef.current === fieldPath) return;
      lastFieldPathRef.current = fieldPath;
      hasLocalChange.current = false;
      setRevealedValue(null);
      setDropdownOpen(false);
      setSearchQuery("");
      setLocalValue(deriveValue());
      setLocalRangeStart(
        resolvedRangeStart !== undefined && resolvedRangeStart !== null
          ? resolvedRangeStart
          : "",
      );
      setLocalRangeEnd(
        resolvedRangeEnd !== undefined && resolvedRangeEnd !== null
          ? resolvedRangeEnd
          : "",
      );
    }, [fieldPath, deriveValue, resolvedRangeStart, resolvedRangeEnd]);

    useEffect(() => {
      if (!isEditing) {
        hasLocalChange.current = false;
        return;
      }
      if (!hasLocalChange.current) {
        setLocalValue(deriveValue());
        setLocalRangeStart(
          resolvedRangeStart !== undefined && resolvedRangeStart !== null
            ? resolvedRangeStart
            : "",
        );
        setLocalRangeEnd(
          resolvedRangeEnd !== undefined && resolvedRangeEnd !== null
            ? resolvedRangeEnd
            : "",
        );
      }
    }, [deriveValue, resolvedRangeStart, resolvedRangeEnd, isEditing]);

    useEffect(() => {
      if (!dropdownOpen) {
        setSearchQuery("");
        return;
      }
      const handleClickOutside = (e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
          setDropdownOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [dropdownOpen]);

    const [searchQuery, setSearchQuery] = useState("");

    const filteredOptions = useMemo(() => {
      if (!options) return [];
      if (!searchQuery) return options;
      return options.filter(opt => {
        const textToSearch = typeof opt === "object" ? `${opt.label} ${opt.value}` : String(opt);
        return textToSearch.toLowerCase().includes(searchQuery.toLowerCase());
      });
    }, [options, searchQuery]);

    const handleChange = (e) => {
      let val = e.target.value;
      if (type === "number") {
        val = formatNumericInput(val, true);
      } else if (type === "ssn") {
        val = formatSSNInput(val);
      } else if (type === "tel" || type === "phone" || label?.toLowerCase().includes("phone")) {
        val = formatPhoneInput(val);
      } else if (
        label?.toLowerCase().includes("zip") ||
        label?.toLowerCase().includes("postal")
      ) {
        val = formatZipInput(val);
      }
      if (revealedValue !== null) setRevealedValue(null);
      hasLocalChange.current = true;
      setLocalValue(val);
      if (commitOnChange && onFieldChange && fieldPath) {
        onFieldChange(fieldPath, val);
      }
    };

    const handleRangeStartChange = (e) => {
      let val = formatNumericInput(e.target.value);
      hasLocalChange.current = true;
      setLocalRangeStart(val);
    };

    const handleRangeEndChange = (e) => {
      let val = formatNumericInput(e.target.value);
      hasLocalChange.current = true;
      setLocalRangeEnd(val);
    };

    const handleBlur = (path, valueToPersist) => () => {
      let sanitizedValue = valueToPersist;

      if (type === "ssn" && sanitizedValue === "" && String(resolvedValue).includes('*')) {
        sanitizedValue = formatSSNInput(String(resolvedValue));
        setLocalValue(sanitizedValue);
      }

      // 1. Handle basic number validation (must be non-negative and cap at max if provided)
      if (
        type === "number" &&
        sanitizedValue !== "" &&
        sanitizedValue !== null
      ) {
        let num = parseFloat(sanitizedValue);
        if (num < 0) {
          num = 0;
          sanitizedValue = "0";
          setLocalValue("0");
        }
        if (max !== null && num > max) {
          const isYears = path && path.includes("investor_experience");
          const errorMessage = isYears
            ? `Years of experience cannot exceed ${max}.`
            : `${label} cannot exceed ${max}.`;
          if (onFieldError) onFieldError(path, errorMessage);
          return;
        } else if (onFieldError) {
          onFieldError(path, null);
        }
      }

      // 2. Handle range validation
      if (isRange) {
        if (path.endsWith(".end")) {
          const startValue = localRangeStart;
          const endValue = sanitizedValue;

          if (
            startValue !== "" &&
            endValue !== "" &&
            parseFloat(endValue) < parseFloat(startValue)
          ) {
            if (onFieldError) onFieldError(fieldPath, "Invalid Range");
            return;
          } else if (onFieldError) {
            onFieldError(fieldPath, null);
          }
        } else if (path.endsWith(".start")) {
          const startValue = sanitizedValue;
          const endValue = localRangeEnd;

          if (
            startValue !== "" &&
            endValue !== "" &&
            parseFloat(startValue) > parseFloat(endValue)
          ) {
            if (onFieldError) onFieldError(fieldPath, "Invalid Range");
            return;
          } else if (onFieldError) {
            onFieldError(fieldPath, null);
          }
        }
      }

      // 3. Validate sensitive fields
      if (onFieldError) {
        if (
          type === "ssn" &&
          sanitizedValue &&
          (ssnLengthOnly
            ? countSSNDigits(sanitizedValue) !== 9
            : !validateSSNDigits(sanitizedValue))
        ) {
          onFieldError(
            path,
            ssnLengthOnly ? "Tax ID must be 9 digits" : "Invalid SSN/TIN",
          );
        } else if (
          (type === "tel" || type === "phone" || label?.toLowerCase().includes("phone")) &&
          sanitizedValue &&
          !validatePhoneDigits(sanitizedValue)
        ) {
          onFieldError(path, "Incomplete Phone");
        } else if (
          (label?.toLowerCase().includes("zip") ||
            label?.toLowerCase().includes("postal")) &&
          sanitizedValue &&
          !validateZipDigits(sanitizedValue)
        ) {
          onFieldError(path, "Invalid Zip");
        } else {
          onFieldError(path, null);
        }
      }

      if (onFieldChange && path) {
        onFieldChange(path, sanitizedValue);
      }
    };

    return (
      <div className="py-2">
        <div className="flex justify-between items-center mb-0.5">
          <span className="text-sm font-medium text-gray-500">{label}</span>
          {error && isEditing && (
            <span className="text-[10px] text-red-500 font-bold bg-red-50 px-1.5 py-0.5 rounded border border-red-100 uppercase tracking-tight">
              Invalid
            </span>
          )}
        </div>
        {isEditing && isEditable ? (
          <div className="relative">
            {isRange ? (
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="number"
                  min={0}
                  placeholder="From"
                  value={localRangeStart}
                  onChange={handleRangeStartChange}
                  onBlur={handleBlur(`${fieldPath}.start`, localRangeStart)}
                  onWheel={
                    disableWheel ? (e) => e.currentTarget.blur() : undefined
                  }
                  onKeyDown={(e) => {
                    if (e.key === "-" || e.key === "e" || e.key === "E")
                      e.preventDefault();
                    if (e.key === "Enter") e.currentTarget.blur();
                  }}
                  className={`flex-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${error ? "border-red-500 bg-red-50/30" : "border-gray-300"}`}
                />
                <span className="text-gray-500">to</span>
                <input
                  type="number"
                  min={0}
                  placeholder="To"
                  value={localRangeEnd}
                  onChange={handleRangeEndChange}
                  onBlur={handleBlur(`${fieldPath}.end`, localRangeEnd)}
                  onWheel={
                    disableWheel ? (e) => e.currentTarget.blur() : undefined
                  }
                  onKeyDown={(e) => {
                    if (e.key === "-" || e.key === "e" || e.key === "E")
                      e.preventDefault();
                    if (e.key === "Enter") e.currentTarget.blur();
                  }}
                  className={`flex-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${error ? "border-red-500 bg-red-50/30" : "border-gray-300"}`}
                />
              </div>
            ) : options ? (
              <div ref={dropdownRef} className="relative mt-1">
                {allowCustom ? (
                  <>
                    <div className="relative">
                      <input
                        type="text"
                        value={localValue}
                        onChange={(e) => {
                          hasLocalChange.current = true;
                          setLocalValue(e.target.value);
                          setSearchQuery(e.target.value);
                          if (!dropdownOpen) setDropdownOpen(true);
                        }}
                        onFocus={() => {
                          setSearchQuery(localValue || "");
                          setDropdownOpen(true);
                        }}
                        onBlur={() => {
                          if (onFieldChange && fieldPath) onFieldChange(fieldPath, localValue);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.currentTarget.blur();
                            setDropdownOpen(false);
                          }
                        }}
                        placeholder={`Enter ${label.toLowerCase()}`}
                        className={`w-full px-3 py-2 pr-8 border rounded-md shadow-sm text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 ${error ? "border-red-500 bg-red-50/30" : "border-gray-300"}`}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setSearchQuery("");
                          setDropdownOpen((o) => !o);
                        }}
                        className="absolute inset-y-0 right-0 flex items-center pr-2"
                        tabIndex={-1}
                      >
                        <svg
                          className={`w-4 h-4 text-gray-400 transition-transform duration-150 ${dropdownOpen ? "rotate-180" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                    {dropdownOpen && filteredOptions.length > 0 && (
                      <ul className="absolute top-full left-0 z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                        {filteredOptions.map((option, index) => {
                          const optLabel = typeof option === "object" ? option.label : option;
                          const optValue = typeof option === "object" ? option.value : option;
                          return (
                            <li
                              key={index}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                hasLocalChange.current = true;
                                setLocalValue(optValue);
                                setDropdownOpen(false);
                                if (onFieldChange && fieldPath)
                                  onFieldChange(fieldPath, optValue);
                              }}
                              className={`px-3 py-2 text-sm cursor-pointer hover:bg-emerald-50 hover:text-emerald-700 ${localValue === optValue ? "bg-emerald-50 text-emerald-700 font-medium" : "text-gray-900"}`}
                            >
                              {optLabel}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </>
                ) : (
                  <>
                <button
                  type="button"
                  onClick={() => setDropdownOpen((o) => !o)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm text-left flex items-center justify-between bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 ${error ? "border-red-500 bg-red-50/30" : "border-gray-300"}`}
                >
                  <span
                    className={localValue ? "text-gray-900" : "text-gray-400"}
                  >
                    {(() => {
                      if (!localValue) return "Select";
                      if (!options) return localValue;
                      const found = options.find(o => typeof o === 'object' ? o.value === localValue : o === localValue);
                      return typeof found === 'object' ? found.label : localValue;
                    })()}
                  </span>
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform duration-150 ${dropdownOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {dropdownOpen && (
                  <div className="absolute top-full left-0 z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
                    <div className="p-2 border-b border-gray-100 sticky top-0 bg-white z-10">
                      <input
                        type="text"
                        className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <ul className="max-h-60 overflow-auto">
                      <li
                        onClick={() => {
                          hasLocalChange.current = true;
                          setLocalValue("");
                          setDropdownOpen(false);
                          if (onFieldChange && fieldPath)
                            onFieldChange(fieldPath, "");
                        }}
                        className="px-3 py-2 text-sm text-gray-400 cursor-pointer hover:bg-gray-50"
                      >
                        Select
                      </li>
                      {filteredOptions.map((option, index) => {
                        const optLabel = typeof option === "object" ? option.label : option;
                        const optValue = typeof option === "object" ? option.value : option;
                        return (
                          <li
                            key={index}
                            onClick={() => {
                              hasLocalChange.current = true;
                              setLocalValue(optValue);
                              setDropdownOpen(false);
                              if (onFieldChange && fieldPath)
                                onFieldChange(fieldPath, optValue);
                            }}
                            className={`px-3 py-2 text-sm cursor-pointer hover:bg-emerald-50 hover:text-emerald-700 ${localValue === optValue ? "bg-emerald-50 text-emerald-700 font-medium" : "text-gray-900"}`}
                          >
                            {optLabel}
                          </li>
                        );
                      })}
                      {filteredOptions.length === 0 && (
                        <li className="px-3 py-4 text-sm text-center text-gray-500 italic">
                          No results found
                        </li>
                      )}
                    </ul>
                  </div>
                )}
                  </>
                )}
              </div>
            ) : type === "date" ? (
              <div className="mt-1 w-full relative z-20">
                <DateInput
                  value={
                    revealedValue !== null
                      ? formatDateForInput(revealedValue)
                      : localValue
                  }
                  onRequestReveal={revealTarget ? revealForPicker : null}
                  onChange={(val) => {
                    if (revealedValue !== null) setRevealedValue(null);
                    hasLocalChange.current = true;
                    setLocalValue(val);
                    if (onFieldChange && fieldPath) {
                      onFieldChange(fieldPath, val);
                    }
                    if (onFieldError && fieldPath && dateConstraint) {
                      const err = getDateConstraintError(
                        val,
                        dateConstraint,
                        label,
                      );
                      onFieldError(fieldPath, err);
                    }
                  }}
                  placeholder="mm-dd-yyyy"
                  disabled={!isEditable || !isEditing}
                  error={!!error}
                  {...(dateConstraint
                    ? getDatePickerBoundsForConstraint(dateConstraint)
                    : {})}
                />
                {renderRevealButton("right-9")}
              </div>
            ) : (
              <div className="relative">
                <input
                  type={type === "ssn" ? "text" : type}
                  value={
                    type === "ssn" && revealedValue !== null
                      ? formatSSNInput(String(revealedValue))
                      : localValue
                  }
                  min={min}
                  max={max}
                  placeholder={type === "ssn" ? "XXX-XX-XXXX" : (type === "tel" || type === "phone" || label?.toLowerCase().includes("phone") ? "XXX-XXX-XXXX" : `Enter ${label.toLowerCase()}`)}
                  onChange={handleChange}
                  onBlur={handleBlur(fieldPath, localValue)}
                  onFocus={(e) => {
                    if (
                      type === "ssn" &&
                      revealedValue === null &&
                      String(localValue).includes("*")
                    ) {
                      setLocalValue("");
                    }
                  }}
                  onWheel={
                    disableWheel ? (e) => e.currentTarget.blur() : undefined
                  }
                  onKeyDown={(e) => {
                    if (type === "number") {
                      if (e.key === "-" || e.key === "e" || e.key === "E")
                        e.preventDefault();
                      if (e.key === "Enter") e.currentTarget.blur();
                    } else if (e.key === "Enter") {
                      e.currentTarget.blur();
                    }
                  }}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm ${
                    type === "number"
                      ? "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      : ""
                  } ${showRevealToggle ? "pr-10" : ""} ${error ? "border-red-500 bg-red-50/30" : "border-gray-300"}`}
                />
                {renderRevealButton()}
              </div>
            )
}
            {error && (
              <p className="text-[11px] text-red-600 mt-1 font-medium ml-1 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                {error}
              </p>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 mt-1">
            <p
              className={`text-sm flex-1 ${
                isEditing && !isEditable
                  ? "text-gray-500 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 cursor-not-allowed"
                  : "text-gray-900"
              }`}
            >
              {(() => {
                if (revealedValue !== null) {
                  if (type === "ssn")
                    return revealedValue
                      ? formatSSNInput(String(revealedValue))
                      : "N/A";
                  if (type === "date")
                    return revealedValue
                      ? formatDateForDisplay(revealedValue)
                      : "N/A";
                  return revealedValue || "N/A";
                }
                return type === "ssn"
                  ? (resolvedValue ? formatSSNInput(String(resolvedValue)) : "N/A")
                  : (type === "tel" || type === "phone" || label?.toLowerCase().includes("phone"))
                  ? (resolvedValue ? formatPhoneInput(String(resolvedValue)) : "N/A")
                  : (resolvedValue || "N/A");
              })()}
            </p>
            {showRevealToggle && (
              <button
                type="button"
                onClick={handleRevealToggle}
                disabled={revealing}
                className="shrink-0 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                title={revealedValue !== null ? "Hide" : "Reveal"}
              >
                {revealedValue !== null ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            )}
          </div>
        )}
      </div>
    );
  },
);

DataField.displayName = "DataField";

// BeneficiaryCard component - memoized to prevent re-renders and focus loss
const BeneficiaryCard = React.memo(
  ({
    beneficiary,
    originalIdx,
    accountId,
    isEditingAccounts,
    accountsFormData,
    handleAccountFieldChange,
    getMemberNameById,
    household,
    accountOwnersInfo,
    handleRemoveBeneficiary,
  }) => {
    // Derive values from props
    const memberIdValue =
      accountsFormData[accountId]?.beneficiaries?.[originalIdx]
        ?.household_member_id ?? beneficiary.household_member_id;
    const typeValue =
      accountsFormData[accountId]?.beneficiaries?.[originalIdx]?.type ??
      beneficiary.type;
    const shareValue =
      accountsFormData[accountId]?.beneficiaries?.[originalIdx]?.share ??
      beneficiary.share;
    const relationshipValue =
      accountsFormData[accountId]?.beneficiaries?.[originalIdx]?.relationship ??
      beneficiary.relationship;

    useRelationshipPrefill({
      enabled: isEditingAccounts,
      householdId: household?.id,
      memberId: memberIdValue,
      accountId,
      currentValue: relationshipValue,
      onPrefill: (relationship) =>
        handleAccountFieldChange(
          `${accountId}.beneficiaries.${originalIdx}.relationship`,
          relationship,
        ),
    });

    // Handle share change from slider
    const handleShareChange = (value) => {
      handleAccountFieldChange(
        `${accountId}.beneficiaries.${originalIdx}.share`,
        value,
      );
    };

    return (
      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Member
          </label>
          {isEditingAccounts ? (
            <select
              value={memberIdValue || ""}
              onChange={(e) =>
                handleAccountFieldChange(
                  `${accountId}.beneficiaries.${originalIdx}.household_member_id`,
                  e.target.value,
                )
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            >
              <option value="">Select member</option>
              {(household.members || []).map((m) => {
                const isOwner = accountOwnersInfo?.some(
                  (owner) => owner.id === m.id,
                );
                return (
                  <option
                    key={m.id}
                    value={m.id}
                    disabled={isOwner}
                    className={isOwner ? "text-gray-400" : ""}
                  >
                    {getMemberNameById(m.id)}
                    {isOwner ? " (Owner - Cannot be beneficiary)" : ""}
                  </option>
                );
              })}
            </select>
          ) : (
            <p className="text-sm text-gray-900">
              {getMemberNameById(beneficiary.household_member_id)}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type
          </label>
          {isEditingAccounts ? (
            <select
              value={typeValue || ""}
              onChange={(e) =>
                handleAccountFieldChange(
                  `${accountId}.beneficiaries.${originalIdx}.type`,
                  e.target.value,
                )
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            >
              <option value="">Select type</option>
              <option value="primary">Primary</option>
              <option value="contingent">Contingent</option>
            </select>
          ) : (
            <p className="text-sm text-gray-900">
              {beneficiary.type === "primary"
                ? "Primary"
                : beneficiary.type === "contingent"
                  ? "Contingent"
                  : beneficiary.type || "N/A"}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Relationship to Account Owner
          </label>
          {isEditingAccounts ? (
            <select
              value={relationshipValue || ""}
              onChange={(e) =>
                handleAccountFieldChange(
                  `${accountId}.beneficiaries.${originalIdx}.relationship`,
                  e.target.value,
                )
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            >
              <option value="">Select relationship to account owner</option>
              {RELATIONSHIP_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-gray-900">
              {beneficiary.relationship || "N/A"}
            </p>
          )}
        </div>
        <div>
          <div className="flex items-center gap-4 mb-1">
            <label className="text-sm font-medium text-gray-700">Share %</label>
            {isEditingAccounts && (
              <SharePresetButtons
                value={shareValue ?? 0}
                onChange={handleShareChange}
              />
            )}
          </div>
          {isEditingAccounts ? (
            <ShareSlider value={shareValue ?? 0} onChange={handleShareChange} />
          ) : (
            <p className="text-sm text-gray-900">
              {beneficiary.share != null ? `${beneficiary.share}%` : "N/A"}
            </p>
          )}
        </div>
        {isEditingAccounts && (
          <button
            type="button"
            onClick={() => handleRemoveBeneficiary(accountId, beneficiary.id)}
            className="w-full px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors"
          >
            Remove
          </button>
        )}
      </div>
    );
  },
);

BeneficiaryCard.displayName = "BeneficiaryCard";

// TrustedContactCard component - memoized to prevent re-renders and focus loss
const TrustedContactCard = React.memo(
  ({
    tc,
    originalIdx,
    accountId,
    isEditingAccounts,
    accountsFormData,
    handleAccountFieldChange,
    getMemberNameById,
    household,
    accountOwnersInfo,
    handleRemoveTrustedContact,
  }) => {
    const memberIdValue =
      accountsFormData[accountId]?.trusted_contacts?.[originalIdx]
        ?.household_member_id ?? tc.household_member_id;
    const tcRelationshipValue =
      accountsFormData[accountId]?.trusted_contacts?.[originalIdx]?.relationship ??
      tc.relationship;

    useRelationshipPrefill({
      enabled: isEditingAccounts,
      householdId: household?.id,
      memberId: memberIdValue,
      accountId,
      currentValue: tcRelationshipValue,
      onPrefill: (relationship) =>
        handleAccountFieldChange(
          `${accountId}.trusted_contacts.${originalIdx}.relationship`,
          relationship,
        ),
    });

    return (
      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Member
          </label>
          {isEditingAccounts ? (
            <select
              value={memberIdValue || ""}
              onChange={(e) =>
                handleAccountFieldChange(
                  `${accountId}.trusted_contacts.${originalIdx}.household_member_id`,
                  e.target.value,
                )
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            >
              <option value="">Select member</option>
              {(household.members || []).map((m) => {
                const isOwner = accountOwnersInfo?.some(
                  (owner) => owner.id === m.id,
                );
                return (
                  <option
                    key={m.id}
                    value={m.id}
                    disabled={isOwner}
                    className={isOwner ? "text-gray-400" : ""}
                  >
                    {getMemberNameById(m.id)}
                    {isOwner ? " (Owner - Cannot be trusted contact)" : ""}
                  </option>
                );
              })}
            </select>
          ) : (
            <p className="text-sm text-gray-900">
              {getMemberNameById(tc.household_member_id)}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Relationship to Account Owner
          </label>
          {isEditingAccounts ? (
            <select
              value={tcRelationshipValue || ""}
              onChange={(e) =>
                handleAccountFieldChange(
                  `${accountId}.trusted_contacts.${originalIdx}.relationship`,
                  e.target.value,
                )
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
            >
              <option value="">Select relationship to account owner</option>
              {RELATIONSHIP_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-gray-900">{tc.relationship || "N/A"}</p>
          )}
        </div>
        {isEditingAccounts && (
          <button
            type="button"
            onClick={() => handleRemoveTrustedContact(accountId, tc.id)}
            className="w-full px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors"
          >
            Remove
          </button>
        )}
      </div>
    );
  },
);

TrustedContactCard.displayName = "TrustedContactCard";

/** Account value feeds the derived household AUM, so show it in Basic Details for every account. */
function shouldShowAccountValueField() {
  return true;
}

function HouseholdContent({
  household,
  onHouseholdUpdate,
  missingFields = null,
  aumAvailability,
  custodianTypes,
  custodian,
  destinationFirm,
}) {
  const onHouseholdUpdateRef = useRef(onHouseholdUpdate);
  onHouseholdUpdateRef.current = onHouseholdUpdate;
  const primaryMember = useMemo(
    () => household?.members?.find((m) => m.is_primary),
    [household?.members],
  );
  const primaryName = primaryMember
    ? `${primaryMember.personal_info?.first_name || ""} ${primaryMember.personal_info?.last_name || ""} ${primaryMember.personal_info?.suffix || ""}`.trim().replace(/\s+/g, " ")
    : "Primary Member";

  const brokerageFlags = computeBrokerageFlags({
    custodian: custodian ?? household?.custodian,
    custodianTypes: custodianTypes ?? household?.custodian_types,
    destinationFirm: destinationFirm ?? household?.destination_firm,
  });
  const hasVisibleExperienceFields = [
    ...ALWAYS_EXPERIENCE_KEYS,
    ...CONDITIONAL_EXPERIENCE_KEYS,
  ].some((key) => showExperienceField(key, brokerageFlags, household?.accounts));

  const isFeeBasedEditable = !custodianTypes || (custodianTypes.iws === true && custodianTypes.nfs === true);
  const { toast } = useToast();
  const confirmDialog = useConfirm();
  const [activeTab, setActiveTab] = useState("household");
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingMembers, setIsEditingMembers] = useState(false);
  const [isEditingAccounts, setIsEditingAccounts] = useState(false);
  const [householdFormData, setHouseholdFormData] = useState({});
  const [membersFormData, setMembersFormData] = useState({});
  const [accountsFormData, setAccountsFormData] = useState({});
  const [savingHousehold, setSavingHousehold] = useState(false);
  const [savingMembers, setSavingMembers] = useState(false);
  const [savingAccounts, setSavingAccounts] = useState(false);
  const [clearingClientNote, setClearingClientNote] = useState(false);
  const [showMissingFieldModal, setShowMissingFieldModal] = useState(false);
  const [selectedMissingField, setSelectedMissingField] = useState(null);
  const [showMissingMemberFieldModal, setShowMissingMemberFieldModal] =
    useState(false);
  const [selectedMemberField, setSelectedMemberField] = useState(null);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [showMissingAccountFieldModal, setShowMissingAccountFieldModal] =
    useState(false);
  const [selectedAccountField, setSelectedAccountField] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedBankAccountId, setSelectedBankAccountId] = useState(null);
  const [missingFieldsExpanded, setMissingFieldsExpanded] = useState(true);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [showResolveNigoModal, setShowResolveNigoModal] = useState(false);
  const [showBeneficiaryTrustedModal, setShowBeneficiaryTrustedModal] =
    useState(false);
  const [showSelectMemberTypeModal, setShowSelectMemberTypeModal] =
    useState(false);
  const [memberTypeModalType, setMemberTypeModalType] = useState(null); // 'beneficiary' or 'trusted_contact'
  const [newlyCreatedMemberId, setNewlyCreatedMemberId] = useState(null);
  const [accountForBeneficiary, setAccountForBeneficiary] = useState(null);
  const [beneficiaryPreSelectRole, setBeneficiaryPreSelectRole] = useState(null);
  const [docsStatus, setDocsStatus] = useState(null);
  const docsWereProcessing = useRef(false);

  // Fetch document processing status — reusable so SSE listeners can trigger it too
  const fetchDocsStatus = useCallback(async () => {
    if (!household?.id) return;
    try {
      const response = await axiosInstance.get(
        `/households/${household.id}/docs-status`,
      );
      setDocsStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch docs status:", error);
    }
  }, [household?.id]);

  // Subscribe to SSE notifications (document processed, transcript, audio,
  // household enrichment) so the page refreshes immediately when events target
  // this household. NotificationContext fires listeners on both SSE events and polls.
  const { onDocumentProcessed } = useNotifications();
  useEffect(() => {
    const unsubscribe = onDocumentProcessed(
      `household-content-${household?.id}`,
      (notifs) => {
        const list = Array.isArray(notifs)
          ? notifs
          : notifs != null
            ? [notifs]
            : [];
        const hid = household?.id;
        if (!hid || list.length === 0) return;
        const relevant = list.filter((n) => {
          const target = n?.household_id ?? n?.payload?.household_id;
          if (target == null) return true;
          return String(target) === String(hid);
        });
        if (relevant.length === 0) return;
        if (onHouseholdUpdateRef.current) {
          onHouseholdUpdateRef.current(null);
        }
        fetchDocsStatus();
      },
    );
    return unsubscribe;
  }, [household?.id, onDocumentProcessed, fetchDocsStatus]);

  const handleClearClientNote = async () => {
    if (!household?.id || !household?.client_note || clearingClientNote) return;
    setClearingClientNote(true);
    try {
      await axiosInstance.delete(`/households/${household.id}/client-note`);
      onHouseholdUpdateRef.current?.(null);
    } catch (e) {
      console.error("Failed to clear client note:", e);
    } finally {
      setClearingClientNote(false);
    }
  };

  const confirmClearClientNote = () => {
    if (!household?.client_note || household.client_note.trim() === "") return;
    openDeleteConfirm(
      {
        title: "Delete client note?",
        message:
          "This note will be removed and cannot be shown again. Are you sure you want to delete it?",
        confirmLabel: "Delete",
      },
      handleClearClientNote,
    );
  };
  const [expandedSections, setExpandedSections] = useState({
    incomeDetails: true,
    qualifyingQuestions: false,
    investmentExperience: false,
    owners: true,
    additionalMembers: true,
  });

  const [expandedMembers, setExpandedMembers] = useState({});
  const [expandedMemberSections, setExpandedMemberSections] = useState({});
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [expandedAccountSections, setExpandedAccountSections] = useState({});
  const [expandedBankAccounts, setExpandedBankAccounts] = useState({});

  // Tab-based sub-view state (persisted per main tab when switching)
  const [householdInfoSubTab, setHouseholdInfoSubTab] =
    useState("incomeDetails");

  useEffect(() => {
    if (
      householdInfoSubTab === "investmentExperience" &&
      !hasVisibleExperienceFields
    ) {
      setHouseholdInfoSubTab("incomeDetails");
    }
  }, [householdInfoSubTab, hasVisibleExperienceFields]);
  const [membersSubTab, setMembersSubTab] = useState("owners");
  const [selectedOwnerId, setSelectedOwnerId] = useState(null);
  const [selectedOwnerSection, setSelectedOwnerSection] = useState("personal");
  const [selectedAddnMemberId, setSelectedAddnMemberId] = useState(null);
  const [selectedAddnMemberSection, setSelectedAddnMemberSection] =
    useState("personal");
  const [selectedAccountSectionByAccount, setSelectedAccountSectionByAccount] =
    useState({});
  const [showAddBankAccountModal, setShowAddBankAccountModal] = useState(false);
  const [showLinkBankAccountModal, setShowLinkBankAccountModal] =
    useState(false);
  const [newBankAccountId, setNewBankAccountId] = useState(null);
  const [newBankAccountName, setNewBankAccountName] = useState("");
  const [showAddContributionModal, setShowAddContributionModal] =
    useState(false);
  const [showAddWithdrawalModal, setShowAddWithdrawalModal] = useState(false);
  const [accountForMoneyMovement, setAccountForMoneyMovement] = useState(null);
  const [pendingNewDeposits, setPendingNewDeposits] = useState({});
  const [pendingNewWithdrawals, setPendingNewWithdrawals] = useState({});
  const [removedDeposits, setRemovedDeposits] = useState({});
  const [removedWithdrawals, setRemovedWithdrawals] = useState({});
  const [removedBeneficiaries, setRemovedBeneficiaries] = useState({});
  const [removedTrustedContacts, setRemovedTrustedContacts] = useState({});
  /** Per-account messages after save blocked by beneficiary % rules (advisor accounts tab). */
  const [beneficiaryValidationErrorsByAccount, setBeneficiaryValidationErrorsByAccount] =
    useState({});
  const [showSelectBankAccountModal, setShowSelectBankAccountModal] =
    useState(false);
  const [accountForBankLinking, setAccountForBankLinking] = useState(null);
  const [deletingMemberId, setDeletingMemberId] = useState(null);
  const [deletingBankAccountId, setDeletingBankAccountId] = useState(null);
  const [deletingAccountId, setDeletingAccountId] = useState(null);

  /** Members already used as beneficiaries on this account (any type) — hide from Add Beneficiary picker. */
  const beneficiaryMemberIdsExcludedFromSelectModal = useMemo(() => {
    if (!accountForBeneficiary || memberTypeModalType !== "beneficiary") {
      return [];
    }
    const account = household?.accounts?.find((a) => a.id === accountForBeneficiary);
    if (!account) return [];
    const removed = new Set(removedBeneficiaries[accountForBeneficiary] || []);
    const ids = new Set();
    (account.beneficiaries || []).forEach((b, originalIdx) => {
      if (removed.has(b.id)) return;
      const formMember =
        accountsFormData[accountForBeneficiary]?.beneficiaries?.[originalIdx]
          ?.household_member_id;
      const memberId =
        formMember !== undefined &&
        formMember !== null &&
        formMember !== ""
          ? formMember
          : b.household_member_id;
      if (memberId) ids.add(memberId);
    });
    return [...ids];
  }, [
    accountForBeneficiary,
    memberTypeModalType,
    household?.accounts,
    removedBeneficiaries,
    accountsFormData,
  ]);

  const trustedContactMemberIdsExcludedFromSelectModal = useMemo(() => {
    if (!accountForBeneficiary || memberTypeModalType !== "trusted_contact") {
      return [];
    }
    const account = household?.accounts?.find((a) => a.id === accountForBeneficiary);
    if (!account) return [];
    const removed = new Set(removedTrustedContacts[accountForBeneficiary] || []);
    const ids = new Set();
    (account.trusted_contacts || []).forEach((tc, originalIdx) => {
      if (removed.has(tc.id)) return;
      const formMember =
        accountsFormData[accountForBeneficiary]?.trusted_contacts?.[originalIdx]
          ?.household_member_id;
      const memberId =
        formMember !== undefined &&
        formMember !== null &&
        formMember !== ""
          ? formMember
          : tc.household_member_id;
      if (memberId) ids.add(memberId);
    });
    return [...ids];
  }, [
    accountForBeneficiary,
    memberTypeModalType,
    household?.accounts,
    removedTrustedContacts,
    accountsFormData,
  ]);

  const openDeleteConfirm = useCallback(
    async (config, action) => {
      const ok = await confirmDialog({
        title: config.title,
        message: config.message,
        confirmLabel: config.confirmLabel || "Remove",
      });
      if (ok && typeof action === "function") action();
    },
    [confirmDialog],
  );
  const [showEditBankAccountModal, setShowEditBankAccountModal] = useState(false);
  const [bankAccountToEdit, setBankAccountToEdit] = useState(null);
  // State for withdrawal bank account selection
  const [showWithdrawalBankSelectModal, setShowWithdrawalBankSelectModal] =
    useState(false);
  const [showWithdrawalAddBankModal, setShowWithdrawalAddBankModal] =
    useState(false);
  const [withdrawalForBankSelection, setWithdrawalForBankSelection] =
    useState(null); // { accountId, withdrawalIdx }
  const [
    newlyAddedWithdrawalBankAccounts,
    setNewlyAddedWithdrawalBankAccounts,
  ] = useState({}); // { bankAccountId: { id, name } }

  // State for unlinked data popup
  const [unmappedDataExpanded, setUnmappedDataExpanded] = useState(false);
  const [showLinkUnlinkedBankModal, setShowLinkUnlinkedBankModal] =
    useState(false);
  const [selectedUnlinkedBankAccountId, setSelectedUnlinkedBankAccountId] =
    useState(null);
  const [selectedUnlinkedBankName, setSelectedUnlinkedBankName] = useState("");
  const [showLinkUnlinkedMemberModal, setShowLinkUnlinkedMemberModal] =
    useState(false);
  const [selectedUnlinkedMemberId, setSelectedUnlinkedMemberId] =
    useState(null);

  const [validationErrors, setValidationErrors] = useState({});

  const handleFieldError = useCallback((fieldPath, errorMessage) => {
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      if (errorMessage) {
        newErrors[fieldPath] = errorMessage;
      } else {
        delete newErrors[fieldPath];
      }
      return newErrors;
    });
  }, []);

  // Compute unmapped beneficiaries
  const unmappedBeneficiaries = useMemo(
    () => getUnmappedMembers(household?.members, household?.accounts),
    [household],
  );

  const accountsMissingBeneficiary = useMemo(
    () =>
      (household?.accounts || [])
        .map((account, index) => ({ account, index }))
        .filter(
          ({ account }) =>
            !isSpecialRegistration(account.registration) &&
            (account.beneficiaries || []).length === 0,
        ),
    [household],
  );

  // Normalize bank accounts to a safe array
  const householdBankAccounts = useMemo(() => {
    const raw = household?.bank_accounts;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    // Wrap any non-array, non-string value into an array to keep downstream logic simple
    return [raw];
  }, [household?.bank_accounts]);

  // Compute unmapped bank accounts
  const unmappedBankAccounts = useMemo(() => {
    if (!household?.accounts) return [];
    const list = Array.isArray(householdBankAccounts)
      ? householdBankAccounts
      : householdBankAccounts
        ? [householdBankAccounts]
        : [];
    if (!list.length) return [];

    return list.filter((ba) => {
      const isLinked = household.accounts.some((account) => {
        const inLinks = isBankLinkedToAccount(account, ba.id);
        const inWithdrawals = account.withdrawals?.some(
          (w) => w.destination_identifier === ba.id,
        );
        return inLinks || inWithdrawals;
      });
      return !isLinked;
    });
  }, [household, householdBankAccounts]);

  // Expand all accounts by default when household data is available
  useEffect(() => {
    if (household?.accounts && household.accounts.length > 0) {
      const accountsExpanded = {};
      const accountSectionsExpanded = {};

      household.accounts.forEach((account, index) => {
        const accountId = account.id || index;
        accountsExpanded[accountId] = true;

        // Expand all required account sections by default
        accountSectionsExpanded[`${accountId}_account_info`] = true;
        accountSectionsExpanded[`${accountId}_money_movement`] = true;
        accountSectionsExpanded[`${accountId}_basic_details`] = true;
        accountSectionsExpanded[`${accountId}_investment_strategy`] = true;
        accountSectionsExpanded[`${accountId}_transfer_details`] = true;
        accountSectionsExpanded[`${accountId}_contributions`] = true;
        accountSectionsExpanded[`${accountId}_withdrawals`] = true;
        accountSectionsExpanded[`${accountId}_bank_account_details`] = true;
      });

      setExpandedAccounts((prev) => {
        // Merge with existing state, but ensure all current accounts are expanded
        return { ...prev, ...accountsExpanded };
      });

      setExpandedAccountSections((prev) => {
        // Merge with existing state, but ensure all current account sections are expanded
        return { ...prev, ...accountSectionsExpanded };
      });
    }
  }, [household?.accounts]);

  // Default tab selection when household data loads: first section per main tab
  useEffect(() => {
    if (!household) return;
    const owners = household.members
      ? household.members.filter((m) => isOwner(m.id))
      : [];
    const addnMembers = household.members
      ? household.members.filter((m) => !isOwner(m.id))
      : [];
    const accounts = household.accounts || [];
    setSelectedOwnerId((prev) => {
      if (owners.length === 0) return null;
      if (prev && owners.some((m) => m.id === prev)) return prev;
      return owners[0]?.id ?? null;
    });
    setSelectedAddnMemberId((prev) => {
      if (addnMembers.length === 0) return null;
      if (prev && addnMembers.some((m) => m.id === prev)) return prev;
      return addnMembers[0]?.id ?? null;
    });
    setSelectedAccountId((prev) => {
      if (accounts.length === 0) return null;
      const firstId = accounts[0]?.id ?? 0;
      if (prev !== null && accounts.some((a, i) => (a.id || i) === prev))
        return prev;
      return firstId;
    });
  }, [household?.id, household?.members, household?.accounts]);

  // Poll document processing status
  useEffect(() => {
    if (!household?.id) return;

    fetchDocsStatus();

    // Set up polling interval
    const pollInterval = setInterval(() => {
      fetchDocsStatus();
    }, 60000); // Poll every 60 seconds

    // Cleanup on unmount
    return () => {
      clearInterval(pollInterval);
    };
  }, [household?.id, fetchDocsStatus]);

  // When all documents finish processing, refresh household data to pick up
  // enriched fields (e.g. newly extracted bank accounts).
  useEffect(() => {
    if (!household?.id || !docsStatus) return;

    const allDone = docsStatus.total > 0 && docsStatus.total === docsStatus.completed;

    if (allDone && docsWereProcessing.current) {
      docsWereProcessing.current = false;
      if (onHouseholdUpdateRef.current) {
        onHouseholdUpdateRef.current(null);
      }
    } else if (!allDone && docsStatus.total > 0) {
      docsWereProcessing.current = true;
    }
  }, [docsStatus, household?.id]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleMember = (memberId) => {
    setExpandedMembers((prev) => ({
      ...prev,
      [memberId]: !prev[memberId],
    }));
  };

  const toggleMemberSection = (memberId, section) => {
    const key = `${memberId}_${section}`;
    setExpandedMemberSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleAccount = (accountId) => {
    setExpandedAccounts((prev) => ({
      ...prev,
      [accountId]: !prev[accountId],
    }));
  };

  const toggleAccountSection = (accountId, section) => {
    const key = `${accountId}_${section}`;
    setExpandedAccountSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleBankAccount = (bankAccountId) => {
    setExpandedBankAccounts((prev) => ({
      ...prev,
      [bankAccountId]: !prev[bankAccountId],
    }));
  };

  const performDeleteBankAccount = async (bankAccountId) => {
    if (!household?.id) return;
    setDeletingBankAccountId(bankAccountId);
    try {
      await axiosInstance.delete(
        `/households/${household.id}/bank-account/${bankAccountId}`,
        { headers: { "Content-Type": "application/json" } },
      );
      if (onHouseholdUpdate) {
        onHouseholdUpdate(null);
      }
    } catch (err) {
      toast.error(humanizeError(err, "Couldn't delete bank account."));
    } finally {
      setDeletingBankAccountId(null);
    }
  };

  const handleDeleteBankAccount = (bankAccountId) => {
    if (!household?.id) return;
    openDeleteConfirm(
      {
        title: "Delete bank account?",
        message:
          "This permanently removes this bank account from the household. This action cannot be undone.",
        confirmLabel: "Delete",
      },
      () => {
        void performDeleteBankAccount(bankAccountId);
      },
    );
  };

  const performDeleteAccount = async (accountId) => {
    if (!household?.id || !accountId) return;
    setDeletingAccountId(accountId);
    try {
      await axiosInstance.delete(`/households/accounts/${accountId}`, {
        headers: { "Content-Type": "application/json" },
      });
      if (onHouseholdUpdate) {
        onHouseholdUpdate(null);
      }
    } catch (err) {
      toast.error(humanizeError(err, "Couldn't delete account."));
    } finally {
      setDeletingAccountId(null);
    }
  };

  const handleDeleteAccount = (accountId) => {
    if (!household?.id || !accountId) return;
    openDeleteConfirm(
      {
        title: "Delete account?",
        message:
          "This will remove the account from this household. This action cannot be undone.",
        confirmLabel: "Delete",
      },
      () => {
        void performDeleteAccount(accountId);
      },
    );
  };

  // Mark contribution for removal (applied when user clicks Save)
  const handleDeleteDeposit = (accountId, depositId) => {
    openDeleteConfirm(
      {
        title: "Remove contribution?",
        message:
          "This contribution will be marked for removal. Changes apply when you save.",
        confirmLabel: "Remove",
      },
      () => {
        setRemovedDeposits((prev) => ({
          ...prev,
          [accountId]: [...(prev[accountId] || []), depositId],
        }));
      },
    );
  };

  // Mark withdrawal for removal (applied when user clicks Save)
  const handleDeleteWithdrawal = (accountId, withdrawalId) => {
    openDeleteConfirm(
      {
        title: "Remove withdrawal?",
        message:
          "This withdrawal will be marked for removal. Changes apply when you save.",
        confirmLabel: "Remove",
      },
      () => {
        setRemovedWithdrawals((prev) => ({
          ...prev,
          [accountId]: [...(prev[accountId] || []), withdrawalId],
        }));
      },
    );
  };

  const handleAddContributionLocal = (accountId, depositPayload) => {
    setPendingNewDeposits((prev) => ({
      ...prev,
      [accountId]: [...(prev[accountId] || []), depositPayload],
    }));
    setShowAddContributionModal(false);
    setAccountForMoneyMovement(null);
  };

  const handleAddWithdrawalLocal = (accountId, withdrawalPayload) => {
    setPendingNewWithdrawals((prev) => ({
      ...prev,
      [accountId]: [...(prev[accountId] || []), withdrawalPayload],
    }));
    setShowAddWithdrawalModal(false);
    setAccountForMoneyMovement(null);
  };

  const handleRemovePendingDeposit = (accountId, index) => {
    openDeleteConfirm(
      {
        title: "Discard unsaved contribution?",
        message:
          "This contribution has not been saved yet. It will be removed from the list.",
        confirmLabel: "Discard",
      },
      () => {
        setPendingNewDeposits((prev) => {
          const list = (prev[accountId] || []).filter((_, i) => i !== index);
          const next = { ...prev };
          if (list.length) next[accountId] = list;
          else delete next[accountId];
          return next;
        });
      },
    );
  };

  const handleRemovePendingWithdrawal = (accountId, index) => {
    openDeleteConfirm(
      {
        title: "Discard unsaved withdrawal?",
        message:
          "This withdrawal has not been saved yet. It will be removed from the list.",
        confirmLabel: "Discard",
      },
      () => {
        setPendingNewWithdrawals((prev) => {
          const list = (prev[accountId] || []).filter((_, i) => i !== index);
          const next = { ...prev };
          if (list.length) next[accountId] = list;
          else delete next[accountId];
          return next;
        });
      },
    );
  };

  const getFlattenedMissingFields = useCallback(() => {
    if (!missingFields) return [];

    const flattened = [];
    const incomeFields = [
      "tax_info",
      "tax_percentage",
      "tax_bracket",
      "estimated_liquid_net_worth",
      "estimated_total_net_worth",
      "source_of_wealth",
      "banking_estimates",
      "annual_income",
      "aum",
      "annual_expense_range",
      "special_expense_range",
    ];

    const strategyFields = [
      "investment_strategy_defaults",
      "invest_objectives",
      "risk_tolerance",
      "time_horizon",
      "decision_making",
      "source_of_funds",
      "use_of_funds",
      "liquidity_requirements",
    ];

    const associationFields = [
      "investor_associations",
      "accredited_investor",
      "qualified_purchaser",
      "us_broker_dealer_association",
      "broker_dealer_employee",
      "ria_association",
      "shareholder_or_member_board_director",
      "low_priced_securities",
      "finra_affiliation",
      "marijuana_securities",
      "politically_exposed_person",
    ];

    const experienceFields = [
      "investor_experience",
      "stocks",
      "bonds",
      "mutual_funds",
      "alternative_investments",
      "variable_annuities",
      "partnerships",
      "options",
    ];

    // 1. Household fields
    if (missingFields.household) {
      const visibleHouseholdFields = missingFields.household.filter((f) => {
        if (f === "qualified_purchaser") return false;
        if (f === "annual_expense_range")
          return showAnnualExpenseRange(brokerageFlags);
        if (f === "special_expense_range")
          return showSpecialExpenseRange(brokerageFlags);
        if (f === "source_of_funds") return showSourceOfFunds(brokerageFlags);
        if (f === "use_of_funds") return showUseOfFunds(brokerageFlags);
        if (f === "liquidity_requirements")
          return showLiquidityRequirements(brokerageFlags);
        if (!showExperienceField(f, brokerageFlags, household?.accounts)) return false;
        return true;
      });

      // Group: Income Details
      const hasIncomeFields = visibleHouseholdFields.some((f) =>
        incomeFields.includes(f),
      );
      if (hasIncomeFields) {
        flattened.push({ type: "household", field: "income_details" });
      }

      // Group: Investment Strategy
      const hasStrategyFields = visibleHouseholdFields.some((f) =>
        strategyFields.includes(f),
      );
      if (hasStrategyFields) {
        flattened.push({ type: "household", field: "investment_strategy_defaults" });
      }

      // Group: Investor Associations
      const hasAssociationFields = visibleHouseholdFields.some((f) =>
        associationFields.includes(f),
      );
      if (hasAssociationFields) {
        flattened.push({ type: "household", field: "investor_associations" });
      }

      // Group: Investor Experience
      const hasExperienceFields = visibleHouseholdFields.some((f) =>
        experienceFields.includes(f),
      );
      if (hasExperienceFields) {
        flattened.push({ type: "household", field: "investor_experience" });
      }

      // Individual fields (anything not in the groups above)
      const allGroupedFields = [
        ...incomeFields,
        ...strategyFields,
        ...associationFields,
        ...experienceFields,
        "income_details",
        "investment_strategy_defaults",
      ];

      const nonGroupedFields = visibleHouseholdFields.filter(
        (f) => !allGroupedFields.includes(f),
      );
      nonGroupedFields.forEach((f) => {
        flattened.push({ type: "household", field: f });
      });
    }

    // 2. Member fields
    if (missingFields.household_members) {
      Object.entries(missingFields.household_members).forEach(
        ([id, fields]) => {
          const member = household?.members?.find((m) => m.id === id);
          getMemberMissingPills({
            member,
            memberId: id,
            fields,
            household,
            brokerageFlags,
          }).forEach((pill) => {
            flattened.push({ type: "member", id, field: pill.editorField });
          });
        },
      );
    }

    // 3. Account fields
    if (missingFields.accounts) {
      Object.entries(missingFields.accounts).forEach(([id, fields]) => {
        fields.forEach((f) => {
          if (f === "beneficiaries_primary_missing") return;
          flattened.push({ type: "account", id, field: f });
        });
      });
    }

    // 4. Bank issues — one task per flagged bank id, opened via its first linked account.
    Object.keys(getMissingBankAccountsMap(missingFields)).forEach((bankId) => {
      const linkedAccount = (household?.accounts || []).find((a) =>
        isBankLinkedToAccount(a, bankId),
      );
      if (linkedAccount) {
        flattened.push({
          type: "account",
          id: linkedAccount.id,
          field: "bank_details",
          bankId,
        });
      }
    });

    return flattened;
  }, [missingFields, household]);

  const handleSaveAndNext = useCallback(
    (currentType, currentId, currentField) => {
      const flattened = getFlattenedMissingFields();
      if (flattened.length === 0) {
        setShowMissingFieldModal(false);
        setShowMissingMemberFieldModal(false);
        setShowMissingAccountFieldModal(false);
        return;
      }

      // Find current task index
      const currentIndex = flattened.findIndex(
        (t) =>
          t.type === currentType &&
          t.field === currentField &&
          (!currentId || t.id === currentId),
      );

      if (flattened.length <= 1 && currentIndex !== -1) {
        // Only one field left and we just saved it - close
        setShowMissingFieldModal(false);
        setShowMissingMemberFieldModal(false);
        setShowMissingAccountFieldModal(false);
        return;
      }

      // Circular navigation - find next index
      const nextIndex = (currentIndex + 1) % flattened.length;
      const nextTask = flattened[nextIndex];

      // Close all modals first
      setShowMissingFieldModal(false);
      setShowMissingMemberFieldModal(false);
      setShowMissingAccountFieldModal(false);

      // Open the next task modal after a short delay
      setTimeout(() => {
        if (nextTask.type === "household") {
          setSelectedMissingField(nextTask.field);
          setShowMissingFieldModal(true);
        } else if (nextTask.type === "member") {
          setSelectedMemberId(nextTask.id);
          setSelectedMemberField(nextTask.field);
          setShowMissingMemberFieldModal(true);
        } else if (nextTask.type === "account") {
          setSelectedAccountId(nextTask.id);
          setSelectedAccountField(nextTask.field);
          setSelectedBankAccountId(nextTask.bankId || null);
          setShowMissingAccountFieldModal(true);
        }
      }, 150);
    },
    [
      getFlattenedMissingFields,
      setSelectedMissingField,
      setShowMissingFieldModal,
      setSelectedMemberId,
      setSelectedMemberField,
      setShowMissingMemberFieldModal,
      setSelectedAccountId,
      setSelectedAccountField,
      setShowMissingAccountFieldModal,
    ],
  );

  // Mark beneficiary for removal (will be removed on Save)
  const handleRemoveBeneficiary = (accountId, beneficiaryId) => {
    openDeleteConfirm(
      {
        title: "Remove beneficiary?",
        message:
          "This beneficiary will be marked for removal. Changes apply when you save.",
        confirmLabel: "Remove",
      },
      () => {
        setRemovedBeneficiaries((prev) => ({
          ...prev,
          [accountId]: [...(prev[accountId] || []), beneficiaryId],
        }));
      },
    );
  };

  // Mark trusted contact for removal (will be removed on Save)
  const handleRemoveTrustedContact = (accountId, trustedContactId) => {
    openDeleteConfirm(
      {
        title: "Remove trusted contact?",
        message:
          "This trusted contact will be marked for removal. Changes apply when you save.",
        confirmLabel: "Remove",
      },
      () => {
        setRemovedTrustedContacts((prev) => ({
          ...prev,
          [accountId]: [...(prev[accountId] || []), trustedContactId],
        }));
      },
    );
  };

  const handleAddBeneficiaryForAccount = (accountId, preSelectRole = null) => {
    setAccountForBeneficiary(accountId);
    setMemberTypeModalType("beneficiary");
    setBeneficiaryPreSelectRole(preSelectRole);
    setShowSelectMemberTypeModal(true);
  };

  const handleAddTrustedContactForAccount = (accountId) => {
    setAccountForBeneficiary(accountId);
    setMemberTypeModalType("trusted_contact");
    setShowSelectMemberTypeModal(true);
  };

  const handleSelectExistingMember = (member) => {
    setShowSelectMemberTypeModal(false);
    setNewlyCreatedMemberId(member.id);
    setShowBeneficiaryTrustedModal(true);
  };

  const handleAddNewMember = () => {
    setShowSelectMemberTypeModal(false);
    setShowAddMemberModal(true);
  };

  const getBankOpsForAccount = (accountId) =>
    getAccountFormValue(`${accountId}.bankAccountsOps`, null) || emptyBankOps();

  const handleRemoveBankAccount = (accountId, bankAccountId) => {
    if (!bankAccountId) return;
    openDeleteConfirm(
      {
        title: "Unlink bank account?",
        message:
          "This bank account will no longer be linked to this investment account. Changes apply when you save.",
        confirmLabel: "Unlink",
      },
      () => {
        const ops = getBankOpsForAccount(accountId);
        handleAccountFieldChange(
          `${accountId}.bankAccountsOps`,
          stageUnlink(ops, bankAccountId),
        );
      },
    );
  };

  const handleChooseOtherBankAccount = (accountId) => {
    setAccountForBankLinking(accountId);
    setShowSelectBankAccountModal(true);
  };

  const handleSelectExistingBankAccount = (bankAccountId) => {
    setShowSelectBankAccountModal(false);
    if (accountForBankLinking) {
      const ops = getBankOpsForAccount(accountForBankLinking);
      handleAccountFieldChange(
        `${accountForBankLinking}.bankAccountsOps`,
        stageLinkExisting(ops, bankAccountId),
      );
      setAccountForBankLinking(null);
    }
  };

  const handleEditLinkedBankAccountField = (accountId, bankAccountId, fieldPath, value) => {
    const ops = getBankOpsForAccount(accountId);
    handleAccountFieldChange(
      `${accountId}.bankAccountsOps`,
      stageFieldEdit(ops, bankAccountId, fieldPath, value),
    );
  };

  const handleAddNewBankAccount = () => {
    setShowSelectBankAccountModal(false);
    setShowAddBankAccountModal(true);
  };

  const handleBankAccountAdded = (bankAccountId, bankAccountName) => {
    setNewBankAccountId(bankAccountId);
    setNewBankAccountName(bankAccountName);
    if (accountForBankLinking) {
      // Open link modal with preselected account
      setShowLinkBankAccountModal(true);
    }
  };

  const handleBankAccountLinked = (data) => {
    setShowLinkBankAccountModal(false);
    setNewBankAccountId(null);
    setNewBankAccountName("");
    setAccountForBankLinking(null);
    if (onHouseholdUpdate) {
      onHouseholdUpdate(data ?? null);
    }
  };

  // Handlers for withdrawal bank account selection
  const handleWithdrawalBankSelect = (accountId, withdrawalIdx) => {
    setWithdrawalForBankSelection({ accountId, withdrawalIdx });
    setShowWithdrawalBankSelectModal(true);
  };

  const handleWithdrawalSelectExistingBank = (bankAccountId) => {
    if (withdrawalForBankSelection) {
      const { accountId, withdrawalIdx } = withdrawalForBankSelection;
      handleAccountFieldChange(
        `${accountId}.withdrawals.${withdrawalIdx}.destination_identifier`,
        bankAccountId,
      );
    }
    setShowWithdrawalBankSelectModal(false);
    setWithdrawalForBankSelection(null);
  };

  const handleWithdrawalAddNewBank = () => {
    setShowWithdrawalBankSelectModal(false);
    setShowWithdrawalAddBankModal(true);
  };

  const handleWithdrawalBankAdded = (bankAccountId, bankAccountName) => {
    if (withdrawalForBankSelection) {
      const { accountId, withdrawalIdx } = withdrawalForBankSelection;
      handleAccountFieldChange(
        `${accountId}.withdrawals.${withdrawalIdx}.destination_identifier`,
        bankAccountId,
      );
    }
    // Store the newly added bank account info for display purposes
    setNewlyAddedWithdrawalBankAccounts((prev) => ({
      ...prev,
      [bankAccountId]: { id: bankAccountId, name: bankAccountName },
    }));
    setShowWithdrawalAddBankModal(false);
    setWithdrawalForBankSelection(null);
    // Note: We don't trigger a refresh here to keep the user in edit mode
    // The bank account will be available in household.bank_accounts after the next save/refresh
  };

  // Get bank account label for withdrawal display
  const getWithdrawalBankAccountLabel = (bankAccountId) => {
    if (!bankAccountId) return null;
    // First check if it's a newly added bank account
    const newlyAdded = newlyAddedWithdrawalBankAccounts[bankAccountId];
    if (newlyAdded) {
      return newlyAdded.name;
    }
    // Then check household bank accounts
    const list = Array.isArray(householdBankAccounts)
      ? householdBankAccounts
      : householdBankAccounts
        ? [householdBankAccounts]
        : [];
    const bankAccount = list.find((ba) => ba.id === bankAccountId);
    if (bankAccount) {
      return `${bankAccount.bank_name || "Bank"} - ${bankAccount.account_number || "N/A"}`;
    }
    return null;
  };

  const performDeleteMember = async (memberId) => {
    if (!household?.id) return;
    setDeletingMemberId(memberId);
    try {
      await axiosInstance.delete(
        `/households/${household.id}/members/${memberId}`,
      );
      setDeletingMemberId(null);
      if (onHouseholdUpdate) {
        onHouseholdUpdate(null);
      }
    } catch (err) {
      toast.error(humanizeError(err, "Couldn't delete this member."));
      setDeletingMemberId(null);
    }
  };

  const handleDeleteMember = (memberId) => {
    if (!household?.id) return;
    if (isPrimaryMember(memberId)) {
      toast.error("You can't delete the primary household member.");
      return;
    }
    if (isOwner(memberId)) {
      toast.error("This member owns an account and can't be deleted.");
      return;
    }
    const member = household.members?.find((m) => m.id === memberId);
    const memberName = member
      ? `${member.personal_info?.first_name || ""} ${member.personal_info?.last_name || ""}`.trim() ||
        "this member"
      : "this member";
    openDeleteConfirm(
      {
        title: `Delete ${memberName}?`,
        message:
          "This permanently removes this household member. This action cannot be undone.",
        confirmLabel: "Delete",
      },
      () => {
        void performDeleteMember(memberId);
      },
    );
  };

  const formatCurrency = (value) => {
    if (!value) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatRange = (range) => {
    if (!range || (range.start === null && range.end === null)) return "N/A";
    const start = range.start !== null ? formatCurrency(range.start) : "N/A";
    const end = range.end !== null ? formatCurrency(range.end) : "N/A";
    return `${start} - ${end}`;
  };

  const formatTaxBracket = (taxInfo) => {
    if (
      !taxInfo ||
      !taxInfo.tax_bracket ||
      (taxInfo.tax_bracket.start === null && taxInfo.tax_bracket.end === null)
    ) {
      return "N/A";
    }
    const entry = Object.entries(TAX_BRACKET_MAP).find(
      ([, v]) => v.start === taxInfo.tax_bracket.start && v.end === taxInfo.tax_bracket.end
    );
    return entry ? entry[0] : "N/A";
  };

  const formatDate = formatDateForDisplay;

  const isOwner = (memberId) => {
    if (!household.accounts || !Array.isArray(household.accounts)) return false;

    return household.accounts.some((account) => {
      const info = account.owners_info || account.owners;
      if (!info || !Array.isArray(info)) return false;
      return info.some(
        (owner) =>
          owner &&
          (owner.id === memberId || owner.member_id === memberId),
      );
    });
  };

  const isPrimaryMember = (memberId) =>
    household.members?.some(
      (m) => m.id === memberId && m.is_primary === true,
    ) ?? false;

  const getOwners = () => {
    if (!household.members) return [];
    return household.members.filter((member) => isOwner(member.id));
  };

  const getAdditionalMembers = () => {
    if (!household.members) return [];
    return household.members.filter((member) => !isOwner(member.id));
  };

  const getAddressByType = (addresses, type) => {
    if (!addresses || !Array.isArray(addresses)) return null;
    return addresses.find((addr) => addr.type === type);
  };

  // Helper function to get member name by ID
  const getMemberNameById = (memberId) => {
    if (!household?.members || !memberId) return "N/A";
    const member = household.members.find((m) => m.id === memberId);
    if (!member) return "N/A";
    return (
      `${member.personal_info?.first_name || ""} ${member.personal_info?.last_name || ""}`.trim() ||
      "N/A"
    );
  };

  const getMemberSsnDigitsById = (memberId) => {
    if (!household?.members || !memberId) return "";
    const member = household.members.find((m) => m.id === memberId);
    const raw = member?.personal_info?.ssn_or_tin;
    return String(raw || "").replace(/\D/g, "");
  };

  const getMemberSsnById = (memberId) => {
    if (!household?.members || !memberId) return "";
    const member = household.members.find((m) => m.id === memberId);
    return member?.personal_info?.ssn_or_tin || "";
  };

  const investmentObjectives = useMemo(() => {
    const base = [...PRIMARY_OBJECTIVE_OPTIONS];
    if (!household?.accounts?.length) return base;
    const idx = household.accounts.findIndex(
      (a, i) => (a.id || i) === selectedAccountId,
    );
    const acc = idx >= 0 ? household.accounts[idx] : household.accounts[0];
    const aid = acc?.id;
    const fromForm =
      aid && accountsFormData[aid]?.risk_tolerance?.invest_objectives?.[0];
    const fromApi = acc?.risk_tolerance?.invest_objectives?.[0];
    const current = fromForm ?? fromApi;
    if (current && !base.includes(current)) {
      return [current, ...base];
    }
    return base;
  }, [household?.accounts, selectedAccountId, accountsFormData]);

  // Handle save household
  const handleSaveHousehold = async () => {
    if (!household) return;

    setSavingHousehold(true);
    try {
      // Prepare update payload
      const updatePayload = {};

      // Handle simple fields
      if (householdFormData.annual_income !== undefined) {
        updatePayload.annual_income = householdFormData.annual_income
          ? parseFloat(householdFormData.annual_income)
          : null;
      }
      if (householdFormData.estimated_total_net_worth !== undefined) {
        updatePayload.estimated_total_net_worth =
          householdFormData.estimated_total_net_worth
            ? parseFloat(householdFormData.estimated_total_net_worth)
            : null;
      }
      if (householdFormData.estimated_liquid_net_worth !== undefined) {
        updatePayload.estimated_liquid_net_worth =
          householdFormData.estimated_liquid_net_worth
            ? parseFloat(householdFormData.estimated_liquid_net_worth)
            : null;
      }
      if (householdFormData.source_of_wealth !== undefined) {
        updatePayload.source_of_wealth = householdFormData.source_of_wealth || null;
      }

      // Handle tax_info (JSON field)
      if (householdFormData.tax_info?.tax_bracket) {
        const existingTaxBracket = household.tax_info?.tax_bracket || {};
        const taxBracket = {
          start:
            householdFormData.tax_info.tax_bracket.start !== undefined
              ? householdFormData.tax_info.tax_bracket.start
                ? parseFloat(householdFormData.tax_info.tax_bracket.start)
                : null
              : existingTaxBracket.start,
          end:
            householdFormData.tax_info.tax_bracket.end !== undefined
              ? householdFormData.tax_info.tax_bracket.end
                ? parseFloat(householdFormData.tax_info.tax_bracket.end)
                : null
              : existingTaxBracket.end,
        };
        updatePayload.tax_info = { tax_bracket: taxBracket };
      }

      // Handle annual_expense_range (JSON field)
      if (householdFormData.annual_expense_range) {
        const existingRange = household.annual_expense_range || {};
        const expenseRange = {
          start:
            householdFormData.annual_expense_range.start !== undefined
              ? householdFormData.annual_expense_range.start
                ? parseFloat(householdFormData.annual_expense_range.start)
                : null
              : existingRange.start,
          end:
            householdFormData.annual_expense_range.end !== undefined
              ? householdFormData.annual_expense_range.end
                ? parseFloat(householdFormData.annual_expense_range.end)
                : null
              : existingRange.end,
        };
        updatePayload.annual_expense_range = expenseRange;
      }

      // Handle special_expense_range (JSON field)
      if (householdFormData.special_expense_range) {
        const existingRange = household.special_expense_range || {};
        const specialExpenseRange = {
          start:
            householdFormData.special_expense_range.start !== undefined
              ? householdFormData.special_expense_range.start
                ? parseFloat(householdFormData.special_expense_range.start)
                : null
              : existingRange.start,
          end:
            householdFormData.special_expense_range.end !== undefined
              ? householdFormData.special_expense_range.end
                ? parseFloat(householdFormData.special_expense_range.end)
                : null
              : existingRange.end,
        };
        updatePayload.special_expense_range = specialExpenseRange;
      }

      // Handle banking_estimates (JSON field — whole-dict replacement)
      if (householdFormData.banking_estimates) {
        const baseEstimates = {
          no_of_checks_per_month:
            household.banking_estimates?.no_of_checks_per_month ?? 0,
          no_of_domestic_wire_transfers_per_month:
            household.banking_estimates?.no_of_domestic_wire_transfers_per_month ?? 0,
          no_of_international_wire_transfers_per_month:
            household.banking_estimates?.no_of_international_wire_transfers_per_month ?? 0,
        };
        const merged = { ...baseEstimates, ...householdFormData.banking_estimates };
        updatePayload.banking_estimates = {
          no_of_checks_per_month: Math.max(0, parseInt(merged.no_of_checks_per_month, 10) || 0),
          no_of_domestic_wire_transfers_per_month: Math.max(0, parseInt(merged.no_of_domestic_wire_transfers_per_month, 10) || 0),
          no_of_international_wire_transfers_per_month: Math.max(0, parseInt(merged.no_of_international_wire_transfers_per_month, 10) || 0),
        };
      }

      // Handle investor_associations (JSON field)
      if (householdFormData.investor_associations) {
        const baseAssociations = {
          accredited_investor:
            household.investor_associations?.accredited_investor ?? false,
          qualified_purchaser:
            household.investor_associations?.qualified_purchaser ?? false,
          us_broker_dealer_association:
            household.investor_associations?.us_broker_dealer_association ??
            false,
          broker_dealer_employee:
            household.investor_associations?.broker_dealer_employee ?? false,
          ria_association:
            household.investor_associations?.ria_association ?? false,
          shareholder_or_member_board_director:
            household.investor_associations
              ?.shareholder_or_member_board_director ?? false,
          finra_affiliation:
            household.investor_associations?.finra_affiliation ?? false,
          low_priced_securities:
            household.investor_associations?.low_priced_securities ?? false,
          marijuana_securities:
            household.investor_associations?.marijuana_securities ?? false,
          politically_exposed_person:
            household.investor_associations?.politically_exposed_person ?? false,
        };
        updatePayload.investor_associations = {
          ...baseAssociations,
          ...householdFormData.investor_associations,
        };
      }

      // Handle investor_experience (JSON field)
      if (householdFormData.investor_experience) {
        const baseExperience = {
          bonds: household.investor_experience?.bonds ?? null,
          stocks: household.investor_experience?.stocks ?? null,
          alternative_investments:
            household.investor_experience?.alternative_investments ?? null,
          variable_annuities:
            household.investor_experience?.variable_annuities ?? null,
          mutual_funds: household.investor_experience?.mutual_funds ?? null,
          options: household.investor_experience?.options ?? null,
          partnerships: household.investor_experience?.partnerships ?? null,
        };
        const updatedExperience = { ...baseExperience };
        Object.keys(householdFormData.investor_experience).forEach((key) => {
          const rawValue = householdFormData.investor_experience[key];
          if (rawValue !== undefined && rawValue !== null && rawValue !== "") {
            const numValue = parseInt(rawValue, 10);
            updatedExperience[key] = isNaN(numValue)
              ? baseExperience[key]
              : Math.max(0, Math.min(100, numValue));
          } else {
            updatedExperience[key] = baseExperience[key];
          }
        });
        updatePayload.investor_experience = updatedExperience;
      }

      if (householdFormData.is_fee_standard !== undefined) {
        updatePayload.is_fee_standard = householdFormData.is_fee_standard;
        if (householdFormData.is_fee_standard === true) {
          updatePayload.custom_fee_percentage = null;
        }
      }
      const effectiveStandardAfterSave =
        updatePayload.is_fee_standard !== undefined
          ? updatePayload.is_fee_standard
          : household?.is_fee_standard !== false;
      if (
        householdFormData.custom_fee_percentage !== undefined &&
        !effectiveStandardAfterSave
      ) {
        const raw = householdFormData.custom_fee_percentage;
        if (raw === "" || raw === null || raw === undefined) {
          updatePayload.custom_fee_percentage = null;
        } else {
          const n = parseFloat(raw);
          updatePayload.custom_fee_percentage = isNaN(n) ? null : n;
        }
      }

      // Only send if there are changes
      if (Object.keys(updatePayload).length > 0) {
        const response = await axiosInstance.put(
          `/households/${household.id}`,
          updatePayload,
        );
        if (onHouseholdUpdate) {
          onHouseholdUpdate(response.data);
        }
        toast.success("Household updated.");
      }

      setIsEditing(false);
      setHouseholdFormData({});
    } catch (error) {
      toast.error(humanizeError(error, "Couldn't update household."));
    } finally {
      setSavingHousehold(false);
    }
  };

  const buildPersonalInfoUpdate = (member, personalUpdates) => {
    const basePersonal = member.personal_info || {};
    const updatedPersonal = {
      ...basePersonal,
    };

    if (personalUpdates) {
      Object.keys(personalUpdates).forEach((key) => {
        if (key !== "address") {
          updatedPersonal[key] = personalUpdates[key];
        }
      });
    }

    // The UI defaults Country of Citizenship to "USA". Persist that default on save
    // so the backend doesn't remain blank when the user clicks Save.
    if (
      updatedPersonal.country_of_citizenship === null ||
      updatedPersonal.country_of_citizenship === undefined ||
      String(updatedPersonal.country_of_citizenship).trim() === ""
    ) {
      updatedPersonal.country_of_citizenship = "USA";
    }

    if (personalUpdates?.address) {
      const existingAddresses = Array.isArray(basePersonal.address)
        ? [...basePersonal.address]
        : [];
      const updatedAddresses = [...existingAddresses];

      Object.keys(personalUpdates.address).forEach((typeKey) => {
        const addressUpdates = personalUpdates.address[typeKey] || {};
        const existingIndex = updatedAddresses.findIndex(
          (addr) => addr?.type === typeKey,
        );
        const baseAddress =
          existingIndex >= 0
            ? { ...updatedAddresses[existingIndex] }
            : { type: typeKey };

        const mergedAddress = { ...baseAddress, ...addressUpdates };
        if (existingIndex >= 0) {
          updatedAddresses[existingIndex] = mergedAddress;
        } else {
          updatedAddresses.push(mergedAddress);
        }
      });

      updatedPersonal.address = updatedAddresses;
    }

    if (updatedPersonal.dob) {
      updatedPersonal.dob = formatDateForInput(updatedPersonal.dob);
    }

    return updatedPersonal;
  };

  const buildEmploymentInfoUpdate = (member, employmentUpdates) => {
    const baseEmployment = member.employment_info || {};
    const updatedEmployment = { ...baseEmployment };

    if (employmentUpdates) {
      Object.keys(employmentUpdates).forEach((key) => {
        if (key !== "address") {
          updatedEmployment[key] = employmentUpdates[key];
        }
      });
    }

    if (employmentUpdates && Object.prototype.hasOwnProperty.call(employmentUpdates, "address")) {
      if (employmentUpdates.address == null) {
        updatedEmployment.address = null;
      } else {
        const baseAddress = baseEmployment.address || {};
        const mergedAddress = {
          ...baseAddress,
          ...employmentUpdates.address,
        };
        // The UI defaults Country to "USA" via StateCitySelect's fallback display,
        // but that default is never dispatched to form state unless the user picks
        // a non-US country or a city (which skips the callback when the internal
        // id is already US). Persist the displayed default here so the backend
        // doesn't keep flagging `address` as incomplete.
        if (
          mergedAddress.country === null ||
          mergedAddress.country === undefined ||
          String(mergedAddress.country).trim() === ""
        ) {
          mergedAddress.country = "USA";
        }
        updatedEmployment.address = mergedAddress;
      }
    }

    return normalizeEmploymentInfoForStatus(updatedEmployment);
  };

  const buildIdInfoUpdate = (member, idUpdates) => {
    const baseIds = Array.isArray(member.id_info) ? member.id_info : [];
    const updatedIds = [...baseIds];
    const updateIndices = idUpdates
      ? Object.keys(idUpdates)
          .map((k) => parseInt(k, 10))
          .filter(Number.isFinite)
      : [];
    const maxIndex = updateIndices.length ? Math.max(...updateIndices) : -1;

    if (maxIndex >= 0 && updatedIds.length <= maxIndex) {
      for (let i = updatedIds.length; i <= maxIndex; i++) {
        updatedIds[i] = {};
      }
    }

    if (idUpdates) {
      Object.keys(idUpdates).forEach((indexKey) => {
        const index = parseInt(indexKey, 10);
        if (Number.isNaN(index)) return;
        const baseId = updatedIds[index] || {};
        updatedIds[index] = { ...baseId, ...idUpdates[indexKey] };
      });
    }

    updatedIds.forEach((id) => {
      if (id.issuance_date) {
        id.issuance_date = formatDateForInput(id.issuance_date);
      }
      if (id.expiration_date) {
        id.expiration_date = formatDateForInput(id.expiration_date);
      }
    });

    return updatedIds;
  };

  const handleSaveMembers = async () => {
    if (!household) return;

    const memberIds = Object.keys(membersFormData);
    if (!memberIds.length) {
      setIsEditingMembers(false);
      return;
    }

    setSavingMembers(true);
    try {
      const updates = [];

      memberIds.forEach((memberId) => {
        const member = household.members?.find((m) => m.id === memberId);
        if (!member) return;

        const memberChanges = membersFormData[memberId] || {};
        const update = {};

        if (memberChanges.personal_info) {
          update.personal_info = buildPersonalInfoUpdate(
            member,
            memberChanges.personal_info,
          );
        }
        if (memberChanges.employment_info) {
          update.employment_info = buildEmploymentInfoUpdate(
            member,
            memberChanges.employment_info,
          );
        }
        if (memberChanges.id_info) {
          update.id_info = buildIdInfoUpdate(member, memberChanges.id_info);
        }

        if (Object.keys(update).length) {
          updates.push({
            member_id: memberId,
            update,
          });
        }
      });

      if (!updates.length) {
        setIsEditingMembers(false);
        setMembersFormData({});
        return;
      }

      const invalidEmail = updates.find(({ update }) => {
        const email = update.personal_info?.email;
        return email && !validateEmail(email);
      });
      if (invalidEmail) {
        const member = household.members?.find((m) => m.id === invalidEmail.member_id);
        const name = member?.personal_info?.first_name || "this member";
        toast.error(`Please enter a valid email address for ${name}.`);
        setSavingMembers(false);
        return;
      }

      const response = await axiosInstance.put(
        `/households/${household.id}/members/bulk`,
        {
          members: updates,
        },
      );

      if (response.data?.failed?.length) {
        toast.warning(
          `Some members couldn't be updated (${response.data.failed.length}).`,
        );
      } else {
        toast.success("Members updated.");
      }

      if (onHouseholdUpdate) {
        onHouseholdUpdate(response.data);
      }

      setIsEditingMembers(false);
      setMembersFormData({});
      setValidationErrors({});
    } catch (error) {
      toast.error(humanizeError(error, "Couldn't update members."));
    } finally {
      setSavingMembers(false);
    }
  };

  const handleSaveAccounts = async () => {
    if (!household) return;

    const accountsMissingOwner1 = [];
    const twoOwnerAccountsMissingOwner2 = [];
    (household.accounts || []).forEach((account) => {
      const ownerId1 = getAccountFormValue(
        `${account.id}.owners_info.0.id`,
        account.owners_info?.[0]?.id,
      );
      const isNonProfitBusinessAccount =
        (account.registration || "").toLowerCase() === "business" &&
        getAccountFormValue(
          `${account.id}.business_info.is_non_profit`,
          account.business_info?.is_non_profit,
        ) === true;
      if (!ownerId1 && !isNonProfitBusinessAccount) {
        accountsMissingOwner1.push(account);
      }
      if (isTwoOwnerAccount(account)) {
        const ownerId2 = getAccountFormValue(
          `${account.id}.owners_info.1.id`,
          account.owners_info?.[1]?.id,
        );
        if (!ownerId2) {
          twoOwnerAccountsMissingOwner2.push(account);
        }
      }
    });
    const accountLabel = (account) =>
      formatAccountName(
        account,
        (household.accounts || []).findIndex((a) => a?.id === account.id) + 1,
        household.members || [],
      );
    if (accountsMissingOwner1.length > 0) {
      const names = accountsMissingOwner1.map(accountLabel).join(", ");
      toast.error(
        `Account owner is still missing for ${names}. Please add an owner to ${accountsMissingOwner1.length === 1 ? "it" : "each of them"} before saving.`,
      );
      return;
    }
    if (twoOwnerAccountsMissingOwner2.length > 0) {
      const names = twoOwnerAccountsMissingOwner2.map(accountLabel).join(", ");
      toast.error(
        `A second owner is still missing for ${names}. Please assign both owners before saving.`,
      );
      return;
    }

    const getDuplicateMemberIds = (rows) => {
      const seen = new Set();
      const dups = new Set();
      (rows || []).forEach((raw) => {
        if (raw === null || raw === undefined || raw === "") return;
        const id = String(raw);
        if (seen.has(id)) dups.add(id);
        else seen.add(id);
      });
      return Array.from(dups);
    };

    const accountsList = household.accounts || [];
    const membersList = household.members || [];
    const hhName = household.name || "";
    const errMap = {};
    let firstFailingAccId = null;
    accountsList.forEach((account, idx) => {
      const accId = account.id ?? idx;
      const form =
        (account.id != null ? accountsFormData[account.id] : null) ||
        accountsFormData[accId] ||
        {};
      const benForm = form.beneficiaries || {};
      const removed =
        (account.id != null ? removedBeneficiaries[account.id] : null) ||
        removedBeneficiaries[accId] ||
        [];
      const msgs = getBeneficiaryShareTotalErrorsFromForm(account, benForm, removed);
      if (msgs.length === 0) return;
      if (firstFailingAccId == null) firstFailingAccId = accId;
      const accLabel = formatAccountContextLabel(account, accountsList, membersList, hhName);
      errMap[accId] = msgs.map((m) => `${accLabel}: ${m}`);
    });
    if (Object.keys(errMap).length > 0) {
      setBeneficiaryValidationErrorsByAccount(errMap);
      if (firstFailingAccId != null) {
        setSelectedAccountId(firstFailingAccId);
        setSelectedAccountSectionByAccount((prev) => ({
          ...prev,
          [firstFailingAccId]: "beneficiaries",
        }));
      }
      return;
    }
    setBeneficiaryValidationErrorsByAccount({});

    // Duplicates: block save if duplicate beneficiaries or trusted contacts on any account
    for (const account of accountsList) {
      const accountId = account.id;
      const changes = accountsFormData[accountId] || {};

      const removedBenIds = new Set(removedBeneficiaries[accountId] || []);
      const baseBen = Array.isArray(account.beneficiaries) ? account.beneficiaries : [];
      const benChanges = changes.beneficiaries || {};
      const resolvedBenMemberIds = baseBen
        .filter((b) => b && !removedBenIds.has(b.id))
        .map((b, idx) => {
          const c = benChanges[idx] || {};
          return c.household_member_id !== undefined ? c.household_member_id : b.household_member_id;
        })
        .filter((id) => id !== null && id !== undefined && id !== "");

      const benDupIds = getDuplicateMemberIds(resolvedBenMemberIds);
      if (benDupIds.length > 0) {
        setBeneficiaryValidationErrorsByAccount({
          [accountId]: [
            "Duplicate beneficiaries detected. Each household member can only appear once in beneficiaries.",
          ],
        });
        setSelectedAccountId(accountId);
        setSelectedAccountSectionByAccount((prev) => ({
          ...prev,
          [accountId]: "beneficiaries",
        }));
        return;
      }

      const removedTcIds = new Set(removedTrustedContacts[accountId] || []);
      const baseTc = Array.isArray(account.trusted_contacts) ? account.trusted_contacts : [];
      const tcChanges = changes.trusted_contacts || {};
      const resolvedTcMemberIds = baseTc
        .filter((t) => t && !removedTcIds.has(t.id))
        .map((t, idx) => {
          const c = tcChanges[idx] || {};
          return c.household_member_id !== undefined ? c.household_member_id : t.household_member_id;
        })
        .filter((id) => id !== null && id !== undefined && id !== "");

      const tcDupIds = getDuplicateMemberIds(resolvedTcMemberIds);
      if (tcDupIds.length > 0) {
        setBeneficiaryValidationErrorsByAccount({
          [accountId]: [
            "Duplicate trusted contacts detected. Each household member can only appear once in trusted contacts.",
          ],
        });
        setSelectedAccountId(accountId);
        setSelectedAccountSectionByAccount((prev) => ({
          ...prev,
          [accountId]: "trusted_contacts",
        }));
        return;
      }
    }

    for (const account of accountsList) {
      if (!account.trust_info || Object.keys(account.trust_info).length === 0) {
        continue;
      }
      const accountId = account.id;
      const changes = accountsFormData[accountId] || {};
      const baseT = Array.isArray(account.trust_info.trustees)
        ? account.trust_info.trustees
        : [];
      const chT = changes.trust_info?.trustees || {};
      const grantorIds = getAccountFormValue(
        `${accountId}.trust_info.grantorIds`,
        getTrustGrantorIds(account.trust_info),
      );
      const grantorIdSet = new Set((grantorIds || []).map(String));
      const idxKeys = Object.keys(chT)
        .map(Number)
        .filter(Number.isFinite);
      const maxFromChanges = idxKeys.length ? Math.max(...idxKeys) : -1;
      const maxIdx = Math.max(baseT.length - 1, maxFromChanges, -1);
      const resolvedIds = [];
      for (let i = 0; i <= maxIdx; i++) {
        const tid =
          chT[i]?.id !== undefined
            ? chT[i].id
            : baseT[i]
              ? getTrusteeMemberId(baseT[i])
              : "";
        if (tid) resolvedIds.push(String(tid));
      }
      const trusteeDupIds = getDuplicateMemberIds(resolvedIds);
      if (trusteeDupIds.length > 0) {
        toast.error("Each member can only be a trustee once.");
        setSelectedAccountId(accountId);
        setSelectedAccountSectionByAccount((prev) => ({
          ...prev,
          [accountId]: "trust_account",
        }));
        return;
      }
      for (let i = 0; i <= maxIdx; i++) {
        const tid =
          chT[i]?.id !== undefined
            ? chT[i].id
            : baseT[i]
              ? getTrusteeMemberId(baseT[i])
              : "";
        if (!tid) continue;
        if (grantorIdSet.has(String(tid))) continue;
        const rel =
          chT[i]?.relationship_to_grantor !== undefined
            ? chT[i].relationship_to_grantor
            : baseT[i]?.relationship_to_grantor;
        if (isPlaceholderTrusteeRelationship(rel)) {
          toast.error("Pick a Relationship to Grantor for each trustee.");
          setSelectedAccountId(accountId);
          setSelectedAccountSectionByAccount((prev) => ({
            ...prev,
            [accountId]: "trust_account",
          }));
          return;
        }
      }
    }

    const accountIds = Object.keys(accountsFormData);
    const accountsWithRemovals = new Set([
      ...accountIds,
      ...Object.keys(removedBeneficiaries),
      ...Object.keys(removedTrustedContacts),
      ...Object.keys(pendingNewDeposits),
      ...Object.keys(pendingNewWithdrawals),
      ...Object.keys(removedDeposits),
      ...Object.keys(removedWithdrawals),
    ]);

    if (!accountsWithRemovals.size) {
      setIsEditingAccounts(false);
      return;
    }

    setSavingAccounts(true);
    try {
      const updates = [];

      accountsWithRemovals.forEach((accountId) => {
        const account = household.accounts?.find((a) => a.id === accountId);
        if (!account) return;

        const accountChanges = accountsFormData[accountId] || {};
        const update = {};

        // Helper to check if any field in a path has changed
        const hasChangesInPath = (path) => {
          const keys = path.split(".");
          let current = accountChanges;
          for (const key of keys) {
            if (current && current[key] !== undefined) {
              current = current[key];
            } else {
              return false;
            }
          }
          return true;
        };

        // Build complete JSON objects when any field changes
        if (hasChangesInPath("risk_tolerance")) {
          const baseRisk = account.risk_tolerance || {};
          const changes = accountChanges.risk_tolerance || {};

          // Helper to merge range values - preserve existing values if not changed
          const mergeTimeHorizon = () => {
            if (
              changes.time_horizon?.start !== undefined ||
              changes.time_horizon?.end !== undefined
            ) {
              const existing = baseRisk.time_horizon || {};
              return {
                start:
                  changes.time_horizon?.start !== undefined
                    ? changes.time_horizon.start !== ""
                      ? parseFloat(changes.time_horizon.start)
                      : null
                    : (existing.start ?? null),
                end:
                  changes.time_horizon?.end !== undefined
                    ? changes.time_horizon.end !== ""
                      ? parseFloat(changes.time_horizon.end)
                      : null
                    : (existing.end ?? null),
              };
            }
            return baseRisk.time_horizon;
          };

          const mergeLiquidityAmount = () => {
            if (
              changes.liquidity_requirements?.amount?.start !== undefined ||
              changes.liquidity_requirements?.amount?.end !== undefined
            ) {
              const existing = baseRisk.liquidity_requirements?.amount || {};
              return {
                start:
                  changes.liquidity_requirements.amount.start !== undefined
                    ? changes.liquidity_requirements.amount.start !== ""
                      ? parseFloat(changes.liquidity_requirements.amount.start)
                      : null
                    : (existing.start ?? null),
                end:
                  changes.liquidity_requirements.amount.end !== undefined
                    ? changes.liquidity_requirements.amount.end !== ""
                      ? parseFloat(changes.liquidity_requirements.amount.end)
                      : null
                    : (existing.end ?? null),
              };
            }
            return baseRisk.liquidity_requirements?.amount || null;
          };

          const mergeLiquidityTimeHorizon = () => {
            if (
              changes.liquidity_requirements?.time_horizon?.start !==
                undefined ||
              changes.liquidity_requirements?.time_horizon?.end !== undefined
            ) {
              const existing =
                baseRisk.liquidity_requirements?.time_horizon || {};
              return {
                start:
                  changes.liquidity_requirements.time_horizon.start !==
                  undefined
                    ? changes.liquidity_requirements.time_horizon.start !== ""
                      ? parseFloat(
                          changes.liquidity_requirements.time_horizon.start,
                        )
                      : null
                    : (existing.start ?? null),
                end:
                  changes.liquidity_requirements.time_horizon.end !== undefined
                    ? changes.liquidity_requirements.time_horizon.end !== ""
                      ? parseFloat(
                          changes.liquidity_requirements.time_horizon.end,
                        )
                      : null
                    : (existing.end ?? null),
              };
            }
            return baseRisk.liquidity_requirements?.time_horizon || null;
          };

          update.risk_tolerance = {
            invest_objectives:
              changes.invest_objectives?.[0] !== undefined
                ? [changes.invest_objectives[0]]
                : baseRisk.invest_objectives || [],
            risk_tolerance:
              changes.risk_tolerance !== undefined
                ? changes.risk_tolerance
                : baseRisk.risk_tolerance,
            time_horizon: mergeTimeHorizon(),
            decision_making:
              changes.decision_making !== undefined
                ? changes.decision_making
                : baseRisk.decision_making,
            source_of_funds:
              changes.source_of_funds !== undefined
                ? Array.isArray(changes.source_of_funds)
                  ? changes.source_of_funds
                  : [changes.source_of_funds]
                : baseRisk.source_of_funds || [],
            use_of_funds:
              changes.use_of_funds !== undefined
                ? changes.use_of_funds
                : baseRisk.use_of_funds,
            liquidity_requirements: {
              amount: mergeLiquidityAmount(),
              time_horizon: mergeLiquidityTimeHorizon(),
            },
          };
        }

        if (hasChangesInPath("transfer_info")) {
          const baseTransfer = account.transfer_info || {};
          const changes = accountChanges.transfer_info || {};

          update.transfer_info = {
            old_account_number:
              changes.old_account_number !== undefined
                ? changes.old_account_number
                : baseTransfer.old_account_number,
            account_owner:
              changes.account_owner !== undefined
                ? changes.account_owner
                : baseTransfer.account_owner,
            delivering_firm_name:
              changes.delivering_firm_name !== undefined
                ? changes.delivering_firm_name
                : baseTransfer.delivering_firm_name,
            address: changes.address
              ? {
                  ...(baseTransfer.address || {}),
                  ...changes.address,
                }
              : baseTransfer.address,
            registration_type:
              changes.registration_type !== undefined
                ? changes.registration_type
                : baseTransfer.registration_type,
            is_full_transfer:
              changes.is_full_transfer !== undefined
                ? changes.is_full_transfer
                : baseTransfer.is_full_transfer,
            transfer_type:
              changes.transfer_type !== undefined
                ? changes.transfer_type
                : baseTransfer.transfer_type,
          };
        }

        if (hasChangesInPath("deposits")) {
          const baseDeposits = Array.isArray(account.deposits)
            ? account.deposits
            : [];
          const changes = accountChanges.deposits || {};
          const removedDepositIds = new Set(removedDeposits[accountId] || []);
          const baseDepositsNotRemoved = baseDeposits.filter(
            (d) => !removedDepositIds.has(d.id),
          );
          update.deposits = baseDepositsNotRemoved.map((deposit) => {
            const idx = baseDeposits.findIndex((d) => d.id === deposit.id);
            const depositChanges = changes[idx] || {};
            return {
              id: deposit.id, // ID is mandatory
              type:
                depositChanges.type !== undefined
                  ? depositChanges.type
                  : deposit.type || "Fixed Amount",
              amount:
                depositChanges.amount !== undefined &&
                depositChanges.amount !== ""
                  ? parseFloat(depositChanges.amount)
                  : deposit.amount,
              federal_tax_withholding:
                depositChanges.federal_tax_withholding !== undefined
                  ? (depositChanges.federal_tax_withholding !== "" && depositChanges.federal_tax_withholding !== null
                      ? parseFloat(depositChanges.federal_tax_withholding)
                      : null)
                  : deposit.federal_tax_withholding,
              state_tax_withholding:
                depositChanges.state_tax_withholding !== undefined
                  ? (depositChanges.state_tax_withholding !== "" && depositChanges.state_tax_withholding !== null
                      ? parseFloat(depositChanges.state_tax_withholding)
                      : null)
                  : deposit.state_tax_withholding,
              frequency:
                depositChanges.frequency !== undefined
                  ? depositChanges.frequency
                  : deposit.frequency,
              start_date:
                depositChanges.start_date !== undefined
                  ? formatDateForInput(depositChanges.start_date)
                  : deposit.start_date
                    ? formatDateForInput(deposit.start_date)
                    : null,
              method:
                depositChanges.method !== undefined
                  ? depositChanges.method
                  : deposit.method,
              destination:
                depositChanges.destination !== undefined
                  ? depositChanges.destination
                  : deposit.destination,
              destination_identifier:
                depositChanges.destination_identifier !== undefined
                  ? depositChanges.destination_identifier
                  : deposit.destination_identifier,
            };
          });
        }
        if ((removedDeposits[accountId] || []).length > 0) {
          update.remove_deposits = removedDeposits[accountId];
        }

        if (hasChangesInPath("withdrawals")) {
          const baseWithdrawals = Array.isArray(account.withdrawals)
            ? account.withdrawals
            : [];
          const changes = accountChanges.withdrawals || {};
          const removedWithdrawalIds = new Set(
            removedWithdrawals[accountId] || [],
          );
          const baseWithdrawalsNotRemoved = baseWithdrawals.filter(
            (w) => !removedWithdrawalIds.has(w.id),
          );
          update.withdrawals = baseWithdrawalsNotRemoved.map((withdrawal) => {
            const idx = baseWithdrawals.findIndex(
              (w) => w.id === withdrawal.id,
            );
            const withdrawalChanges = changes[idx] || {};
            return {
              id: withdrawal.id, // ID is mandatory
              type:
                withdrawalChanges.type !== undefined
                  ? (withdrawalChanges.type === "" ? null : withdrawalChanges.type)
                  : (withdrawal.type ?? null),
              amount:
                withdrawalChanges.amount !== undefined &&
                withdrawalChanges.amount !== ""
                  ? parseFloat(withdrawalChanges.amount)
                  : withdrawal.amount,
              federal_tax_withholding:
                withdrawalChanges.federal_tax_withholding !== undefined
                  ? (withdrawalChanges.federal_tax_withholding !== "" && withdrawalChanges.federal_tax_withholding !== null
                      ? parseFloat(withdrawalChanges.federal_tax_withholding)
                      : null)
                  : withdrawal.federal_tax_withholding,
              state_tax_withholding:
                withdrawalChanges.state_tax_withholding !== undefined
                  ? (withdrawalChanges.state_tax_withholding !== "" && withdrawalChanges.state_tax_withholding !== null
                      ? parseFloat(withdrawalChanges.state_tax_withholding)
                      : null)
                  : withdrawal.state_tax_withholding,
              frequency:
                withdrawalChanges.frequency !== undefined
                  ? withdrawalChanges.frequency
                  : withdrawal.frequency,
              start_date:
                withdrawalChanges.start_date !== undefined
                  ? formatDateForInput(withdrawalChanges.start_date)
                  : withdrawal.start_date
                    ? formatDateForInput(withdrawal.start_date)
                    : null,
              method:
                withdrawalChanges.method !== undefined
                  ? withdrawalChanges.method
                  : withdrawal.method,
              destination_type:
                withdrawalChanges.destination_type !== undefined
                  ? withdrawalChanges.destination_type
                  : withdrawal.destination_type,
              destination_identifier:
                withdrawalChanges.destination_identifier !== undefined
                  ? withdrawalChanges.destination_identifier
                  : withdrawal.destination_identifier,
            };
          });
        }
        if ((removedWithdrawals[accountId] || []).length > 0) {
          update.remove_withdrawals = removedWithdrawals[accountId];
        }

        if (hasChangesInPath("trust_info")) {
          const baseTrust = account.trust_info || {};
          const changes = accountChanges.trust_info || {};
          const resolvedGrantorIds =
            changes.grantorIds !== undefined
              ? changes.grantorIds
              : getTrustGrantorIds(baseTrust);
          const resolvedPrimaryGrantorId =
            (Array.isArray(resolvedGrantorIds) ? resolvedGrantorIds : []).find(Boolean) || "";
          const resolvedGrantorIdSet = new Set(
            (Array.isArray(resolvedGrantorIds) ? resolvedGrantorIds : [])
              .filter(Boolean)
              .map(String),
          );
          const resolvedRevocable = isRevocableTrust(account.type);
          const resolvedGrantorSsnDigits = getMemberSsnDigitsById(
            resolvedPrimaryGrantorId,
          );
          const baseTrustees = Array.isArray(baseTrust.trustees)
            ? baseTrust.trustees
            : [];
          const changesTrusteesObj =
            changes.trustees && typeof changes.trustees === "object"
              ? changes.trustees
              : {};
          const formIdxKeys = Object.keys(changesTrusteesObj)
            .map(Number)
            .filter(Number.isFinite);
          const maxFromForm = formIdxKeys.length ? Math.max(...formIdxKeys) : -1;
          const trusteeRowCount = Math.max(
            baseTrustees.length,
            maxFromForm + 1,
          );
          const mergedTrustees = [];
          for (let idx = 0; idx < trusteeRowCount; idx++) {
            const trustee = baseTrustees[idx];
            const trusteeChanges = changesTrusteesObj[idx] || {};
            const id =
              trusteeChanges.id !== undefined
                ? trusteeChanges.id
                : trustee?.id ?? getTrusteeMemberId(trustee);
            let relationshipToGrantor =
              trusteeChanges.relationship_to_grantor !== undefined
                ? trusteeChanges.relationship_to_grantor
                : trustee?.relationship_to_grantor;
            if (id && resolvedGrantorIdSet.has(String(id))) {
              relationshipToGrantor = null;
            }
            mergedTrustees.push({
              id: id || null,
              relationship_to_grantor: relationshipToGrantor || null,
            });
          }
          const sanitizedTrustees = finalizeTrusteesForPayload(
            mergedTrustees,
            resolvedPrimaryGrantorId,
          );

          const trustInfoPatch = {
            trust_name:
              changes.trust_name !== undefined
                ? changes.trust_name
                : baseTrust.trust_name,
            trust_title:
              changes.trust_title !== undefined
                ? changes.trust_title
                : baseTrust.trust_title,
            ssn_or_tin:
              resolvedRevocable
                ? resolvedGrantorSsnDigits.length === 9
                  ? resolvedGrantorSsnDigits
                  : null
                : changes.ssn_or_tin !== undefined
                  ? changes.ssn_or_tin
                  : baseTrust.ssn_or_tin,
            execution_date:
              changes.execution_date !== undefined
                ? formatDateForInput(changes.execution_date)
                : baseTrust.execution_date
                  ? formatDateForInput(baseTrust.execution_date)
                  : null,
            amended_date:
              changes.amended_date !== undefined
                ? formatDateForInput(changes.amended_date)
                : baseTrust.amended_date
                  ? formatDateForInput(baseTrust.amended_date)
                  : null,
            trust_state:
              changes.trust_state !== undefined
                ? changes.trust_state
                : baseTrust.trust_state,
            statutory_trust:
              changes.statutory_trust !== undefined
                ? changes.statutory_trust === true ||
                  (typeof changes.statutory_trust === "string" &&
                    changes.statutory_trust.trim().toLowerCase() === "true")
                : baseTrust.statutory_trust,
            tax_id_type:
              changes.tax_id_type !== undefined
                ? changes.tax_id_type || null
                : baseTrust.tax_id_type,
            trustees: sanitizedTrustees,
          };
          update.trust_info = buildTrustInfoUpdatePayload(baseTrust, {
            grantorIds: (Array.isArray(resolvedGrantorIds) ? resolvedGrantorIds : []).filter(Boolean),
            patch: trustInfoPatch,
          });
        }

        if (hasChangesInPath("business_info")) {
          const baseBiz = account.business_info || {};
          const changes = accountChanges.business_info || {};
          const baseAddr = baseBiz.address || {};
          const addrChanges = changes.address || {};
          const accountType =
            accountChanges.type !== undefined ? accountChanges.type : account.type;
          const taxOptions = getTaxClassificationOptions(accountType);
          const taxValue =
            changes.tax_classification !== undefined
              ? changes.tax_classification || null
              : baseBiz.tax_classification || null;
          const isOperatingRaw =
            changes.is_operating !== undefined ? changes.is_operating : baseBiz.is_operating;
          const isNonProfitRaw =
            changes.is_non_profit !== undefined ? changes.is_non_profit : baseBiz.is_non_profit;
          const controlPersons =
            changes.control_persons !== undefined
              ? (Array.isArray(changes.control_persons) ? changes.control_persons : [])
                  .filter((c) => c && c.id)
                  .map((c) => ({ id: c.id }))
              : Array.isArray(baseBiz.control_persons)
                ? baseBiz.control_persons
                : [];
          const mergeAddr = (key) =>
            addrChanges[key] !== undefined ? addrChanges[key] : baseAddr[key];
          update.business_info = {
            business_name:
              changes.business_name !== undefined ? changes.business_name : baseBiz.business_name,
            tax_classification: taxOptions.includes(taxValue) ? taxValue : null,
            is_operating:
              isOperatingRaw === true || isOperatingRaw === "true"
                ? true
                : isOperatingRaw === false || isOperatingRaw === "false"
                  ? false
                  : null,
            is_non_profit: isNonProfitRaw === true || isNonProfitRaw === "true",
            address: {
              address_line_1: mergeAddr("address_line_1"),
              address_line_2: mergeAddr("address_line_2"),
              city: mergeAddr("city"),
              province: mergeAddr("province"),
              postal_code: mergeAddr("postal_code"),
              country: mergeAddr("country"),
              phone: mergeAddr("phone") || null,
            },
            control_persons: controlPersons,
            ssn: (changes.ssn !== undefined ? changes.ssn : baseBiz.ssn) || null,
            notes: changes.notes !== undefined ? changes.notes : baseBiz.notes,
          };
        }

        if (hasChangesInPath("inheritance_info")) {
          const baseDec = account.inheritance_info?.decedent_info || {};
          const decChanges = accountChanges.inheritance_info?.decedent_info || {};
          const id = decChanges.id !== undefined ? decChanges.id : baseDec.id;
          const relationship =
            decChanges.relationship !== undefined
              ? decChanges.relationship || null
              : baseDec.relationship || null;
          update.inheritance_info = {
            decedent_info: id ? { id, relationship } : null,
          };
        }

        if (hasChangesInPath("estate_info")) {
          const baseEI = account.estate_info || {};
          const changes = accountChanges.estate_info || {};
          const resolvedExecutors =
            changes.executors !== undefined
              ? (Array.isArray(changes.executors) ? changes.executors : [])
                  .filter((x) => x && x.id)
                  .map((x) => ({ id: x.id }))
              : (baseEI.executors || []);
          update.estate_info = {
            ...baseEI,
            ...changes,
            executors: resolvedExecutors,
          };
        }

        if (hasChangesInPath("other_info")) {
          const baseOther = account.other_info || {};
          const changes = accountChanges.other_info || {};
          const baseOwners = Array.isArray(baseOther.owners)
            ? baseOther.owners
            : [];
          const ownerChanges = changes.owners || {};
          let mergedOwners;
          if (ownerChanges._length !== undefined) {
            mergedOwners = [];
            for (let i = 0; i < ownerChanges._length; i++) {
              mergedOwners.push({
                full_name: ownerChanges[i]?.full_name !== undefined
                  ? ownerChanges[i].full_name
                  : baseOwners[i]?.full_name || "",
              });
            }
          } else {
            mergedOwners = baseOwners.map((owner, idx) => ({
              full_name:
                ownerChanges[idx]?.full_name !== undefined
                  ? ownerChanges[idx].full_name
                  : owner.full_name,
            }));
            Object.keys(ownerChanges).forEach((k) => {
              const idx = parseInt(k, 10);
              if (idx >= baseOwners.length && ownerChanges[k]?.full_name) {
                mergedOwners.push({ full_name: ownerChanges[k].full_name });
              }
            });
          }
          update.other_info = {
            owners: mergedOwners.filter((o) => o.full_name),
            note:
              changes.note !== undefined
                ? changes.note
                : baseOther.note,
          };
        }

        // Simple fields
        if (accountChanges.new_account_number !== undefined)
          update.new_account_number = accountChanges.new_account_number;
        if (accountChanges.custodian !== undefined)
          update.custodian = accountChanges.custodian;
        if (accountChanges.registration !== undefined)
          update.registration = accountChanges.registration;
        if (accountChanges.type !== undefined)
          update.type = accountChanges.type;
        if (accountChanges.fee_billed_percentage !== undefined)
          update.fee_billed_percentage =
            accountChanges.fee_billed_percentage === "" ||
            accountChanges.fee_billed_percentage == null
              ? null
              : parseFloat(accountChanges.fee_billed_percentage);
        if (accountChanges.account_value !== undefined)
          update.account_value =
            accountChanges.account_value === "" ||
            accountChanges.account_value == null
              ? null
              : parseFloat(accountChanges.account_value);
        if (accountChanges.new_rep_id !== undefined)
          update.new_rep_id = accountChanges.new_rep_id;
        if (accountChanges.proceeds !== undefined)
          update.proceeds = accountChanges.proceeds;
        if (accountChanges.security_purchases !== undefined)
          update.security_purchases = accountChanges.security_purchases;
        if (accountChanges.distribution_income !== undefined)
          update.distribution_income =
            accountChanges.distribution_income || null;
        if (accountChanges.margin !== undefined)
          update.margin = accountChanges.margin;
        if (accountChanges.options !== undefined)
          update.options = accountChanges.options;
        if (accountChanges.fee_based_account !== undefined)
          update.fee_based_account = accountChanges.fee_based_account;
        if (accountChanges.check_writing !== undefined)
          update.check_writing = accountChanges.check_writing;
        if (accountChanges.is_planned !== undefined)
          update.is_planned = accountChanges.is_planned;
        if (accountChanges.notes !== undefined)
          update.notes = accountChanges.notes === "" ? null : accountChanges.notes;

        if (
          hasChangesInPath("owners_info") ||
          accountChanges.registration !== undefined
        ) {
          const baseOwners = Array.isArray(account.owners_info)
            ? [...account.owners_info]
            : [];
          const changesMap = accountChanges.owners_info || {};
          const maxIdx = Math.max(
            baseOwners.length - 1,
            ...Object.keys(changesMap).map(Number).filter(n => !isNaN(n))
          );
          const newOwners = [];
          for (let idx = 0; idx <= maxIdx; idx++) {
            const existing = baseOwners[idx] || {};
            const ownerChanges = changesMap[idx] || {};
            const resolvedId = ownerChanges.id !== undefined ? ownerChanges.id : existing.id;
            if (!resolvedId) continue;
            newOwners.push({
              ...existing,
              id: resolvedId,
              type: ownerChanges.type !== undefined ? ownerChanges.type : (existing.type || "primary"),
            });
          }
          const ownerTotal = newOwners.length || 1;
          const evenShare = Math.floor(10000 / ownerTotal) / 100;
          const firstShare = Math.round((100 - evenShare * (ownerTotal - 1)) * 100) / 100;
          update.owners_info = newOwners.map((o, i) => ({
            ...o,
            share: i === 0 ? firstShare : evenShare,
          }));
        }

        // Handle Beneficiaries and Trusted Contacts
        const hasBeneficiaryChanges = hasChangesInPath("beneficiaries");
        const hasTrustedContactChanges = hasChangesInPath("trusted_contacts");
        const hasRemovedBenes = removedBeneficiaries[accountId]?.length > 0;
        const hasRemovedTCs = removedTrustedContacts[accountId]?.length > 0;

        // Only include beneficiaries that have actually been edited
        if (hasBeneficiaryChanges && account.beneficiaries) {
          const beneficiaryChanges = accountChanges.beneficiaries || {};
          const updatedBeneficiaries = [];

          account.beneficiaries.forEach((bene) => {
            // Skip removed beneficiaries
            if ((removedBeneficiaries[accountId] || []).includes(bene.id))
              return;

            // Find the original index of this beneficiary in the full array
            const originalIdx = account.beneficiaries.findIndex(
              (b) => b.id === bene.id,
            );
            const changes = beneficiaryChanges[originalIdx];

            // Check if this beneficiary has any actual changes
            if (changes && Object.keys(changes).length > 0) {
              const prevRel = bene.relationship ?? "";
              const hasActualChange =
                (changes.household_member_id !== undefined &&
                  changes.household_member_id !== bene.household_member_id) ||
                (changes.type !== undefined && changes.type !== bene.type) ||
                (changes.share !== undefined &&
                  parseFloat(changes.share) !== bene.share) ||
                (changes.relationship !== undefined &&
                  String(changes.relationship ?? "") !== String(prevRel));

              if (hasActualChange) {
                updatedBeneficiaries.push({
                  id: bene.id,
                  household_member_id:
                    changes.household_member_id !== undefined
                      ? changes.household_member_id
                      : bene.household_member_id,
                  type: changes.type !== undefined ? changes.type : bene.type,
                  share:
                    changes.share !== undefined
                      ? parseFloat(changes.share)
                      : bene.share,
                  relationship:
                    changes.relationship !== undefined
                      ? changes.relationship || null
                      : bene.relationship,
                });
              }
            }
          });

          if (updatedBeneficiaries.length > 0) {
            update.beneficiaries = updatedBeneficiaries;
          }
        }

        // Removed beneficiaries
        if (hasRemovedBenes) {
          update.remove_beneficiaries = removedBeneficiaries[accountId];
        }

        // Only include trusted contacts that have actually been edited
        if (hasTrustedContactChanges && account.trusted_contacts) {
          const tcChanges = accountChanges.trusted_contacts || {};
          const updatedTCs = [];

          account.trusted_contacts.forEach((tc) => {
            // Skip removed trusted contacts
            if ((removedTrustedContacts[accountId] || []).includes(tc.id))
              return;

            // Find the original index of this trusted contact in the full array
            const originalIdx = account.trusted_contacts.findIndex(
              (t) => t.id === tc.id,
            );
            const changes = tcChanges[originalIdx];

            // Check if this trusted contact has any actual changes
            if (changes && Object.keys(changes).length > 0) {
              const prevTcRel = tc.relationship ?? "";
              const hasActualChange =
                (changes.household_member_id !== undefined &&
                  changes.household_member_id !== tc.household_member_id) ||
                (changes.relationship !== undefined &&
                  String(changes.relationship ?? "") !== String(prevTcRel));

              if (hasActualChange) {
                updatedTCs.push({
                  id: tc.id,
                  household_member_id:
                    changes.household_member_id !== undefined
                      ? changes.household_member_id
                      : tc.household_member_id,
                  relationship:
                    changes.relationship !== undefined
                      ? changes.relationship || null
                      : tc.relationship,
                });
              }
            }
          });

          if (updatedTCs.length > 0) {
            update.trusted_contacts = updatedTCs;
          }
        }

        // Removed trusted contacts
        if (hasRemovedTCs) {
          update.remove_trusted_contacts = removedTrustedContacts[accountId];
        }

        if (accountChanges.bankAccountsOps) {
          const bankUpdate = buildAdvisorBankAccountsUpdate(accountChanges.bankAccountsOps);
          if (bankUpdate.bank_accounts) update.bank_accounts = bankUpdate.bank_accounts;
          if (bankUpdate.add_bank_accounts) update.add_bank_accounts = bankUpdate.add_bank_accounts;
          if (bankUpdate.remove_bank_accounts) update.remove_bank_accounts = bankUpdate.remove_bank_accounts;
        }

        // Pending new deposits (added via Add Contribution modal, not yet saved)
        const pendingDeposits = pendingNewDeposits[accountId];
        if (pendingDeposits?.length) {
          update.add_deposits = pendingDeposits;
        }

        // Pending new withdrawals (added via Add Withdrawal modal, not yet saved)
        const pendingWithdrawals = pendingNewWithdrawals[accountId];
        if (pendingWithdrawals?.length) {
          update.add_withdrawals = pendingWithdrawals;
        }

        // Handle existing withdrawal updates
        if (accountChanges.withdrawals && account.withdrawals) {
          const withdrawalUpdates = [];
          account.withdrawals.forEach((w, idx) => {
            const changes = accountChanges.withdrawals[idx];
            if (changes && Object.keys(changes).length > 0) {
              const withdrawalUpdate = { id: w.id };
              Object.keys(changes).forEach(field => {
                const value = changes[field];
                if (["amount", "federal_tax_withholding", "state_tax_withholding"].includes(field)) {
                  withdrawalUpdate[field] = (value !== "" && value !== null && value !== undefined) ? parseFloat(value) : null;
                } else if (field === "start_date") {
                  withdrawalUpdate[field] = value ? formatDateForInput(value) : null;
                } else {
                  withdrawalUpdate[field] = value;
                }
              });
              // Preserve an explicit "no selection" as null.
              if (Object.prototype.hasOwnProperty.call(withdrawalUpdate, "type")) {
                withdrawalUpdate.type =
                  withdrawalUpdate.type === "" ? null : withdrawalUpdate.type;
              }
              withdrawalUpdates.push(withdrawalUpdate);
            }
          });
          if (withdrawalUpdates.length > 0) {
            update.update_withdrawals = withdrawalUpdates;
          }
        }

        if (Object.keys(update).length) {
          updates.push({
            account_id: accountId,
            update,
          });
        }
      });

      if (!updates.length) {
        setIsEditingAccounts(false);
        setAccountsFormData({});
        return;
      }

      const response = await axiosInstance.put(
        `/households/${household.id}/accounts/bulk`,
        {
          accounts: updates,
        },
      );

      if (response.data?.failed?.length) {
        toast.warning(
          `Some accounts couldn't be updated (${response.data.failed.length}).`,
        );
      } else {
        toast.success("Accounts updated.");
      }

      // Use API response data directly
      if (onHouseholdUpdate) {
        onHouseholdUpdate(response.data);
      }

      setIsEditingAccounts(false);
      setAccountsFormData({});
      setRemovedBeneficiaries({});
      setRemovedTrustedContacts({});
      setPendingNewDeposits({});
      setPendingNewWithdrawals({});
      setRemovedDeposits({});
      setRemovedWithdrawals({});
      setNewlyAddedWithdrawalBankAccounts({});
      setBeneficiaryValidationErrorsByAccount({});
    } catch (error) {
      toast.error(humanizeError(error, "Couldn't update accounts."));
    } finally {
      setSavingAccounts(false);
    }
  };

  // Handle form data changes - memoized to prevent unnecessary re-renders
  const handleFieldChange = useCallback(
    (fieldPath, value) => {
      let sanitizedValue = value;

      // Silent reset for negative numbers and max caps
      if (
        typeof sanitizedValue === "number" ||
        (typeof sanitizedValue === "string" &&
          sanitizedValue.trim() !== "" &&
          !isNaN(sanitizedValue))
      ) {
        const numVal = parseFloat(sanitizedValue);
        if (numVal < 0) sanitizedValue = 0;

        // Max value caps
        if (fieldPath === "annual_income" && numVal > 5000000) {
          handleFieldError(fieldPath, "Annual Income cannot exceed $5,000,000");
        } else if (
          ["estimated_liquid_net_worth", "estimated_total_net_worth"].includes(
            fieldPath,
          ) &&
          numVal > 10000000
        ) {
          handleFieldError(fieldPath, "Net Worth cannot exceed $10,000,000");
        } else if (
          fieldPath.startsWith("investor_experience.") &&
          numVal > 85
        ) {
          handleFieldError(fieldPath, "Experience cannot exceed 85 years");
        } else if (fieldPath === "custom_fee_percentage" && numVal > 100) {
          handleFieldError(
            fieldPath,
            "Custom fee cannot exceed 100%",
          );
        } else if (
          [
            "annual_income",
            "aum",
            "estimated_liquid_net_worth",
            "estimated_total_net_worth",
          ].includes(fieldPath) ||
          fieldPath === "custom_fee_percentage" ||
          fieldPath.startsWith("investor_experience.")
        ) {
          handleFieldError(fieldPath, null);
        }
      }

      // Range validation (From <= To)
      if (fieldPath.includes(".start") || fieldPath.includes(".end")) {
        const basePath = fieldPath.split(".").slice(0, -1).join(".");
        setHouseholdFormData((prev) => {
          const newData = { ...prev };
          const keys = fieldPath.split(".");
          let current = newData;
          for (let i = 0; i < keys.length - 1; i++) {
            if (!current[keys[i]]) current[keys[i]] = {};
            current = current[keys[i]];
          }
          current[keys[keys.length - 1]] = sanitizedValue;

          // Check range
          const baseKeys = basePath.split(".");
          let base = newData;
          for (const key of baseKeys) {
            if (base) base = base[key];
          }

          if (
            base &&
            base.start !== undefined &&
            base.end !== undefined &&
            base.start !== "" &&
            base.end !== ""
          ) {
            if (parseFloat(base.start) > parseFloat(base.end)) {
              handleFieldError(basePath, "Invalid Range");
            } else {
              handleFieldError(basePath, null);
            }
          }
          return newData;
        });
        return;
      }

      setHouseholdFormData((prev) => {
        const keys = fieldPath.split(".");
        
        const updateRecursive = (obj, pathKeys, val) => {
          const key = pathKeys[0];
          if (pathKeys.length === 1) {
            return { ...obj, [key]: val };
          }
          
          const nextTarget = obj[key] || {};
          const updatedValue = updateRecursive(nextTarget, pathKeys.slice(1), val);
          
          return { ...obj, [key]: updatedValue };
        };

        return updateRecursive(prev, keys, sanitizedValue);
      });
    },
    [handleFieldError],
  );

  const getFormValue = useCallback(
    (fieldPath, fallback) => {
      if (!fieldPath) return fallback;
      const keys = fieldPath.split(".");
      let current = householdFormData;
      for (const key of keys) {
        if (current && Object.prototype.hasOwnProperty.call(current, key)) {
          current = current[key];
        } else {
          return fallback;
        }
      }
      return current !== undefined ? current : fallback;
    },
    [householdFormData],
  );

  const applyFeeStandardToggle = useCallback(
    (val) => {
      setHouseholdFormData((prev) => ({
        ...prev,
        is_fee_standard: val,
        custom_fee_percentage: val
          ? null
          : prev.custom_fee_percentage !== undefined
            ? prev.custom_fee_percentage
            : household?.custom_fee_percentage != null &&
                household.custom_fee_percentage !== ""
              ? String(household.custom_fee_percentage)
              : "",
      }));
      if (val) {
        handleFieldError("custom_fee_percentage", null);
      }
    },
    [household?.custom_fee_percentage, handleFieldError],
  );

  const feeStructureIsStandardEffective = useMemo(
    () =>
      householdFormData.is_fee_standard !== undefined
        ? householdFormData.is_fee_standard
        : household?.is_fee_standard !== false,
    [householdFormData.is_fee_standard, household?.is_fee_standard],
  );

  const handleMemberFieldChange = useCallback((fieldPath, value) => {
    let sanitizedValue = value;

    // Silent reset for negative numbers
    if (
      typeof sanitizedValue === "number" ||
      (typeof sanitizedValue === "string" &&
        sanitizedValue.trim() !== "" &&
        !isNaN(sanitizedValue))
    ) {
      const numVal = parseFloat(sanitizedValue);
      if (numVal < 0) sanitizedValue = 0;
    }

    setMembersFormData((prev) => {
      const keys = fieldPath.split(".");

      const updateRecursive = (obj, pathKeys, val) => {
        const key = pathKeys[0];
        if (pathKeys.length === 1) {
          return { ...obj, [key]: val };
        }

        const nextTarget = obj[key] || {};
        const updatedValue = updateRecursive(nextTarget, pathKeys.slice(1), val);

        return { ...obj, [key]: updatedValue };
      };

      let next = updateRecursive(prev, keys, sanitizedValue);

      const isEmploymentStatus =
        keys.length === 3 &&
        keys[1] === "employment_info" &&
        keys[2] === "status";
      if (isEmploymentStatus && shouldDisableEmploymentDetails(sanitizedValue)) {
        const memberId = keys[0];
        const branch = next[memberId] || {};
        const ei = { ...(branch.employment_info || {}) };
        ei.status = sanitizedValue;
        ei.occupation = null;
        ei.industry = null;
        ei.employer = null;
        ei.address = null;
        ei.title = null;
        ei.tin = null;
        next = { ...next, [memberId]: { ...branch, employment_info: ei } };
      }

      return next;
    });
  }, []);

  const [addressPickerKey, setAddressPickerKey] = useState(0);

  const getMemberFormValue = useCallback(
    (fieldPath, fallback) => {
      if (!fieldPath) return fallback;
      const keys = fieldPath.split(".");
      let current = membersFormData;
      for (const key of keys) {
        if (current && Object.prototype.hasOwnProperty.call(current, key)) {
          current = current[key];
        } else {
          return fallback;
        }
      }
      return current !== undefined ? current : fallback;
    },
    [membersFormData],
  );

  const handleAccountFieldChange = useCallback(
    (fieldPath, value) => {
      let sanitizedValue = value;

      if (fieldPath.includes(".beneficiaries.")) {
        setBeneficiaryValidationErrorsByAccount({});
      }

      const investObjPathSuffix = ".risk_tolerance.invest_objectives.0";
      if (fieldPath.endsWith(investObjPathSuffix)) {
        const accountId = fieldPath.slice(0, -investObjPathSuffix.length);
        if (accountId) {
          setAccountsFormData((prev) => {
            const account = prev[accountId] || {};
            const prevRt = { ...(account.risk_tolerance || {}) };
            const liquidityKeep = prevRt.liquidity_requirements;

            if (!sanitizedValue) {
              return {
                ...prev,
                [accountId]: {
                  ...account,
                  risk_tolerance: {
                    ...prevRt,
                    invest_objectives: [],
                  },
                },
              };
            }

            const matchingKey = Object.keys(BFS_MAPPING).find(
              (k) => BFS_MAPPING[k].invest_objectives?.[0] === sanitizedValue,
            );

            let nextRt;
            if (matchingKey) {
              const d = BFS_MAPPING[matchingKey];
              nextRt = {
                ...prevRt,
                invest_objectives: [...d.invest_objectives],
                risk_tolerance: d.risk_tolerance,
                time_horizon: {
                  start: d.time_horizon.start,
                  end: d.time_horizon.end,
                },
                decision_making: d.decision_making,
                source_of_funds: Array.isArray(d.source_of_funds)
                  ? [...d.source_of_funds]
                  : [d.source_of_funds],
                use_of_funds: d.use_of_funds,
                liquidity_requirements: liquidityKeep,
              };
            } else {
              nextRt = {
                ...prevRt,
                invest_objectives: [sanitizedValue],
              };
            }

            return {
              ...prev,
              [accountId]: {
                ...account,
                risk_tolerance: nextRt,
              },
            };
          });
          return;
        }
      }

      // Silent reset for negative numbers and max caps
      if (
        typeof sanitizedValue === "number" ||
        (typeof sanitizedValue === "string" &&
          sanitizedValue.trim() !== "" &&
          !isNaN(sanitizedValue))
      ) {
        const numVal = parseFloat(sanitizedValue);
        if (numVal < 0) sanitizedValue = 0;

        // Max value caps
        if (fieldPath.includes("account_value") && numVal > 50000000000) {
          handleFieldError(
            fieldPath,
            "Account Value cannot exceed $50,000,000",
          );
        } else if (fieldPath.includes("account_value")) {
          handleFieldError(fieldPath, null);
        }
      }

      // Range validation (From <= To)
      if (fieldPath.includes(".start") || fieldPath.includes(".end")) {
        const basePath = fieldPath.split(".").slice(0, -1).join(".");
        setAccountsFormData((prev) => {
          const newData = { ...prev };
          const keys = fieldPath.split(".");
          let current = newData;
          for (let i = 0; i < keys.length - 1; i++) {
            if (!current[keys[i]]) current[keys[i]] = {};
            current = current[keys[i]];
          }
          current[keys[keys.length - 1]] = sanitizedValue;

          // Check range
          const baseKeys = basePath.split(".");
          let base = newData;
          for (const key of baseKeys) {
            if (base) base = base[key];
          }

          if (
            base &&
            base.start !== undefined &&
            base.end !== undefined &&
            base.start !== "" &&
            base.end !== ""
          ) {
            if (parseFloat(base.start) > parseFloat(base.end)) {
              handleFieldError(basePath, "Invalid Range");
            } else {
              handleFieldError(basePath, null);
            }
          }
          return newData;
        });
        return;
      }

      setAccountsFormData((prev) => {
        const keys = fieldPath.split(".");

        const updateRecursive = (obj, pathKeys, val) => {
          const key = pathKeys[0];
          if (pathKeys.length === 1) {
            if (Array.isArray(obj)) {
               const newArr = [...obj];
               newArr[parseInt(key)] = val;
               return newArr;
            }
            return { ...obj, [key]: val };
          }

          const nextTarget = obj[key] || (isNaN(pathKeys[1]) ? {} : []);
          const updatedValue = updateRecursive(nextTarget, pathKeys.slice(1), val);

          if (Array.isArray(obj)) {
             const newArr = [...obj];
             newArr[parseInt(key)] = updatedValue;
             return newArr;
          }
          return { ...obj, [key]: updatedValue };
        };

        return updateRecursive(prev, keys, sanitizedValue);
      });

      const trusteeIdMatch = fieldPath.match(/^(.+)\.trust_info\.trustees\.(\d+)\.id$/);
      if (trusteeIdMatch && sanitizedValue && household?.id) {
        const [, accountId, trusteeIdxStr] = trusteeIdMatch;
        const trusteeIdx = parseInt(trusteeIdxStr, 10);
        const accountRow = (household.accounts || []).find((a) => a.id === accountId);
        const grantorIds = getTrustGrantorIds(accountRow?.trust_info);
        const primaryGrantorId = grantorIds[0] || "";
        if (primaryGrantorId && String(sanitizedValue) !== String(primaryGrantorId)) {
          runRelationshipPrefill({
            householdId: household.id,
            memberId: sanitizedValue,
            counterpartyMemberId: primaryGrantorId,
            getCurrentValue: () => "",
            onPrefill: (relationship) => {
              setAccountsFormData((prev) => {
                const accountForm = prev[accountId] || {};
                const trustForm = accountForm.trust_info || {};
                const trustees = Array.isArray(trustForm.trustees)
                  ? [...trustForm.trustees]
                  : [];
                const row = trustees[trusteeIdx] || {};
                if (row.relationship_to_grantor) return prev;
                trustees[trusteeIdx] = { ...row, relationship_to_grantor: relationship };
                return {
                  ...prev,
                  [accountId]: {
                    ...accountForm,
                    trust_info: { ...trustForm, trustees },
                  },
                };
              });
            },
          });
        }
      }
    },
    [handleFieldError, household],
  );

  const getAccountFormValue = useCallback(
    (fieldPath, fallback) => {
      if (!fieldPath) return fallback;
      const keys = fieldPath.split(".");
      let current = accountsFormData;
      for (const key of keys) {
        if (current && Object.prototype.hasOwnProperty.call(current, key)) {
          current = current[key];
        } else {
          return fallback;
        }
      }
      return current !== undefined ? current : fallback;
    },
    [accountsFormData],
  );

  const timeHorizonToDisplayAcct = (th) => getAccountRiskTimeHorizonLabel(th, "N/A");

  const handleTimeHorizonChangeAcct = (accountId) => (_fieldPath, displayValue) => {
    const range = ACCOUNT_TIME_HORIZON_MAP[displayValue];
    if (range) {
      handleAccountFieldChange(`${accountId}.risk_tolerance.time_horizon.start`, range.start);
      handleAccountFieldChange(`${accountId}.risk_tolerance.time_horizon.end`, range.end);
    }
  };

  const getTimeHorizonFormValueAcct = (accountId) => (_path, fallback) => {
    const storedStart = getAccountFormValue(`${accountId}.risk_tolerance.time_horizon.start`, null);
    const storedEnd = getAccountFormValue(`${accountId}.risk_tolerance.time_horizon.end`, null);
    if (storedStart !== null && storedEnd !== null) {
      const entry = Object.entries(ACCOUNT_TIME_HORIZON_MAP).find(
        ([, v]) =>
          Number(v.start) === Number(storedStart) &&
          Number(v.end) === Number(storedEnd),
      );
      if (entry) return entry[0];
      const label = timeHorizonToDisplayAcct({ start: storedStart, end: storedEnd });
      return label !== "N/A" ? label : fallback;
    }
    return fallback;
  };

  const liquidityNeedsToDisplayAcct = (liquidityReq) => {
    return getLiquidityNeedsLabel(liquidityReq?.amount, "N/A");
  };

  const handleLiquidityNeedsChangeAcct = (accountId) => (_fieldPath, displayValue) => {
    const range = LIQUIDITY_NEEDS_MAP[displayValue];
    if (range) {
      handleAccountFieldChange(`${accountId}.risk_tolerance.liquidity_requirements.amount.start`, range.start);
      handleAccountFieldChange(`${accountId}.risk_tolerance.liquidity_requirements.amount.end`, range.end);
    }
  };

  const getLiquidityNeedsFormValueAcct = (accountId) => (_path, fallback) => {
    const storedStart = getAccountFormValue(`${accountId}.risk_tolerance.liquidity_requirements.amount.start`, null);
    const storedEnd = getAccountFormValue(`${accountId}.risk_tolerance.liquidity_requirements.amount.end`, null);
    if (storedStart !== null && storedEnd !== null) {
      return getLiquidityNeedsLabel({ start: storedStart, end: storedEnd }, fallback);
    }
    return fallback;
  };

  const liquidityTimeHorizonToDisplayAcct = (liquidityReq) => {
    return getLiquidityTimeHorizonLabel(liquidityReq?.time_horizon, "N/A");
  };

  const handleLiquidityTimeHorizonChangeAcct = (accountId) => (_fieldPath, displayValue) => {
    const range = LIQUIDITY_TIME_HORIZON_MAP[displayValue];
    if (range) {
      handleAccountFieldChange(`${accountId}.risk_tolerance.liquidity_requirements.time_horizon.start`, range.start);
      handleAccountFieldChange(`${accountId}.risk_tolerance.liquidity_requirements.time_horizon.end`, range.end);
    }
  };

  const getLiquidityTimeHorizonFormValueAcct = (accountId) => (_path, fallback) => {
    const storedStart = getAccountFormValue(`${accountId}.risk_tolerance.liquidity_requirements.time_horizon.start`, null);
    const storedEnd = getAccountFormValue(`${accountId}.risk_tolerance.liquidity_requirements.time_horizon.end`, null);
    if (storedStart !== null && storedEnd !== null) {
      return getLiquidityTimeHorizonLabel(
        { start: storedStart, end: storedEnd },
        fallback,
      );
    }
    return fallback;
  };

  // eslint-disable-next-line no-unused-vars
  const navigateToField = useCallback(
    (fieldType, fieldName, memberId = null, accountId = null) => {
      if (!household) return;

      // Small delay to ensure UI is ready
      setTimeout(() => {
        if (fieldType === "household") {
          setActiveTab("household");
          setIsEditing(false);

          // Map field names to sections
          const householdFields = [
            "tax_info",
            "estimated_liquid_net_worth",
            "estimated_total_net_worth",
            "annual_income",
            "aum",
            "annual_expense_range",
            "special_expense_range",
          ];
          const investorFields = [
            "investor_associations",
            "investor_experience",
          ];

          if (householdFields.includes(fieldName)) {
            setHouseholdInfoSubTab("incomeDetails");
            setTimeout(() => {
              const element = document.querySelector(
                '[data-section="income-details"]',
              );
              if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }, 100);
          } else if (investorFields.includes(fieldName)) {
            if (fieldName === "investor_associations") {
              setHouseholdInfoSubTab("qualifyingQuestions");
            } else if (fieldName === "investor_experience") {
              setHouseholdInfoSubTab("investmentExperience");
            }
            setTimeout(() => {
              const element = document.querySelector(
                `[data-section="${fieldName}"]`,
              );
              if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }, 100);
          }
        } else if (fieldType === "household_members" && memberId) {
          // Navigate to members tab and expand member
          setActiveTab("members");
          setIsEditingMembers(false);

          // Find if member is owner or additional member
          const member = household.members?.find((m) => m.id === memberId);
          if (member) {
            // Check if member is an owner
            const isOwnerMember =
              household.accounts?.some((account) =>
                account.owners_info?.some((owner) => owner.id === memberId),
              ) || false;

            if (isOwnerMember) {
              setMembersSubTab("owners");
              setSelectedOwnerId(memberId);
              setSelectedOwnerSection(
                fieldName === "personal_info"
                  ? "personal"
                  : fieldName === "address"
                    ? "contact"
                    : fieldName === "employment_info"
                      ? "employment"
                      : "personal",
              );
            } else {
              setMembersSubTab("additionalMembers");
              setSelectedAddnMemberId(memberId);
              setSelectedAddnMemberSection(
                fieldName === "personal_info"
                  ? "personal"
                  : fieldName === "address"
                    ? "contact"
                    : fieldName === "employment_info"
                      ? "employment"
                      : "personal",
              );
            }

            setTimeout(() => {
              const element = document.querySelector(
                `[data-member-id="${memberId}"]`,
              );
              if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }, 200);
          }
        } else if (fieldType === "accounts" && accountId) {
          // Navigate to accounts tab and select account + section
          setActiveTab("accounts");
          setIsEditingAccounts(false);

          setSelectedAccountId(accountId);

          const sectionMap = {
            owner_info: "basic_details",
            risk_tolerance: "investment_strategy",
            transfer_info: "transfer_details",
            trust_info: "trust_account",
            business_info: "business_info",
            inheritance_info: "inheritance_info",
            other_info: "other_info",
            bank_details: "bank_account_details",
            bank_accounts: "bank_account_details",
            withdrawals: "withdrawals",
            deposits: "contributions",
            beneficiaries: "beneficiaries",
            beneficiaries_primary_allocation: "beneficiaries",
            beneficiaries_contingent_allocation: "beneficiaries",
            beneficiaries_primary_missing: "beneficiaries",
          };
          const sectionKey = sectionMap[fieldName] || "basic_details";
          setSelectedAccountSectionByAccount((prev) => ({
            ...prev,
            [accountId]: sectionKey,
          }));

          setTimeout(() => {
            let element = null;
            if (sectionKey === "transfer_details") {
              element = document.querySelector(
                `[data-section="transfer_details_${accountId}"]`,
              );
            } else if (sectionKey === "basic_details") {
              element = document.querySelector(
                `[data-section="basic_details_${accountId}"]`,
              );
            } else if (sectionKey === "investment_strategy") {
              element = document.querySelector(
                `[data-section="investment_strategy_${accountId}"]`,
              );
            } else if (sectionKey === "contributions") {
              element = document.querySelector(
                `[data-section="contributions_${accountId}"]`,
              );
            } else if (sectionKey === "withdrawals") {
              element = document.querySelector(
                `[data-section="withdrawals_${accountId}"]`,
              );
            } else if (sectionKey === "beneficiaries") {
              element = document.querySelector(
                `[data-section="beneficiaries_${accountId}"]`,
              );
            } else if (sectionKey === "trust_account") {
              element = document.querySelector(
                `[data-section="trust_info_${accountId}"]`,
              );
            } else if (sectionKey === "business_info") {
              element = document.querySelector(
                `[data-section="business_info_${accountId}"]`,
              );
            } else if (sectionKey === "inheritance_info") {
              element = document.querySelector(
                `[data-section="inheritance_info_${accountId}"]`,
              );
            } else if (sectionKey === "other_info") {
              element = document.querySelector(
                `[data-section="other_info_${accountId}"]`,
              );
            } else if (sectionKey === "bank_account_details") {
              element = document.querySelector(
                `[data-section="bank_account_details_${accountId}"]`,
              );
            }

            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "start" });
            } else {
              const accountElement = document.querySelector(
                `[data-account-id="${accountId}"]`,
              );
              if (accountElement) {
                accountElement.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }
            }
          }, 300);
        }
      }, 50);
    },
    [household],
  );

  const CollapsibleSection = ({
    title,
    isExpanded,
    onToggle,
    children,
    icon,
    subtitle,
    headerAction,
  }) => {
    const handleToggle = (e) => {
      e.preventDefault();
      onToggle();
    };

    return (
      <div className="border border-gray-200 rounded-lg">
        <div className="flex items-center justify-between">
          <button
            onClick={handleToggle}
            className={`flex-1 px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors text-left ${
              isExpanded ? "rounded-t-lg" : "rounded-lg"
            }`}
          >
            <div className="flex items-center gap-2">
              {icon}
              <span className="font-medium text-gray-900">{title}</span>
              {subtitle && (
                <span className="text-sm font-medium">{subtitle}</span>
              )}
            </div>
            <svg
              className={`w-5 h-5 text-gray-500 transition-transform ${isExpanded ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          {headerAction && <div className="pr-2">{headerAction}</div>}
        </div>
        {isExpanded && <div className="p-4 bg-white rounded-b-lg">{children}</div>}
      </div>
    );
  };

  const ToggleField = ({
    label,
    value,
    isEditable = false,
    isEditing = false,
    onChange = null,
    fieldPath = null,
    onFieldChange = null,
    formData = null,
  }) => {
    // Get current form value or use original value
    const getCurrentValue = () => {
      if (!fieldPath || !formData) return value;
      const keys = fieldPath.split(".");
      let current = formData;
      for (const key of keys) {
        if (current && current[key] !== undefined) {
          current = current[key];
        } else {
          return value;
        }
      }
      return current !== undefined ? current : value;
    };

    const currentValue = getCurrentValue();

    const handleToggle = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const newValue = !currentValue;
      if (onChange) {
        onChange(newValue);
      }
      if (onFieldChange && fieldPath) {
        onFieldChange(fieldPath, newValue);
      }
    };

    return (
      <div className="flex items-center justify-between py-2">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        {isEditing && isEditable ? (
          <button
            type="button"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
              currentValue ? "bg-emerald-600" : "bg-gray-200"
            }`}
            onClick={handleToggle}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                currentValue ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        ) : (
          <div
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              value ? "bg-emerald-600" : "bg-gray-200"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                value ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </div>
        )}
      </div>
    );
  };

  const CheckboxField = ({
    label,
    value,
    isEditable = false,
    isEditing = false,
    fieldPath = null,
    onFieldChange = null,
    formData = null,
    /** "field" = label above value/control (matches DataField in 2-col grids). */
    variant = "row",
  }) => {
    const getCurrentValue = () => {
      if (!fieldPath || !formData) return value;
      const keys = fieldPath.split(".");
      let current = formData;
      for (const key of keys) {
        if (current && current[key] !== undefined) {
          current = current[key];
        } else {
          return value;
        }
      }
      return current !== undefined ? current : value;
    };

    const currentValue = getCurrentValue();

    const handleChange = (e) => {
      if (onFieldChange && fieldPath) {
        onFieldChange(fieldPath, e.target.checked);
      }
    };

    if (variant === "field") {
      return (
        <div className="py-2">
          <span className="text-sm font-medium text-gray-500">{label}</span>
          {isEditing && isEditable ? (
            <label className="mt-1 flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={currentValue || false}
                onChange={handleChange}
                className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-900">
                {currentValue ? "Yes" : "No"}
              </span>
            </label>
          ) : (
            <p className="text-sm text-gray-900 mt-1">
              {currentValue ? "Yes" : "No"}
            </p>
          )}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2 py-2">
        {isEditing && isEditable ? (
          <>
            <input
              type="checkbox"
              checked={currentValue || false}
              onChange={handleChange}
              className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
            />
            <span className="text-sm font-medium text-gray-700">{label}</span>
          </>
        ) : (
          <>
            <span className="text-sm font-medium text-gray-700 flex-1">
              {label}
            </span>
            <span className="text-sm text-gray-900">
              {currentValue ? "Yes" : "No"}
            </span>
          </>
        )}
      </div>
    );
  };

  const OwnerDropdownField = ({
    label,
    value,
    isEditable = false,
    isEditing = false,
    disabled = false,
    excludeMemberIds = [],
    householdMembers = [],
    fieldPath = null,
    onFieldChange = null,
    getFormValue = null,
  }) => {
    // Determine the current member ID to bind the select to
    const getCurrentValue = () => {
      // If we don't have a field path or getter, try to derive ID from the label value
      if (!fieldPath || !getFormValue) {
        if (!value) return "";
        const memberFromLabel = householdMembers.find((m) => {
          const memberName =
            `${m.personal_info?.first_name || ""} ${m.personal_info?.last_name || ""}`.trim();
          return memberName === value;
        });
        return memberFromLabel ? String(memberFromLabel.id) : "";
      }

      const stored = getFormValue(fieldPath, null);
      if (stored === null || stored === undefined || stored === "") {
        if (!value) return "";
        const memberFromLabel = householdMembers.find((m) => {
          const memberName =
            `${m.personal_info?.first_name || ""} ${m.personal_info?.last_name || ""}`.trim();
          return memberName === value;
        });
        return memberFromLabel ? String(memberFromLabel.id) : "";
      }

      return String(stored);
    };

    const currentValue = getCurrentValue();

    const handleChange = (e) => {
      if (onFieldChange && fieldPath) {
        const selectedId = e.target.value;
        onFieldChange(fieldPath, selectedId || null);
      }
    };

    return (
      <div className="py-2">
        <span className="text-sm font-medium text-gray-500">{label}</span>
        {isEditing && isEditable ? (
          <select
            value={disabled ? "" : currentValue}
            onChange={handleChange}
            disabled={disabled}
            className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm ${disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`}
          >
            <option value="">Select {label}</option>
            {householdMembers
              .map((m) => {
                const memberName =
                  `${m.personal_info?.first_name || ""} ${m.personal_info?.last_name || ""}`.trim();
                if (memberName && !excludeMemberIds.includes(String(m.id))) {
                  return (
                    <option key={m.id} value={m.id}>
                      {memberName}
                    </option>
                  );
                }
                return null;
              })
              .filter(Boolean)}
          </select>
        ) : (
          <p className="text-sm text-gray-900 mt-1">{value || (disabled ? "N/A (single-owner account)" : "N/A")}</p>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {/* Document Processing Status */}
      <DocumentProcessingStatus docs_status={docsStatus} />

      {household?.client_note && household.client_note.trim() !== "" && (
        <div className="mb-6 flex items-start justify-between gap-3 px-4 py-3 rounded-xl bg-emerald-50 border-2 border-emerald-300">
          <div className="flex items-start gap-3">
            <svg
              className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 10h8M8 14h6m-2 7H6a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2v8l-4 4z"
              />
            </svg>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-emerald-800">Client note</p>
              <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                {household.client_note}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={confirmClearClientNote}
            disabled={clearingClientNote}
            className="px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors disabled:opacity-50"
          >
            {clearingClientNote ? "Deleting..." : "Delete"}
          </button>
        </div>
      )}

      {/* Unlinked Data Section */}
      {(unmappedBeneficiaries.length > 0 ||
        unmappedBankAccounts.length > 0 ||
        accountsMissingBeneficiary.length > 0) && (
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border-2 border-amber-300 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <button
                onClick={() => setUnmappedDataExpanded(!unmappedDataExpanded)}
                className="w-full flex items-center justify-between mb-2 text-left hover:opacity-80 transition-opacity cursor-pointer"
                aria-label={unmappedDataExpanded ? "Collapse" : "Expand"}
              >
                <h4 className="font-semibold text-gray-900">
                  {[
                    unmappedBankAccounts.length > 0 && "Unlinked Bank Accounts",
                    unmappedBeneficiaries.length > 0 &&
                      "Unlinked Household Members",
                    accountsMissingBeneficiary.length > 0 &&
                      "Missing Beneficiaries",
                  ]
                    .filter(Boolean)
                    .join(" & ")}
                </h4>
                <ChevronDown
                  className={`w-5 h-5 text-amber-700 transition-transform duration-200 ${unmappedDataExpanded ? "rotate-180" : ""}`}
                />
              </button>

              {unmappedDataExpanded && (
                <div>
                  {(unmappedBeneficiaries.length > 0 ||
                    unmappedBankAccounts.length > 0) && (
                    <p className="text-sm text-gray-700 mb-3">
                      Please map the following entities records to one or more
                      accounts
                    </p>
                  )}

                  {unmappedBeneficiaries.length > 0 && (
                    <div className="mb-3">
                      <h5 className="text-sm font-semibold text-gray-800 mb-2">
                        Unlinked Household Members:
                      </h5>
                      <div className="flex flex-wrap gap-2">
                        {unmappedBeneficiaries.map((member) => (
                          <button
                            key={member.id}
                            onClick={() => {
                              setSelectedUnlinkedMemberId(member.id);
                              setShowLinkUnlinkedMemberModal(true);
                            }}
                            className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-medium rounded-lg border border-amber-300 transition-colors cursor-pointer flex items-center gap-1.5"
                          >
                            {member.personal_info?.first_name}{" "}
                            {member.personal_info?.last_name}
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                              />
                            </svg>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {unmappedBankAccounts.length > 0 && (
                    <div className="mb-3">
                      <h5 className="text-sm font-semibold text-gray-800 mb-2">
                        Unlinked Bank Accounts:
                      </h5>
                      <div className="flex flex-wrap gap-2">
                        {unmappedBankAccounts.map((ba) => (
                          <button
                            key={ba.id}
                            onClick={() => {
                              setSelectedUnlinkedBankAccountId(ba.id);
                              setSelectedUnlinkedBankName(
                                `${ba.bank_name || "Bank"} - ${ba.account_number}`,
                              );
                              setShowLinkUnlinkedBankModal(true);
                            }}
                            className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-medium rounded-lg border border-amber-300 transition-colors cursor-pointer flex items-center gap-1.5"
                          >
                            {ba.bank_name || "Bank"} - {ba.account_number}
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                              />
                            </svg>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {accountsMissingBeneficiary.length > 0 && (
                    <div className="mb-3">
                      <h5 className="text-sm font-semibold text-gray-800 mb-2">
                        Beneficiary missing for accounts:
                      </h5>
                      <div className="flex flex-wrap gap-2">
                        {accountsMissingBeneficiary.map(({ account, index }) => (
                          <button
                            key={account.id}
                            onClick={() =>
                              handleAddBeneficiaryForAccount(
                                account.id,
                                "beneficiary",
                              )
                            }
                            className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-medium rounded-lg border border-amber-300 transition-colors cursor-pointer"
                          >
                            {formatAccountName(
                              account,
                              index + 1,
                              household?.members || [],
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Missing Fields Section */}
      {missingFields &&
        (missingFields.household?.length > 0 ||
          (missingFields.household_members &&
            Object.keys(missingFields.household_members).some(
              (key) => missingFields.household_members[key]?.length > 0,
            )) ||
          (missingFields.accounts &&
            Object.keys(missingFields.accounts).some(
              (key) => missingFields.accounts[key]?.length > 0,
            )) ||
          Object.keys(getMissingBankAccountsMap(missingFields)).length > 0) && (
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border-2 border-amber-300 mb-6">
            <div className="flex items-start gap-3">
              <svg
                className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div className="flex-1">
                <button
                  type="button"
                  onClick={() => setMissingFieldsExpanded((p) => !p)}
                  className="w-full flex items-center justify-between mb-2 text-left hover:opacity-80 transition-opacity cursor-pointer"
                  aria-expanded={missingFieldsExpanded}
                  aria-label={missingFieldsExpanded ? "Collapse" : "Expand"}
                >
                  <h4 className="font-semibold text-gray-900">
                    Edit Pending Data
                  </h4>
                  <svg
                    className={`w-5 h-5 text-amber-700 transition-transform duration-200 flex-shrink-0 ${missingFieldsExpanded ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {missingFieldsExpanded && (
                <div>
                    <p className="text-sm text-gray-700 mb-3">
                      Click any button below to complete details
                    </p>

                    {/* Household Missing Fields */}
                    {missingFields.household &&
                      missingFields.household.length > 0 && (
                        <div className="mb-3">
                          <h5 className="text-sm font-semibold text-gray-800 mb-2">
                            Household:
                          </h5>
                          <div className="flex flex-wrap gap-2">
                            {(() => {
                              const incomeFields = [
                                "tax_info",
                                "estimated_liquid_net_worth",
                                "estimated_total_net_worth",
                                "annual_income",
                                "aum",
                                "annual_expense_range",
                                "special_expense_range",
                              ];
                              const visibleFields =
                                missingFields.household.filter((field) => {
                                  if (field === "qualified_purchaser")
                                    return false;
                                  if (field === "annual_expense_range")
                                    return showAnnualExpenseRange(
                                      brokerageFlags,
                                    );
                                  if (field === "special_expense_range")
                                    return showSpecialExpenseRange(
                                      brokerageFlags,
                                    );
                                  if (field === "source_of_funds")
                                    return showSourceOfFunds(brokerageFlags);
                                  if (field === "use_of_funds")
                                    return showUseOfFunds(brokerageFlags);
                                  if (field === "liquidity_requirements")
                                    return showLiquidityRequirements(
                                      brokerageFlags,
                                    );
                                  if (
                                    !showExperienceField(field, brokerageFlags, household?.accounts)
                                  )
                                    return false;
                                  return true;
                                });
                              const hasIncomeFields = visibleFields.some(
                                (field) => incomeFields.includes(field),
                              );
                              const nonIncomeFields = visibleFields.filter(
                                (field) => !incomeFields.includes(field),
                              );

                              const getFieldLabel = (fieldName) => {
                                if (typeof fieldName !== "string") return "";
                                if (fieldName === "investor_associations")
                                  return "Qualifying Questions";
                                return fieldName
                                  .replace(/_/g, " ")
                                  .replace(/\b\w/g, (l) => l.toUpperCase());
                              };

                              return (
                                <>
                                  {hasIncomeFields && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSelectedMissingField(
                                          "income_details",
                                        );
                                        setShowMissingFieldModal(true);
                                      }}
                                      className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-medium rounded-lg transition duration-200 border border-amber-300"
                                    >
                                      Income Details
                                    </button>
                                  )}
                                  {nonIncomeFields.map((field, idx) => (
                                    <button
                                      type="button"
                                      key={idx}
                                      onClick={() => {
                                        setSelectedMissingField(field);
                                        setShowMissingFieldModal(true);
                                      }}
                                      className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-medium rounded-lg transition duration-200 border border-amber-300"
                                    >
                                      {getFieldLabel(field)}
                                    </button>
                                  ))}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      )}

                    {/* Household Members Missing Fields */}
                    {missingFields.household_members &&
                      Object.keys(missingFields.household_members).length >
                        0 && (
                        <div className="mb-3">
                          <h5 className="text-sm font-semibold text-gray-800 mb-2">
                            Household Members:
                          </h5>
                          {Object.entries(missingFields.household_members).map(
                            ([memberId, fields]) => {
                              const member = household?.members?.find(
                                (m) => m.id === memberId,
                              );
                              const memberFirstName =
                                member?.personal_info?.first_name ||
                                "Unknown Member";

                              const memberPills = getMemberMissingPills({
                                member,
                                memberId,
                                fields,
                                household,
                                brokerageFlags,
                              });

                              if (memberPills.length === 0) return null;

                              return (
                                <div key={memberId} className="mb-2">
                                  <span className="text-xs text-gray-600">
                                    {memberFirstName}:
                                  </span>
                                  <div className="flex flex-wrap gap-2 mt-1">
                                    {memberPills.map((pill, idx) => (
                                      <button
                                        key={`${pill.key}-${idx}`}
                                        onClick={() => {
                                          setSelectedMemberField(
                                            pill.editorField,
                                          );
                                          setSelectedMemberId(memberId);
                                          setShowMissingMemberFieldModal(true);
                                        }}
                                        className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-medium rounded-lg transition duration-200 border border-amber-300"
                                      >
                                        {pill.label}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              );
                            },
                          )}
                        </div>
                      )}

                    {/* Accounts Missing Fields */}
                    {(() => {
                      const accountRows = (household?.accounts || [])
                        .map((account, accountIndex) => {
                              const accountId = account.id;
                              const rawEntry = missingFields?.accounts?.[accountId];
                              // Account-field tokens only; bank problems now live
                              // in the top-level missing_fields.bank_accounts map.
                              const normalizedFields = Array.isArray(rawEntry)
                                ? rawEntry.filter((e) => typeof e === "string")
                                : Array.isArray(rawEntry?.fields)
                                  ? rawEntry.fields.filter((e) => typeof e === "string")
                                  : [];
                              const accountDisplay = formatAccountName(
                                account,
                                accountIndex + 1,
                                household?.members || [],
                              );

                              // Custom label mapping
                              const getFieldLabel = (fieldName) => {
                                if (typeof fieldName !== "string") return "";
                                if (fieldName === "risk_tolerance")
                                  return "Investment Strategy";
                                if (fieldName === "transfer_info")
                                  return "Transfer Details";
                                if (fieldName === "trust_info")
                                  return "Trust Info";
                                if (fieldName === "business_info")
                                  return "Business Info";
                                if (fieldName === "inheritance_info")
                                  return "Inheritance Info";
                                if (fieldName === "minor_details")
                                  return "Minor Details";
                                if (fieldName === "other_info")
                                  return "Other Info";
                                if (fieldName === "basic_details")
                                  return "Basic Details";
                                if (fieldName === "beneficiaries")
                                  return "Beneficiaries";
                                if (fieldName === "beneficiaries_primary_missing")
                                  return "Primary Beneficiary Missing";
                                if (fieldName === "trusted_contacts")
                                  return "Trusted Contacts";
                                if (
                                  fieldName === "bank_details" ||
                                  fieldName === "bank_accounts"
                                )
                                  return "Linked Bank Account";
                                return fieldName
                                  .replace(/_/g, " ")
                                  .replace(/\b\w/g, (l) => l.toUpperCase());
                              };

                              if (normalizedFields.length === 0) return null;

                              return (
                                <div key={accountId} className="mb-2">
                                  <span className="text-xs text-gray-600">
                                    {accountDisplay}:
                                  </span>
                                  <div className="flex flex-wrap gap-2 mt-1">
                                    {normalizedFields.map((field, idx) => (
                                      <button
                                        key={idx}
                                        onClick={() => {
                                          if (
                                            field ===
                                            "beneficiaries_primary_missing"
                                          ) {
                                            handleAddBeneficiaryForAccount(
                                              accountId,
                                              "beneficiary",
                                            );
                                            return;
                                          }
                                          setSelectedAccountField(field);
                                          setSelectedAccountId(accountId);
                                          setSelectedBankAccountId(null);
                                          setShowMissingAccountFieldModal(true);
                                        }}
                                        className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-medium rounded-lg transition duration-200 border border-amber-300"
                                      >
                                        {getFieldLabel(field)}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              );
                        })
                        .filter(Boolean);

                      if (accountRows.length === 0) return null;

                      return (
                        <div>
                          <h5 className="text-sm font-semibold text-gray-800 mb-2">
                            Accounts:
                          </h5>
                          {accountRows}
                        </div>
                      );
                    })()}

                    {/* Bank Accounts Missing Fields — one tile per flagged bank id */}
                    {(() => {
                      const bankMap = getMissingBankAccountsMap(missingFields);
                      const bankIds = Object.keys(bankMap);
                      if (bankIds.length === 0) return null;
                      const householdBanksById = Object.fromEntries(
                        (household?.bank_accounts || [])
                          .filter((b) => b?.id)
                          .map((b) => [b.id, b]),
                      );
                      return (
                        <div>
                          <h5 className="text-sm font-semibold text-gray-800 mb-2">
                            Bank Accounts:
                          </h5>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {bankIds.map((bankId) => {
                              const bank = householdBanksById[bankId] || { id: bankId };
                              const linkedAccount = (household?.accounts || []).find(
                                (a) => isBankLinkedToAccount(a, bankId),
                              );
                              return (
                                <button
                                  key={bankId}
                                  disabled={!linkedAccount}
                                  onClick={() => {
                                    if (!linkedAccount) return;
                                    setSelectedAccountField("bank_details");
                                    setSelectedAccountId(linkedAccount.id);
                                    setSelectedBankAccountId(bankId);
                                    setShowMissingAccountFieldModal(true);
                                  }}
                                  className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-medium rounded-lg transition duration-200 border border-amber-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {formatBankLabel(bank)}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      {/* Primary tabs */}
      <div className="flex flex-wrap gap-3 items-center mb-6 pb-2 border-b-2 border-gray-200">
        <div className="flex gap-3">
          <button
            onClick={() => setActiveTab("household")}
            className={`px-6 py-3 font-semibold rounded-t-xl transition-all duration-200 ${
              activeTab === "household"
                ? "text-emerald-700 bg-gradient-to-b from-emerald-50 to-transparent border-b-2 border-emerald-600 shadow-sm"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            Household Info
          </button>
          <button
            onClick={() => setActiveTab("members")}
            className={`px-6 py-3 font-semibold rounded-t-xl transition-all duration-200 ${
              activeTab === "members"
                ? "text-emerald-700 bg-gradient-to-b from-emerald-50 to-transparent border-b-2 border-emerald-600 shadow-sm"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            Household Members ({household.members?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab("accounts")}
            className={`px-6 py-3 font-semibold rounded-t-xl transition-all duration-200 ${
              activeTab === "accounts"
                ? "text-emerald-700 bg-gradient-to-b from-emerald-50 to-transparent border-b-2 border-emerald-600 shadow-sm"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            Accounts ({household.accounts?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab("bank_accounts")}
            className={`px-6 py-3 font-semibold rounded-t-xl transition-all duration-200 ${
              activeTab === "bank_accounts"
                ? "text-emerald-700 bg-gradient-to-b from-emerald-50 to-transparent border-b-2 border-emerald-600 shadow-sm"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            Bank Accounts (
            {(Array.isArray(householdBankAccounts)
              ? householdBankAccounts.length
              : 0) || 0}
            )
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-96">
        {activeTab === "household" && (
          <div className="space-y-4">
            {/* Household Info sub-tabs + edit actions */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 pb-2">
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => setHouseholdInfoSubTab("incomeDetails")}
                  className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                    householdInfoSubTab === "incomeDetails"
                      ? "bg-emerald-100 text-emerald-800 border-b-2 border-emerald-600"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  Income Details
                </button>
                <button
                  type="button"
                  onClick={() => setHouseholdInfoSubTab("qualifyingQuestions")}
                  className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                    householdInfoSubTab === "qualifyingQuestions"
                      ? "bg-emerald-100 text-emerald-800 border-b-2 border-emerald-600"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  Qualifying Questions
                </button>
                {hasVisibleExperienceFields && (
                  <button
                    type="button"
                    onClick={() => setHouseholdInfoSubTab("investmentExperience")}
                    className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                      householdInfoSubTab === "investmentExperience"
                        ? "bg-emerald-100 text-emerald-800 border-b-2 border-emerald-600"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    Investment Experience (Years)
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setHouseholdInfoSubTab("feeStructure")}
                  className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                    householdInfoSubTab === "feeStructure"
                      ? "bg-emerald-100 text-emerald-800 border-b-2 border-emerald-600"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  Fee Structure
                </button>
              </div>
              <div className="flex gap-2 shrink-0">
                {isEditing ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditing(false);
                        setValidationErrors({});
                        setHouseholdFormData({});
                      }}
                      className="btn-secondary text-xs px-3 py-2 flex items-center gap-1.5"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveHousehold}
                      disabled={
                        savingHousehold ||
                        Object.keys(validationErrors).length > 0
                      }
                      className="btn-primary text-xs px-3 py-2 flex items-center gap-1.5 disabled:opacity-50"
                    >
                      {savingHousehold ? (
                        <>
                          <svg
                            className="animate-spin w-3.5 h-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Saving...
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          Save
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs px-3 py-2 flex items-center gap-1.5 transition-colors"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Edit
                  </button>
                )}
              </div>
            </div>
            {householdInfoSubTab === "incomeDetails" && (
              <div
                data-section="income-details"
                className="border border-gray-200 rounded-lg p-4 bg-white"
              >
                {isEditing &&
                  householdSubTabHasUnsavedChanges(
                    householdFormData,
                    householdInfoSubTab,
                  ) && (
                  <UnsavedChangesBanner
                    className="mb-4"
                    message={advisorHouseholdUnsavedMessage(householdInfoSubTab)}
                  />
                )}
                <div className="grid grid-cols-2 gap-4">
                  {showIncomeDetails(brokerageFlags, household?.accounts) && (
                    <DataField
                      getFormValue={getFormValue}
                      label="Annual Income"
                      value={formatCurrency(household.annual_income)}
                      isEditable={true}
                      type="number"
                      min={0}
                      max={5000000}
                      isEditing={isEditing}
                      fieldPath="annual_income"
                      onFieldChange={handleFieldChange}
                      disableWheel={true}
                      error={validationErrors.annual_income}
                      onFieldError={handleFieldError}
                    />
                  )}
                  {aumAvailability !== 3 && (
                    <DataField
                      getFormValue={getFormValue}
                      label="AUM"
                      value={formatCurrency(household.aum)}
                      isEditable={false}
                      type="number"
                      min={0}
                      isEditing={isEditing}
                      fieldPath="aum"
                      onFieldChange={handleFieldChange}
                      disableWheel={true}
                      error={validationErrors.aum}
                      onFieldError={handleFieldError}
                    />
                  )}
                  <DataField
                    getFormValue={getFormValue}
                    label="Tax Bracket"
                    value={formatTaxBracket(household.tax_info)}
                    isEditable={true}
                    isEditing={isEditing}
                    options={TAX_BRACKET_OPTIONS}
                    fieldPath="tax_info.tax_bracket_display"
                    onFieldChange={(_fieldPath, displayValue) => {
                      const range = TAX_BRACKET_MAP[displayValue];
                      if (range) {
                        handleFieldChange("tax_info.tax_bracket.start", range.start);
                        handleFieldChange("tax_info.tax_bracket.end", range.end);
                      }
                    }}
                    error={validationErrors["tax_info.tax_bracket"]}
                    onFieldError={handleFieldError}
                  />
                  {showIncomeDetails(brokerageFlags, household?.accounts) && (
                    <>
                      <DataField
                        getFormValue={getFormValue}
                        label="Estimated Liquid Net Worth"
                        value={formatCurrency(household.estimated_liquid_net_worth)}
                        isEditable={true}
                        type="number"
                        min={0}
                        max={10000000}
                        isEditing={isEditing}
                        fieldPath="estimated_liquid_net_worth"
                        onFieldChange={handleFieldChange}
                        disableWheel={true}
                        error={validationErrors.estimated_liquid_net_worth}
                        onFieldError={handleFieldError}
                      />
                      <DataField
                        getFormValue={getFormValue}
                        label="Estimated Total Net Worth"
                        value={formatCurrency(household.estimated_total_net_worth)}
                        isEditable={true}
                        type="number"
                        min={0}
                        max={10000000}
                        isEditing={isEditing}
                        fieldPath="estimated_total_net_worth"
                        onFieldChange={handleFieldChange}
                        disableWheel={true}
                        error={validationErrors.estimated_total_net_worth}
                        onFieldError={handleFieldError}
                      />
                    </>
                  )}
                  {showHouseholdSourceOfWealth(brokerageFlags) && (
                    <DataField
                      getFormValue={getFormValue}
                      label="Source of Wealth"
                      value={household.source_of_wealth || ""}
                      isEditable={true}
                      isEditing={isEditing}
                      options={SOURCE_OF_WEALTH_OPTIONS}
                      fieldPath="source_of_wealth"
                      onFieldChange={handleFieldChange}
                      error={validationErrors.source_of_wealth}
                      onFieldError={handleFieldError}
                    />
                  )}
                  {showBankingEstimates(brokerageFlags) && (
                    <>
                      <DataField
                        getFormValue={getFormValue}
                        label="Checks per month"
                        value={household.banking_estimates?.no_of_checks_per_month ?? ""}
                        isEditable={true}
                        type="number"
                        min={0}
                        isEditing={isEditing}
                        fieldPath="banking_estimates.no_of_checks_per_month"
                        onFieldChange={handleFieldChange}
                        disableWheel={true}
                        error={validationErrors["banking_estimates.no_of_checks_per_month"]}
                        onFieldError={handleFieldError}
                      />
                      <DataField
                        getFormValue={getFormValue}
                        label="Domestic wire transfers per month"
                        value={household.banking_estimates?.no_of_domestic_wire_transfers_per_month ?? ""}
                        isEditable={true}
                        type="number"
                        min={0}
                        isEditing={isEditing}
                        fieldPath="banking_estimates.no_of_domestic_wire_transfers_per_month"
                        onFieldChange={handleFieldChange}
                        disableWheel={true}
                        error={validationErrors["banking_estimates.no_of_domestic_wire_transfers_per_month"]}
                        onFieldError={handleFieldError}
                      />
                      <DataField
                        getFormValue={getFormValue}
                        label="International wire transfers per month"
                        value={household.banking_estimates?.no_of_international_wire_transfers_per_month ?? ""}
                        isEditable={true}
                        type="number"
                        min={0}
                        isEditing={isEditing}
                        fieldPath="banking_estimates.no_of_international_wire_transfers_per_month"
                        onFieldChange={handleFieldChange}
                        disableWheel={true}
                        error={validationErrors["banking_estimates.no_of_international_wire_transfers_per_month"]}
                        onFieldError={handleFieldError}
                      />
                    </>
                  )}
                  {showAnnualExpenseRange(brokerageFlags) && (
                    <DataField
                      getFormValue={getFormValue}
                      label="Annual Expenses Range"
                      value={formatRange(household.annual_expense_range)}
                      isEditable={true}
                      isEditing={isEditing}
                      isRange={true}
                      rangeData={household.annual_expense_range}
                      fieldPath="annual_expense_range"
                      onFieldChange={handleFieldChange}
                      disableWheel={true}
                      error={validationErrors.annual_expense_range}
                      onFieldError={handleFieldError}
                    />
                  )}
                  {showSpecialExpenseRange(brokerageFlags) && (
                    <DataField
                      getFormValue={getFormValue}
                      label="Special Expenses Range"
                      value={formatRange(household.special_expense_range)}
                      isEditable={true}
                      isEditing={isEditing}
                      isRange={true}
                      rangeData={household.special_expense_range}
                      fieldPath="special_expense_range"
                      onFieldChange={handleFieldChange}
                      disableWheel={true}
                      error={validationErrors.special_expense_range}
                      onFieldError={handleFieldError}
                    />
                  )}
                </div>
              </div>
            )}
            {householdInfoSubTab === "qualifyingQuestions" && (
              <div
                data-section="investor_associations"
                className="border border-gray-200 rounded-lg p-4 bg-white"
              >
                {isEditing &&
                  householdSubTabHasUnsavedChanges(
                    householdFormData,
                    householdInfoSubTab,
                  ) && (
                  <UnsavedChangesBanner
                    className="mb-4"
                    message={advisorHouseholdUnsavedMessage(householdInfoSubTab)}
                  />
                )}
                <div className="space-y-2">
                  <ToggleField
                    label="Are you an accredited investor"
                    value={household.investor_associations?.accredited_investor}
                    isEditable={true}
                    isEditing={isEditing}
                    fieldPath="investor_associations.accredited_investor"
                    onFieldChange={handleFieldChange}
                    formData={householdFormData}
                  />
                  <ToggleField
                    label="Are you a qualified purchaser"
                    value={
                      household.investor_associations?.qualified_purchaser ?? false
                    }
                    isEditable={true}
                    isEditing={isEditing}
                    fieldPath="investor_associations.qualified_purchaser"
                    onFieldChange={handleFieldChange}
                    formData={householdFormData}
                  />
                  <ToggleField
                    label="Are you associated with a US Broker Dealer"
                    value={
                      household.investor_associations
                        ?.us_broker_dealer_association
                    }
                    isEditable={true}
                    isEditing={isEditing}
                    fieldPath="investor_associations.us_broker_dealer_association"
                    onFieldChange={handleFieldChange}
                    formData={householdFormData}
                  />
                  <ToggleField
                    label="Are you an employee of a Broker Dealer"
                    value={
                      household.investor_associations?.broker_dealer_employee
                    }
                    isEditable={true}
                    isEditing={isEditing}
                    fieldPath="investor_associations.broker_dealer_employee"
                    onFieldChange={handleFieldChange}
                    formData={householdFormData}
                  />
                  <ToggleField
                    label="Are you associated with an RIA"
                    value={household.investor_associations?.ria_association}
                    isEditable={true}
                    isEditing={isEditing}
                    fieldPath="investor_associations.ria_association"
                    onFieldChange={handleFieldChange}
                    formData={householdFormData}
                  />
                  <ToggleField
                    label="Is any member a 10% shareholder or board member of a public company?"
                    value={
                      household.investor_associations
                        ?.shareholder_or_member_board_director
                    }
                    isEditable={true}
                    isEditing={isEditing}
                    fieldPath="investor_associations.shareholder_or_member_board_director"
                    onFieldChange={handleFieldChange}
                    formData={householdFormData}
                  />
                  <ToggleField
                    label="Does the household trade low-priced or restricted securities?"
                    value={
                      household.investor_associations?.low_priced_securities ?? false
                    }
                    isEditable={true}
                    isEditing={isEditing}
                    fieldPath="investor_associations.low_priced_securities"
                    onFieldChange={handleFieldChange}
                    formData={householdFormData}
                  />
                  {showGoldmanQualifiers(brokerageFlags) && (
                    <>
                      <ToggleField
                        label="Is the household affiliated with FINRA"
                        value={household.investor_associations?.finra_affiliation ?? false}
                        isEditable={true}
                        isEditing={isEditing}
                        fieldPath="investor_associations.finra_affiliation"
                        onFieldChange={handleFieldChange}
                        formData={householdFormData}
                      />
                      <ToggleField
                        label="Does the household trade marijuana-related securities?"
                        value={
                          household.investor_associations?.marijuana_securities ?? false
                        }
                        isEditable={true}
                        isEditing={isEditing}
                        fieldPath="investor_associations.marijuana_securities"
                        onFieldChange={handleFieldChange}
                        formData={householdFormData}
                      />
                      <ToggleField
                        label="Is anyone in the household a politically exposed person?"
                        value={
                          household.investor_associations?.politically_exposed_person ?? false
                        }
                        isEditable={true}
                        isEditing={isEditing}
                        fieldPath="investor_associations.politically_exposed_person"
                        onFieldChange={handleFieldChange}
                        formData={householdFormData}
                      />
                    </>
                  )}
                </div>
              </div>
            )}
            {householdInfoSubTab === "investmentExperience" &&
              hasVisibleExperienceFields && (
              <div
                data-section="investor_experience"
                className="border border-gray-200 rounded-lg p-4 bg-white"
              >
                {isEditing &&
                  householdSubTabHasUnsavedChanges(
                    householdFormData,
                    householdInfoSubTab,
                  ) && (
                  <UnsavedChangesBanner
                    className="mb-4"
                    message={advisorHouseholdUnsavedMessage(householdInfoSubTab)}
                  />
                )}
                <div className="grid grid-cols-2 gap-4">
                  {showExperienceField("bonds", brokerageFlags, household?.accounts) && (
                    <DataField
                      getFormValue={getFormValue}
                      label="Bonds"
                      value={
                        household.investor_experience?.bonds != null
                          ? household.investor_experience.bonds.toString()
                          : ""
                      }
                      isEditable={true}
                      type="number"
                      min={0}
                      max={85}
                      isEditing={isEditing}
                      fieldPath="investor_experience.bonds"
                      onFieldChange={handleFieldChange}
                      disableWheel={true}
                      error={validationErrors["investor_experience.bonds"]}
                      onFieldError={handleFieldError}
                    />
                  )}
                  {showExperienceField("stocks", brokerageFlags, household?.accounts) && (
                    <DataField
                      getFormValue={getFormValue}
                      label="Stocks"
                      value={
                        household.investor_experience?.stocks != null
                          ? household.investor_experience.stocks.toString()
                          : ""
                      }
                      isEditable={true}
                      type="number"
                      min={0}
                      max={85}
                      isEditing={isEditing}
                      fieldPath="investor_experience.stocks"
                      onFieldChange={handleFieldChange}
                      disableWheel={true}
                      error={validationErrors["investor_experience.stocks"]}
                      onFieldError={handleFieldError}
                    />
                  )}
                  {showExperienceField(
                    "alternative_investments",
                    brokerageFlags,
                  ) && (
                    <DataField
                      getFormValue={getFormValue}
                      label="Alternative Investments"
                      value={
                        household.investor_experience?.alternative_investments != null
                          ? household.investor_experience.alternative_investments.toString()
                          : ""
                      }
                      isEditable={true}
                      type="number"
                      min={0}
                      max={85}
                      isEditing={isEditing}
                      fieldPath="investor_experience.alternative_investments"
                      onFieldChange={handleFieldChange}
                      disableWheel={true}
                      error={
                        validationErrors[
                          "investor_experience.alternative_investments"
                        ]
                      }
                      onFieldError={handleFieldError}
                    />
                  )}
                  {showExperienceField(
                    "variable_annuities",
                    brokerageFlags,
                  ) && (
                    <DataField
                      getFormValue={getFormValue}
                      label="Variable Annuities"
                      value={
                        household.investor_experience?.variable_annuities != null
                          ? household.investor_experience.variable_annuities.toString()
                          : ""
                      }
                      isEditable={true}
                      type="number"
                      min={0}
                      max={85}
                      isEditing={isEditing}
                      fieldPath="investor_experience.variable_annuities"
                      onFieldChange={handleFieldChange}
                      disableWheel={true}
                      error={
                        validationErrors[
                          "investor_experience.variable_annuities"
                        ]
                      }
                      onFieldError={handleFieldError}
                    />
                  )}
                  {showExperienceField("mutual_funds", brokerageFlags) && (
                    <DataField
                      getFormValue={getFormValue}
                      label="Mutual Funds"
                      value={
                        household.investor_experience?.mutual_funds != null
                          ? household.investor_experience.mutual_funds.toString()
                          : ""
                      }
                      isEditable={true}
                      type="number"
                      min={0}
                      max={85}
                      isEditing={isEditing}
                      fieldPath="investor_experience.mutual_funds"
                      onFieldChange={handleFieldChange}
                      disableWheel={true}
                      error={
                        validationErrors["investor_experience.mutual_funds"]
                      }
                      onFieldError={handleFieldError}
                    />
                  )}
                  {showExperienceField("options", brokerageFlags, household?.accounts) && (
                    <DataField
                      getFormValue={getFormValue}
                      label="Options"
                      value={
                        household.investor_experience?.options != null
                          ? household.investor_experience.options.toString()
                          : ""
                      }
                      isEditable={true}
                      type="number"
                      min={0}
                      max={85}
                      isEditing={isEditing}
                      fieldPath="investor_experience.options"
                      onFieldChange={handleFieldChange}
                      disableWheel={true}
                      error={validationErrors["investor_experience.options"]}
                      onFieldError={handleFieldError}
                    />
                  )}
                  {showExperienceField("partnerships", brokerageFlags) && (
                    <DataField
                      getFormValue={getFormValue}
                      label="Partnerships"
                      value={
                        household.investor_experience?.partnerships != null
                          ? household.investor_experience.partnerships.toString()
                          : ""
                      }
                      isEditable={true}
                      type="number"
                      min={0}
                      max={85}
                      isEditing={isEditing}
                      fieldPath="investor_experience.partnerships"
                      onFieldChange={handleFieldChange}
                      disableWheel={true}
                      error={
                        validationErrors["investor_experience.partnerships"]
                      }
                      onFieldError={handleFieldError}
                    />
                  )}
                </div>
              </div>
            )}
            {householdInfoSubTab === "feeStructure" && (
              <div
                data-section="fee-structure"
                className="border border-gray-200 rounded-lg p-4 bg-white"
              >
                {isEditing &&
                  householdSubTabHasUnsavedChanges(
                    householdFormData,
                    householdInfoSubTab,
                  ) && (
                    <UnsavedChangesBanner
                      className="mb-4"
                      message={advisorHouseholdUnsavedMessage(
                        householdInfoSubTab,
                      )}
                    />
                  )}
                <div className="border border-emerald-100 rounded-lg p-4 bg-gradient-to-br from-emerald-50/60 to-white">
                  <h3 className="text-base font-bold text-gray-800 mb-4">
                    Fee Structure
                  </h3>
                  {isEditing ? (
                    <div className="space-y-4">
                      <div className="flex flex-wrap items-center gap-4">
                        <span className="text-sm font-medium text-gray-600">
                          Is the fee structure standard?
                        </span>
                        <div className="flex rounded-xl border border-gray-200 overflow-hidden bg-gray-50 p-0.5 gap-0.5">
                          {[
                            { label: "Yes", val: true },
                            { label: "No", val: false },
                          ].map(({ label, val }) => (
                            <button
                              key={label}
                              type="button"
                              onClick={() => applyFeeStandardToggle(val)}
                              className={`px-6 py-1.5 text-sm font-semibold rounded-lg transition-all ${
                                feeStructureIsStandardEffective === val
                                  ? "bg-emerald-600 text-white shadow-sm"
                                  : "text-gray-500 hover:text-gray-700"
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                      {!feeStructureIsStandardEffective && (
                        <div className="flex items-center gap-3">
                          <div className="flex flex-col gap-1">
                            <label
                              htmlFor="household-custom-fee-pct"
                              className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest"
                            >
                              Custom Fee
                            </label>
                            <div className="relative w-40">
                              <input
                                id="household-custom-fee-pct"
                                type="number"
                                step="0.01"
                                min={0}
                                max={100}
                                value={(() => {
                                  const v = getFormValue(
                                    "custom_fee_percentage",
                                    household?.custom_fee_percentage,
                                  );
                                  if (
                                    v === null ||
                                    v === undefined ||
                                    v === ""
                                  ) {
                                    return "";
                                  }
                                  return formatNumericInput(String(v), true);
                                })()}
                                onKeyDown={(e) => {
                                  if (["-", "+", "e", "E"].includes(e.key)) {
                                    e.preventDefault();
                                  }
                                }}
                                onChange={(e) => {
                                  let val = formatNumericInput(e.target.value, true);
                                  if (val.includes(".")) {
                                    const [intPart, decPart] = val.split(".");
                                    val = intPart + "." + decPart.slice(0, 2);
                                  }
                                  const n = parseFloat(val);
                                  if (!isNaN(n) && n > 100) {
                                    val = "100";
                                  }
                                  handleFieldChange("custom_fee_percentage", val);
                                }}
                                className="w-full pl-3 pr-8 py-2.5 rounded-xl border border-gray-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                placeholder="0.00"
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-bold">
                                %
                              </span>
                            </div>
                            {validationErrors.custom_fee_percentage && (
                              <p className="text-[11px] text-red-600 mt-1 font-medium">
                                {validationErrors.custom_fee_percentage}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-500 mb-0.5">
                          Is the fee structure standard?
                        </p>
                        <p className="text-sm font-medium text-gray-900">
                          {feeStructureIsStandardEffective
                            ? "Yes — standard fee schedule"
                            : "No — custom fee schedule"}
                        </p>
                      </div>
                      {!feeStructureIsStandardEffective && (
                        <div>
                          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">
                            Custom Fee
                          </p>
                          <p className="text-sm text-gray-900 mt-1">
                            {(() => {
                              const v = getFormValue(
                                "custom_fee_percentage",
                                household?.custom_fee_percentage,
                              );
                              if (
                                v === null ||
                                v === undefined ||
                                v === ""
                              ) {
                                return "—";
                              }
                              const n = parseFloat(v);
                              if (isNaN(n)) return `${v}%`;
                              return `${n.toFixed(2)}%`;
                            })()}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "members" && (
          <div className="space-y-4">
            {/* Sub-tabs: Account Owners | Additional Members + actions */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 pb-2">
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => setMembersSubTab("owners")}
                  className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                    membersSubTab === "owners"
                      ? "bg-emerald-100 text-emerald-800 border-b-2 border-emerald-600"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  Account Owners ({getOwners().length})
                </button>
                <button
                  type="button"
                  onClick={() => setMembersSubTab("additionalMembers")}
                  className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                    membersSubTab === "additionalMembers"
                      ? "bg-emerald-100 text-emerald-800 border-b-2 border-emerald-600"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  Additional Members ({getAdditionalMembers().length}) -
                  Beneficiaries/Trusted Contacts/Minors
                </button>
              </div>
              <div className="flex gap-2 shrink-0">
                {isEditingMembers ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditingMembers(false);
                        setValidationErrors({});
                        setMembersFormData({});
                      }}
                      className="btn-secondary text-xs px-3 py-2 flex items-center gap-1.5"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveMembers}
                      disabled={
                        savingMembers ||
                        Object.keys(validationErrors).length > 0
                      }
                      className="btn-primary text-xs px-3 py-2 flex items-center gap-1.5 disabled:opacity-50"
                    >
                      {savingMembers ? (
                        <>
                          <svg
                            className="animate-spin w-3.5 h-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Saving...
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          Save
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowAddMemberModal(true)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs px-3 py-2 flex items-center gap-1.5 transition-colors"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Add Member
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsEditingMembers(true)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs px-3 py-2 flex items-center gap-1.5 transition-colors"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      Edit
                    </button>
                  </>
                )}
              </div>
            </div>

            {membersSubTab === "owners" && (
              <>
                {getOwners().length > 0 ? (
                  <>
                    <div className="flex gap-2 flex-wrap border-b border-gray-200 pb-2">
                      {getOwners().map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => setSelectedOwnerId(m.id)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            selectedOwnerId === m.id
                              ? "bg-blue-100 text-blue-800 border border-blue-300"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                        >
                          {m.personal_info?.first_name}{" "}
                          {m.personal_info?.last_name}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2 border-b border-gray-200 pb-2">
                      {[
                        { id: "personal", label: "Personal Detail" },
                        { id: "contact", label: "Contact Details" },
                        { id: "employment", label: "Employment Details" },
                        ...(showIdInfo(brokerageFlags)
                          ? [{ id: "id", label: "ID Details" }]
                          : []),
                      ].map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setSelectedOwnerSection(s.id)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            selectedOwnerSection === s.id
                              ? "bg-emerald-100 text-emerald-800"
                              : "text-gray-600 hover:bg-gray-100"
                          }`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                    {(() => {
                      const member =
                        getOwners().find((m) => m.id === selectedOwnerId) ||
                        getOwners()[0];
                      if (!member)
                        return (
                          <div className="text-center py-8 text-gray-600">
                            No owner selected
                          </div>
                        );
                      return (
                        <div
                          key={member.id}
                          data-member-id={member.id}
                          className="border border-gray-200 rounded-lg p-4 bg-white"
                        >
                          <div className="flex justify-between items-center mb-4">
                            <span className="font-medium text-gray-900">
                              {member.personal_info?.first_name}{" "}
                              {member.personal_info?.last_name}
                            </span>
                          </div>
                          {isEditingMembers &&
                            memberSectionHasUnsavedChanges(
                              membersFormData,
                              member.id,
                              selectedOwnerSection,
                            ) && (
                            <UnsavedChangesBanner
                              className="mb-4"
                              message={advisorMembersUnsavedMessage(
                                membersSubTab,
                                selectedOwnerSection,
                              )}
                            />
                          )}
                          {selectedOwnerSection === "personal" && (
                            <div className="grid grid-cols-2 gap-4">
                              <DataField
                                getFormValue={getMemberFormValue}
                                label="Title"
                                value={member.personal_info?.title}
                                isEditable={true}
                                isEditing={isEditingMembers}
                                fieldPath={`${member.id}.personal_info.title`}
                                onFieldChange={handleMemberFieldChange}
                                options={TITLE_OPTIONS}
                              />
                              <DataField
                                getFormValue={getMemberFormValue}
                                label="Gender"
                                value={member.personal_info?.gender}
                                isEditable={true}
                                isEditing={isEditingMembers}
                                fieldPath={`${member.id}.personal_info.gender`}
                                onFieldChange={handleMemberFieldChange}
                                options={GENDER_OPTIONS}
                              />
                              <DataField
                                getFormValue={getMemberFormValue}
                                label="First Name"
                                value={member.personal_info?.first_name}
                                isEditable={true}
                                isEditing={isEditingMembers}
                                fieldPath={`${member.id}.personal_info.first_name`}
                                onFieldChange={handleMemberFieldChange}
                              />
                              <DataField
                                getFormValue={getMemberFormValue}
                                label="Middle Name"
                                value={member.personal_info?.middle_name}
                                isEditable={true}
                                isEditing={isEditingMembers}
                                fieldPath={`${member.id}.personal_info.middle_name`}
                                onFieldChange={handleMemberFieldChange}
                              />
                              <DataField
                                getFormValue={getMemberFormValue}
                                label="Last Name"
                                value={member.personal_info?.last_name}
                                isEditable={true}
                                isEditing={isEditingMembers}
                                fieldPath={`${member.id}.personal_info.last_name`}
                                onFieldChange={handleMemberFieldChange}
                              />
                              <DataField
                                getFormValue={getMemberFormValue}
                                label="Suffix"
                                value={member.personal_info?.suffix}
                                isEditable={true}
                                isEditing={isEditingMembers}
                                fieldPath={`${member.id}.personal_info.suffix`}
                                onFieldChange={handleMemberFieldChange}
                                options={SUFFIX_OPTIONS}
                              />
                              {showMaritalStatus(brokerageFlags) && (
                              <DataField
                                getFormValue={getMemberFormValue}
                                label="Marital Status"
                                value={member.personal_info?.marital_status}
                                isEditable={true}
                                isEditing={isEditingMembers}
                                fieldPath={`${member.id}.personal_info.marital_status`}
                                onFieldChange={handleMemberFieldChange}
                                options={MARITAL_STATUS_OPTIONS}
                              />
                              )}
                                {!member.is_primary && (
                                 <DataField
                                   getFormValue={getMemberFormValue}
                                   label={`RELATED TO — RELATIONSHIP TO ${primaryName.toUpperCase()}`}
                                   value={member.personal_info?.relationship}
                                   isEditable={true}
                                   isEditing={isEditingMembers}
                                   fieldPath={`${member.id}.personal_info.relationship`}
                                   onFieldChange={handleMemberFieldChange}
                                   options={RELATIONSHIP_OPTIONS}
                                 />
                               )}
                              <DataField
                                getFormValue={getMemberFormValue}
                                label="SSN/TIN"
                                value={member.personal_info?.ssn_or_tin}
                                isEditable={true}
                                isEditing={isEditingMembers}
                                type="ssn"
                                fieldPath={`${member.id}.personal_info.ssn_or_tin`}
                                onFieldChange={handleMemberFieldChange}
                                error={validationErrors[`${member.id}.personal_info.ssn_or_tin`]}
                                onFieldError={handleFieldError}
                              />
                              <DataField
                                getFormValue={getMemberFormValue}
                                label="Date of Birth"
                                value={formatDate(member.personal_info?.dob)}
                                dateValue={member.personal_info?.dob}
                                isEditable={true}
                                type="date"
                                isEditing={isEditingMembers}
                                fieldPath={`${member.id}.personal_info.dob`}
                                onFieldChange={handleMemberFieldChange}
                                error={
                                  validationErrors[
                                    `${member.id}.personal_info.dob`
                                  ]
                                }
                                onFieldError={handleFieldError}
                                dateConstraint={
                                  member.is_primary &&
                                  !isCustodialMinorOwner(household?.accounts, member.id)
                                    ? "dob_primary"
                                    : "dob_non_primary"
                                }
                              />
                              <DataField
                                getFormValue={getMemberFormValue}
                                label="Email"
                                value={member.personal_info?.email}
                                isEditable={true}
                                type="email"
                                isEditing={isEditingMembers}
                                fieldPath={`${member.id}.personal_info.email`}
                                onFieldChange={handleMemberFieldChange}
                              />
                              {isOwner(member.id) && (
                                <DataField
                                  getFormValue={getMemberFormValue}
                                  label="Username"
                                  value={member.personal_info?.username}
                                  isEditable={true}
                                  isEditing={isEditingMembers}
                                  fieldPath={`${member.id}.personal_info.username`}
                                  onFieldChange={handleMemberFieldChange}
                                />
                              )}
                              <DataField
                                getFormValue={getMemberFormValue}
                                label="Country of Citizenship"
                                value={
                                  member.personal_info?.country_of_citizenship
                                }
                                isEditable={true}
                                isEditing={isEditingMembers}
                                options={COUNTRY_OPTIONS}
                                fieldPath={`${member.id}.personal_info.country_of_citizenship`}
                                onFieldChange={handleMemberFieldChange}
                              />
                               {/* Deceased Status */}
                               <div className="col-span-2 grid grid-cols-2 gap-4 items-center py-2 border-t border-gray-100 mt-1">
                                 <div className="flex items-center gap-3">
                                 {isEditingMembers ? (
                                   <label className="flex items-center gap-2 cursor-pointer select-none">
                                     <input
                                       type="checkbox"
                                       checked={!!getMemberFormValue(`${member.id}.personal_info.is_deceased`, member.personal_info?.is_deceased)}
                                       onChange={(e) => handleMemberFieldChange(`${member.id}.personal_info.is_deceased`, e.target.checked)}
                                       className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                                     />
                                     <span className="text-sm font-medium text-gray-700">Deceased</span>
                                   </label>
                                 ) : (
                                   <div className="flex items-center gap-2">
                                     <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Deceased</span>
                                     <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                       member.personal_info?.is_deceased
                                         ? "bg-red-100 text-red-800"
                                         : "bg-gray-100 text-gray-600"
                                     }`}>
                                       {member.personal_info?.is_deceased ? "Yes" : "No"}
                                     </span>
                                   </div>
                                 )}
                                 </div>
                                 {(getMemberFormValue(`${member.id}.personal_info.is_deceased`, member.personal_info?.is_deceased)) && (
                                   <DataField
                                     getFormValue={getMemberFormValue}
                                     label="Date of Death"
                                     value={formatDate(member.personal_info?.date_of_death)}
                                     dateValue={member.personal_info?.date_of_death}
                                     isEditable={true}
                                     type="date"
                                     isEditing={isEditingMembers}
                                     fieldPath={`${member.id}.personal_info.date_of_death`}
                                     onFieldChange={handleMemberFieldChange}
                                   />
                                 )}
                               </div>
                            </div>
                          )}

                          {selectedOwnerSection === "contact" && (
                            <div className="space-y-4">
                              <div>
                                <h4 className="font-medium text-gray-900 mb-2">
                                  Home Address
                                </h4>
                                {isEditingMembers && (
                                  <div className="pl-4 mb-3">
                                    <ExistingAddressPicker
                                      household={household}
                                      excludeMemberId={member.id}
                                      getField={(k) =>
                                        getMemberFormValue(
                                          `${member.id}.personal_info.address.home.${k}`,
                                          getAddressByType(member.personal_info?.address, "home")?.[k],
                                        )
                                      }
                                      setField={(k, v) =>
                                        handleMemberFieldChange(
                                          `${member.id}.personal_info.address.home.${k}`,
                                          v,
                                        )
                                      }
                                      onAfterSelect={() => setAddressPickerKey((k) => k + 1)}
                                    />
                                  </div>
                                )}
                                <div
                                  className="grid grid-cols-2 gap-4 pl-4"
                                  key={`owner-home-${member.id}-${addressPickerKey}`}
                                >
                                  {(() => {
                                    const homeAddress = getAddressByType(
                                      member.personal_info?.address,
                                      "home",
                                    );
                                    return (
                                      <>
                                        <DataField
                                          getFormValue={getMemberFormValue}
                                          label="Address Line 1"
                                          value={homeAddress?.address_line_1}
                                          commitOnChange={true}
                                          isEditable={true}
                                          isEditing={isEditingMembers}
                                          fieldPath={`${member.id}.personal_info.address.home.address_line_1`}
                                          onFieldChange={
                                            handleMemberFieldChange
                                          }
                                        />
                                        <DataField
                                          getFormValue={getMemberFormValue}
                                          label="Address Line 2"
                                          value={homeAddress?.address_line_2}
                                          commitOnChange={true}
                                          isEditable={true}
                                          isEditing={isEditingMembers}
                                          fieldPath={`${member.id}.personal_info.address.home.address_line_2`}
                                          onFieldChange={
                                            handleMemberFieldChange
                                          }
                                        />
                                        <div className="col-span-2">
                                          <StateCitySelect
                                            key={`owner-addr-${member.id}-${addressPickerKey}`}
                                            countryValue={getMemberFormValue(`${member.id}.personal_info.address.home.country`, homeAddress?.country || "USA")}
                                            stateValue={getMemberFormValue(`${member.id}.personal_info.address.home.province`, homeAddress?.province)}
                                            cityValue={getMemberFormValue(`${member.id}.personal_info.address.home.city`, homeAddress?.city)}
                                            onCountryChange={(val) => handleMemberFieldChange(`${member.id}.personal_info.address.home.country`, val)}
                                            onStateChange={(val) => handleMemberFieldChange(`${member.id}.personal_info.address.home.province`, val)}
                                            onCityChange={(val) => handleMemberFieldChange(`${member.id}.personal_info.address.home.city`, val)}
                                            isEditing={isEditingMembers}
                                            stateLabel="State"
                                            cityLabel="City"
                                            zipValue={getMemberFormValue(`${member.id}.personal_info.address.home.postal_code`, homeAddress?.postal_code)}
                                            onZipChange={(val) => handleMemberFieldChange(`${member.id}.personal_info.address.home.postal_code`, val)}
                                            zipLabel="ZIP Code"
                                            zipRestrictUsFive={true}
                                            zipError={validationErrors[`${member.id}.personal_info.address.home.postal_code`]}
                                            onZipValidationError={(err) => handleFieldError(`${member.id}.personal_info.address.home.postal_code`, err)}
                                          />
                                        </div>
                                        <DataField
                                          getFormValue={getMemberFormValue}
                                          label="Phone"
                                          value={homeAddress?.phone}
                                          commitOnChange={true}
                                          isEditable={true}
                                          type="tel"
                                          isEditing={isEditingMembers}
                                          fieldPath={`${member.id}.personal_info.address.home.phone`}
                                          onFieldChange={
                                            handleMemberFieldChange
                                          }
                                        />
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>
                              {(() => {
                                const mailingAddress = getAddressByType(
                                  member.personal_info?.address,
                                  "mailing",
                                );
                                if (!mailingAddress) return null;
                                return (
                                  <div>
                                    <h4 className="font-medium text-gray-900 mb-2">
                                      Mailing Address
                                    </h4>
                                    <div className="grid grid-cols-2 gap-4 pl-4">
                                      <DataField
                                        getFormValue={getMemberFormValue}
                                        label="Address Line 1"
                                        value={mailingAddress?.address_line_1}
                                        isEditable={true}
                                        isEditing={isEditingMembers}
                                        fieldPath={`${member.id}.personal_info.address.mailing.address_line_1`}
                                        onFieldChange={handleMemberFieldChange}
                                      />
                                      <DataField
                                        getFormValue={getMemberFormValue}
                                        label="Address Line 2"
                                        value={mailingAddress?.address_line_2}
                                        isEditable={true}
                                        isEditing={isEditingMembers}
                                        fieldPath={`${member.id}.personal_info.address.mailing.address_line_2`}
                                        onFieldChange={handleMemberFieldChange}
                                      />
                                      <div className="col-span-2">
                                        <StateCitySelect
                                          key={`owner-mailing-${member.id}-${addressPickerKey}`}
                                          countryValue={getMemberFormValue(`${member.id}.personal_info.address.mailing.country`, mailingAddress?.country || "USA")}
                                          stateValue={getMemberFormValue(`${member.id}.personal_info.address.mailing.province`, mailingAddress?.province)}
                                          cityValue={getMemberFormValue(`${member.id}.personal_info.address.mailing.city`, mailingAddress?.city)}
                                          onCountryChange={(val) => handleMemberFieldChange(`${member.id}.personal_info.address.mailing.country`, val)}
                                          onStateChange={(val) => handleMemberFieldChange(`${member.id}.personal_info.address.mailing.province`, val)}
                                          onCityChange={(val) => handleMemberFieldChange(`${member.id}.personal_info.address.mailing.city`, val)}
                                          isEditing={isEditingMembers}
                                          stateLabel="State"
                                          cityLabel="City"
                                          zipValue={getMemberFormValue(`${member.id}.personal_info.address.mailing.postal_code`, mailingAddress?.postal_code)}
                                          onZipChange={(val) => handleMemberFieldChange(`${member.id}.personal_info.address.mailing.postal_code`, val)}
                                          zipLabel="ZIP Code"
                                          zipRestrictUsFive={true}
                                          zipError={validationErrors[`${member.id}.personal_info.address.mailing.postal_code`]}
                                          onZipValidationError={(err) => handleFieldError(`${member.id}.personal_info.address.mailing.postal_code`, err)}
                                        />
                                      </div>
                                      <DataField
                                        getFormValue={getMemberFormValue}
                                        label="Phone"
                                        value={mailingAddress?.phone}
                                        isEditable={true}
                                        type="tel"
                                        isEditing={isEditingMembers}
                                        fieldPath={`${member.id}.personal_info.address.mailing.phone`}
                                        onFieldChange={handleMemberFieldChange}
                                      />
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                          {selectedOwnerSection === "employment" && (
                            <div className="space-y-4">
                              {(() => {
                                const effEmploymentStatus = getMemberFormValue(
                                  `${member.id}.employment_info.status`,
                                  member.employment_info?.status || "",
                                );
                                const showEmploymentWorkDetails =
                                  !shouldDisableEmploymentDetails(
                                    effEmploymentStatus,
                                  );
                                return (
                                  <>
                                    <div className="grid grid-cols-2 gap-4">
                                      <DataField
                                        getFormValue={getMemberFormValue}
                                        label="Employment Status"
                                        value={member.employment_info?.status}
                                        isEditable={true}
                                        isEditing={isEditingMembers}
                                        fieldPath={`${member.id}.employment_info.status`}
                                        onFieldChange={handleMemberFieldChange}
                                        options={EMPLOYMENT_STATUS_OPTIONS}
                                      />
                                      {showEmploymentWorkDetails && (
                                        <>
                                          <DataField
                                            getFormValue={getMemberFormValue}
                                            label="Occupation"
                                            value={
                                              member.employment_info?.occupation
                                            }
                                            isEditable={true}
                                            isEditing={isEditingMembers}
                                            fieldPath={`${member.id}.employment_info.occupation`}
                                            onFieldChange={
                                              handleMemberFieldChange
                                            }
                                          />
                                          {/* industry removed from UI */}
                                          {/* <DataField
                                            getFormValue={getMemberFormValue}
                                            label="Industry"
                                            value={
                                              member.employment_info?.industry
                                            }
                                            isEditable={true}
                                            isEditing={isEditingMembers}
                                            fieldPath={`${member.id}.employment_info.industry`}
                                            onFieldChange={
                                              handleMemberFieldChange
                                            }
                                          /> */}
                                          <DataField
                                            getFormValue={getMemberFormValue}
                                            label="Employer"
                                            value={
                                              member.employment_info?.employer
                                            }
                                            isEditable={true}
                                            isEditing={isEditingMembers}
                                            fieldPath={`${member.id}.employment_info.employer`}
                                            onFieldChange={
                                              handleMemberFieldChange
                                            }
                                          />
                                          <DataField
                                            getFormValue={getMemberFormValue}
                                            label="Job Title"
                                            value={
                                              member.employment_info?.title
                                            }
                                            isEditable={true}
                                            isEditing={isEditingMembers}
                                            fieldPath={`${member.id}.employment_info.title`}
                                            onFieldChange={
                                              handleMemberFieldChange
                                            }
                                          />
                                          <DataField
                                            getFormValue={getMemberFormValue}
                                            label="Employer TIN/EIN"
                                            value={
                                              member.employment_info?.tin
                                            }
                                            isEditable={true}
                                            isEditing={isEditingMembers}
                                            type="ssn"
                                            ssnLengthOnly
                                            fieldPath={`${member.id}.employment_info.tin`}
                                            onFieldChange={
                                              handleMemberFieldChange
                                            }
                                            error={
                                              validationErrors[
                                                `${member.id}.employment_info.tin`
                                              ]
                                            }
                                            onFieldError={handleFieldError}
                                          />
                                        </>
                                      )}
                                    </div>
                                    {showEmploymentWorkDetails && (
                                      <div>
                                        <h4 className="font-medium text-gray-900 mb-2">
                                          Work Address
                                        </h4>
                                        <div className="grid grid-cols-2 gap-4 pl-4">
                                          <DataField
                                            getFormValue={getMemberFormValue}
                                            label="Address Line 1"
                                            value={
                                              member.employment_info?.address
                                                ?.address_line_1
                                            }
                                            isEditable={true}
                                            isEditing={isEditingMembers}
                                            fieldPath={`${member.id}.employment_info.address.address_line_1`}
                                            onFieldChange={
                                              handleMemberFieldChange
                                            }
                                          />
                                          <DataField
                                            getFormValue={getMemberFormValue}
                                            label="Address Line 2"
                                            value={
                                              member.employment_info?.address
                                                ?.address_line_2
                                            }
                                            isEditable={true}
                                            isEditing={isEditingMembers}
                                            fieldPath={`${member.id}.employment_info.address.address_line_2`}
                                            onFieldChange={
                                              handleMemberFieldChange
                                            }
                                          />
                                          <div className="col-span-2">
                                            <StateCitySelect
                                              countryValue={getMemberFormValue(
                                                `${member.id}.employment_info.address.country`,
                                                member.employment_info?.address
                                                  ?.country || "USA",
                                              )}
                                              stateValue={getMemberFormValue(
                                                `${member.id}.employment_info.address.province`,
                                                member.employment_info?.address
                                                  ?.province,
                                              )}
                                              cityValue={getMemberFormValue(
                                                `${member.id}.employment_info.address.city`,
                                                member.employment_info?.address
                                                  ?.city,
                                              )}
                                              onCountryChange={(val) =>
                                                handleMemberFieldChange(
                                                  `${member.id}.employment_info.address.country`,
                                                  val,
                                                )
                                              }
                                              onStateChange={(val) =>
                                                handleMemberFieldChange(
                                                  `${member.id}.employment_info.address.province`,
                                                  val,
                                                )
                                              }
                                              onCityChange={(val) =>
                                                handleMemberFieldChange(
                                                  `${member.id}.employment_info.address.city`,
                                                  val,
                                                )
                                              }
                                              isEditing={isEditingMembers}
                                              stateLabel="State"
                                              cityLabel="City"
                                              zipValue={getMemberFormValue(
                                                `${member.id}.employment_info.address.postal_code`,
                                                member.employment_info?.address
                                                  ?.postal_code,
                                              )}
                                              onZipChange={(val) =>
                                                handleMemberFieldChange(
                                                  `${member.id}.employment_info.address.postal_code`,
                                                  val,
                                                )
                                              }
                                              zipLabel="ZIP Code"
                                              zipRestrictUsFive={true}
                                              zipError={
                                                validationErrors[
                                                  `${member.id}.employment_info.address.postal_code`
                                                ]
                                              }
                                              onZipValidationError={(err) =>
                                                handleFieldError(
                                                  `${member.id}.employment_info.address.postal_code`,
                                                  err,
                                                )
                                              }
                                            />
                                          </div>
                                          <DataField
                                            getFormValue={getMemberFormValue}
                                            label="Phone"
                                            value={
                                              member.employment_info?.address
                                                ?.phone
                                            }
                                            isEditable={true}
                                            type="tel"
                                            isEditing={isEditingMembers}
                                            fieldPath={`${member.id}.employment_info.address.phone`}
                                            onFieldChange={
                                              handleMemberFieldChange
                                            }
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          )}
                          {showIdInfo(brokerageFlags) &&
                            selectedOwnerSection === "id" && (
                            <div className="space-y-4">
                              {member.id_info && member.id_info.length > 0 ? (
                                member.id_info.map((id, index) => (
                                  <div
                                    key={index}
                                    className="border border-gray-200 rounded-lg p-4"
                                  >
                                    <h5 className="font-medium text-gray-900 mb-2">
                                      ID #{index + 1}
                                    </h5>
                                    <div className="grid grid-cols-2 gap-4">
                                      <DataField
                                        getFormValue={getMemberFormValue}
                                        label="ID Type"
                                        value={id.type}
                                        isEditable={true}
                                        isEditing={isEditingMembers}
                                        fieldPath={`${member.id}.id_info.${index}.type`}
                                        onFieldChange={handleMemberFieldChange}
                                      />
                                      <DataField
                                        getFormValue={getMemberFormValue}
                                        label="Issue State"
                                        value={id.issue_state}
                                        isEditable={true}
                                        isEditing={isEditingMembers}
                                        fieldPath={`${member.id}.id_info.${index}.issue_state`}
                                        onFieldChange={handleMemberFieldChange}
                                        options={US_STATES.map(s => ({ label: `${s.name} (${s.abbreviation})`, value: s.abbreviation }))}
                                      />
                                      <DataField
                                        getFormValue={getMemberFormValue}
                                        label="ID Number"
                                        value={id.number}
                                        isEditable={true}
                                        isEditing={isEditingMembers}
                                        fieldPath={`${member.id}.id_info.${index}.number`}
                                        onFieldChange={handleMemberFieldChange}
                                      />
                                      <DataField
                                        getFormValue={getMemberFormValue}
                                        label="Issuance Date"
                                        value={formatDate(id.issuance_date)}
                                        dateValue={id.issuance_date}
                                        isEditable={true}
                                        type="date"
                                        isEditing={isEditingMembers}
                                        fieldPath={`${member.id}.id_info.${index}.issuance_date`}
                                        onFieldChange={handleMemberFieldChange}
                                      />
                                      <DataField
                                        getFormValue={getMemberFormValue}
                                        label="Expiration Date"
                                        value={formatDate(id.expiration_date)}
                                        dateValue={id.expiration_date}
                                        isEditable={true}
                                        type="date"
                                        isEditing={isEditingMembers}
                                        fieldPath={`${member.id}.id_info.${index}.expiration_date`}
                                        onFieldChange={handleMemberFieldChange}
                                      />
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="border border-gray-200 rounded-lg p-4">
                                  <h5 className="font-medium text-gray-900 mb-2">
                                    ID #1
                                  </h5>
                                  <div className="grid grid-cols-2 gap-4">
                                    <DataField
                                      getFormValue={getMemberFormValue}
                                      label="ID Type"
                                      value={null}
                                      isEditable={true}
                                      isEditing={isEditingMembers}
                                      fieldPath={`${member.id}.id_info.0.type`}
                                      onFieldChange={handleMemberFieldChange}
                                    />
                                    <DataField
                                      getFormValue={getMemberFormValue}
                                      label="Issue State"
                                      value={null}
                                      isEditable={true}
                                      isEditing={isEditingMembers}
                                      fieldPath={`${member.id}.id_info.0.issue_state`}
                                      onFieldChange={handleMemberFieldChange}
                                      options={US_STATES.map(s => ({ label: `${s.name} (${s.abbreviation})`, value: s.abbreviation }))}
                                    />
                                    <DataField
                                      getFormValue={getMemberFormValue}
                                      label="ID Number"
                                      value={null}
                                      isEditable={true}
                                      isEditing={isEditingMembers}
                                      fieldPath={`${member.id}.id_info.0.number`}
                                      onFieldChange={handleMemberFieldChange}
                                    />
                                    <DataField
                                      getFormValue={getMemberFormValue}
                                      label="Issuance Date"
                                      value={null}
                                      isEditable={true}
                                      type="date"
                                      isEditing={isEditingMembers}
                                      fieldPath={`${member.id}.id_info.0.issuance_date`}
                                      onFieldChange={handleMemberFieldChange}
                                    />
                                    <DataField
                                      getFormValue={getMemberFormValue}
                                      label="Expiration Date"
                                      value={null}
                                      isEditable={true}
                                      type="date"
                                      isEditing={isEditingMembers}
                                      fieldPath={`${member.id}.id_info.0.expiration_date`}
                                      onFieldChange={handleMemberFieldChange}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600">No owners found</p>
                  </div>
                )}
              </>
            )}

            {membersSubTab === "additionalMembers" && (
              <>
                {getAdditionalMembers().length > 0 ? (
                  <>
                    <div className="flex gap-2 flex-wrap border-b border-gray-200 pb-2">
                      {getAdditionalMembers().map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => setSelectedAddnMemberId(m.id)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            selectedAddnMemberId === m.id
                              ? "bg-green-100 text-green-800 border border-green-300"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                        >
                          {m.personal_info?.first_name}{" "}
                          {m.personal_info?.last_name}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2 border-b border-gray-200 pb-2">
                      {[
                        { id: "personal", label: "Personal Detail" },
                        { id: "contact", label: "Contact Details" },
                        { id: "employment", label: "Employment Details" },
                        ...(showIdInfo(brokerageFlags)
                          ? [{ id: "id", label: "ID Details" }]
                          : []),
                      ].map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setSelectedAddnMemberSection(s.id)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            selectedAddnMemberSection === s.id
                              ? "bg-emerald-100 text-emerald-800"
                              : "text-gray-600 hover:bg-gray-100"
                          }`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                    {(() => {
                      const member =
                        getAdditionalMembers().find(
                          (m) => m.id === selectedAddnMemberId,
                        ) || getAdditionalMembers()[0];
                      if (!member)
                        return (
                          <div className="text-center py-8 text-gray-600">
                            No member selected
                          </div>
                        );
                      return (
                        <div
                          key={member.id}
                          data-member-id={member.id}
                          className="border border-gray-200 rounded-lg p-4 bg-white"
                        >
                          <div className="flex justify-between items-center mb-4">
                            <span className="font-medium text-gray-900">
                              {member.personal_info?.first_name}{" "}
                              {member.personal_info?.last_name}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleDeleteMember(member.id)}
                              disabled={
                                member.is_primary ||
                                isOwner(member.id) ||
                                deletingMemberId === member.id
                              }
                              className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={
                                member.is_primary
                                  ? "Cannot delete primary household member"
                                  : isOwner(member.id)
                                    ? "Cannot delete household member who is an owner of an account"
                                    : "Delete member"
                              }
                            >
                              {deletingMemberId === member.id ? (
                                <svg
                                  className="w-5 h-5 animate-spin"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                              ) : (
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              )}
                            </button>
                          </div>
                          {isEditingMembers &&
                            memberSectionHasUnsavedChanges(
                              membersFormData,
                              member.id,
                              selectedAddnMemberSection,
                            ) && (
                            <UnsavedChangesBanner
                              className="mb-4"
                              message={advisorMembersUnsavedMessage(
                                membersSubTab,
                                selectedAddnMemberSection,
                              )}
                            />
                          )}
                          {selectedAddnMemberSection === "personal" && (
                            <div className="grid grid-cols-2 gap-4">
                              <DataField
                                getFormValue={getMemberFormValue}
                                label="First Name"
                                value={member.personal_info?.first_name}
                                isEditable={true}
                                isEditing={isEditingMembers}
                                fieldPath={`${member.id}.personal_info.first_name`}
                                onFieldChange={handleMemberFieldChange}
                              />
                              <DataField
                                getFormValue={getMemberFormValue}
                                label="Middle Name"
                                value={member.personal_info?.middle_name}
                                isEditable={true}
                                isEditing={isEditingMembers}
                                fieldPath={`${member.id}.personal_info.middle_name`}
                                onFieldChange={handleMemberFieldChange}
                              />
                              <DataField
                                getFormValue={getMemberFormValue}
                                label="Last Name"
                                value={member.personal_info?.last_name}
                                isEditable={true}
                                isEditing={isEditingMembers}
                                fieldPath={`${member.id}.personal_info.last_name`}
                                onFieldChange={handleMemberFieldChange}
                              />
                              <DataField
                                getFormValue={getMemberFormValue}
                                label="Suffix"
                                value={member.personal_info?.suffix}
                                isEditable={true}
                                isEditing={isEditingMembers}
                                fieldPath={`${member.id}.personal_info.suffix`}
                                onFieldChange={handleMemberFieldChange}
                                options={SUFFIX_OPTIONS}
                              />
                              <DataField
                                getFormValue={getMemberFormValue}
                                label="Email"
                                value={member.personal_info?.email}
                                isEditable={true}
                                type="email"
                                isEditing={isEditingMembers}
                                fieldPath={`${member.id}.personal_info.email`}
                                onFieldChange={handleMemberFieldChange}
                              />
                              <DataField
                                getFormValue={getMemberFormValue}
                                label="Date of Birth"
                                value={formatDate(member.personal_info?.dob)}
                                dateValue={member.personal_info?.dob}
                                isEditable={true}
                                type="date"
                                isEditing={isEditingMembers}
                                fieldPath={`${member.id}.personal_info.dob`}
                                onFieldChange={handleMemberFieldChange}
                                error={
                                  validationErrors[
                                    `${member.id}.personal_info.dob`
                                  ]
                                }
                                onFieldError={handleFieldError}
                                dateConstraint={
                                  member.is_primary &&
                                  !isCustodialMinorOwner(household?.accounts, member.id)
                                    ? "dob_primary"
                                    : "dob_non_primary"
                                }
                              />
                              <DataField
                                getFormValue={getMemberFormValue}
                                label="SSN"
                                value={member.personal_info?.ssn_or_tin}
                                isEditable={true}
                                isEditing={isEditingMembers}
                                type="ssn"
                                fieldPath={`${member.id}.personal_info.ssn_or_tin`}
                                onFieldChange={handleMemberFieldChange}
                                error={validationErrors[`${member.id}.personal_info.ssn_or_tin`]}
                                onFieldError={handleFieldError}
                              />
                              {!member.is_primary && (
                                <DataField
                                  getFormValue={getMemberFormValue}
                                  label={`RELATED TO — RELATIONSHIP TO ${primaryName.toUpperCase()}`}
                                  value={member.personal_info?.relationship}
                                  isEditable={true}
                                  isEditing={isEditingMembers}
                                  fieldPath={`${member.id}.personal_info.relationship`}
                                  onFieldChange={handleMemberFieldChange}
                                  options={RELATIONSHIP_OPTIONS}
                                />
                              )}
                              {/* Deceased Status */}
                              <div className="col-span-2 grid grid-cols-2 gap-4 items-center py-2 border-t border-gray-100 mt-1">
                                <div className="flex items-center gap-3">
                                {isEditingMembers ? (
                                  <label className="flex items-center gap-2 cursor-pointer select-none">
                                    <input
                                      type="checkbox"
                                      checked={!!getMemberFormValue(`${member.id}.personal_info.is_deceased`, member.personal_info?.is_deceased)}
                                      onChange={(e) => handleMemberFieldChange(`${member.id}.personal_info.is_deceased`, e.target.checked)}
                                      className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                                    />
                                    <span className="text-sm font-medium text-gray-700">Deceased</span>
                                  </label>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Deceased</span>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                      member.personal_info?.is_deceased
                                        ? "bg-red-100 text-red-800"
                                        : "bg-gray-100 text-gray-600"
                                    }`}>
                                      {member.personal_info?.is_deceased ? "Yes" : "No"}
                                    </span>
                                  </div>
                                )}
                                </div>
                                {getMemberFormValue(`${member.id}.personal_info.is_deceased`, member.personal_info?.is_deceased) && (
                                  <DataField
                                    getFormValue={getMemberFormValue}
                                    label="Date of Death"
                                    value={formatDate(member.personal_info?.date_of_death)}
                                    dateValue={member.personal_info?.date_of_death}
                                    isEditable={true}
                                    type="date"
                                    isEditing={isEditingMembers}
                                    fieldPath={`${member.id}.personal_info.date_of_death`}
                                    onFieldChange={handleMemberFieldChange}
                                  />
                                )}
                              </div>
                            </div>
                          )}
                          {selectedAddnMemberSection === "contact" && (
                            <div className="space-y-4">
                              {isEditingMembers && (
                                <ExistingAddressPicker
                                  household={household}
                                  excludeMemberId={member.id}
                                  getField={(k) =>
                                    getMemberFormValue(
                                      `${member.id}.personal_info.address.home.${k}`,
                                      (member.personal_info?.address || []).find(
                                        (a) => (a?.type || "home") === "home",
                                      )?.[k],
                                    )
                                  }
                                  setField={(k, v) =>
                                    handleMemberFieldChange(
                                      `${member.id}.personal_info.address.home.${k}`,
                                      v,
                                    )
                                  }
                                  onAfterSelect={() => setAddressPickerKey((k) => k + 1)}
                                />
                              )}
                              {member.personal_info?.address &&
                              member.personal_info.address.length > 0 ? (
                                member.personal_info.address.map(
                                  (address, addressIndex) => (
                                    <div key={`${addressIndex}-${addressPickerKey}`}>
                                      <h4 className="font-medium text-gray-900 mb-2 capitalize">
                                        {address.type || "Address"} Address
                                      </h4>
                                      <div className="grid grid-cols-2 gap-4 pl-4">
                                        <DataField
                                          getFormValue={getMemberFormValue}
                                          label="Address Line 1"
                                          value={address?.address_line_1}
                                          commitOnChange={true}
                                          isEditable={true}
                                          isEditing={isEditingMembers}
                                          fieldPath={`${member.id}.personal_info.address.${address?.type || "home"}.address_line_1`}
                                          onFieldChange={
                                            handleMemberFieldChange
                                          }
                                        />
                                        <DataField
                                          getFormValue={getMemberFormValue}
                                          label="Address Line 2"
                                          value={address?.address_line_2}
                                          commitOnChange={true}
                                          isEditable={true}
                                          isEditing={isEditingMembers}
                                          fieldPath={`${member.id}.personal_info.address.${address?.type || "home"}.address_line_2`}
                                          onFieldChange={
                                            handleMemberFieldChange
                                          }
                                        />
                                        <div className="col-span-2">
                                          <StateCitySelect
                                            key={`addn-addr-${member.id}-${address?.type || "home"}-${addressPickerKey}`}
                                            countryValue={getMemberFormValue(`${member.id}.personal_info.address.${address?.type || "home"}.country`, address?.country || "USA")}
                                            stateValue={getMemberFormValue(`${member.id}.personal_info.address.${address?.type || "home"}.province`, address?.province)}
                                            cityValue={getMemberFormValue(`${member.id}.personal_info.address.${address?.type || "home"}.city`, address?.city)}
                                            onCountryChange={(val) => handleMemberFieldChange(`${member.id}.personal_info.address.${address?.type || "home"}.country`, val)}
                                            onStateChange={(val) => handleMemberFieldChange(`${member.id}.personal_info.address.${address?.type || "home"}.province`, val)}
                                            onCityChange={(val) => handleMemberFieldChange(`${member.id}.personal_info.address.${address?.type || "home"}.city`, val)}
                                            isEditing={isEditingMembers}
                                            stateLabel="State"
                                            cityLabel="City"
                                            zipValue={getMemberFormValue(`${member.id}.personal_info.address.${address?.type || "home"}.postal_code`, address?.postal_code)}
                                            onZipChange={(val) => handleMemberFieldChange(`${member.id}.personal_info.address.${address?.type || "home"}.postal_code`, val)}
                                            zipLabel="ZIP Code"
                                            zipRestrictUsFive={true}
                                            zipError={validationErrors[`${member.id}.personal_info.address.${address?.type || "home"}.postal_code`]}
                                            onZipValidationError={(err) => handleFieldError(`${member.id}.personal_info.address.${address?.type || "home"}.postal_code`, err)}
                                          />
                                        </div>
                                        <DataField
                                          getFormValue={getMemberFormValue}
                                          label="Phone"
                                          value={address?.phone}
                                          commitOnChange={true}
                                          isEditable={true}
                                          type="tel"
                                          isEditing={isEditingMembers}
                                          fieldPath={`${member.id}.personal_info.address.${address?.type || "home"}.phone`}
                                          onFieldChange={
                                            handleMemberFieldChange
                                          }
                                        />
                                      </div>
                                    </div>
                                  ),
                                )
                              ) : (
                                <div>
                                  <h4 className="font-medium text-gray-900 mb-2">
                                    Address
                                  </h4>
                                  <div className="grid grid-cols-2 gap-4 pl-4">
                                    <DataField
                                      getFormValue={getMemberFormValue}
                                      label="Address Line 1"
                                      value={null}
                                      isEditable={true}
                                      isEditing={isEditingMembers}
                                      fieldPath={`${member.id}.personal_info.address.home.address_line_1`}
                                      onFieldChange={handleMemberFieldChange}
                                    />
                                    <DataField
                                      getFormValue={getMemberFormValue}
                                      label="Address Line 2"
                                      value={null}
                                      isEditable={true}
                                      isEditing={isEditingMembers}
                                      fieldPath={`${member.id}.personal_info.address.home.address_line_2`}
                                      onFieldChange={handleMemberFieldChange}
                                    />
                                    <DataField
                                      getFormValue={getMemberFormValue}
                                      label="City"
                                      value={null}
                                      isEditable={true}
                                      isEditing={isEditingMembers}
                                      fieldPath={`${member.id}.personal_info.address.home.city`}
                                      onFieldChange={handleMemberFieldChange}
                                    />
                                    <DataField
                                      getFormValue={getMemberFormValue}
                                      label="State"
                                      value={null}
                                      isEditable={true}
                                      isEditing={isEditingMembers}
                                      fieldPath={`${member.id}.personal_info.address.home.province`}
                                      onFieldChange={handleMemberFieldChange}
                                    />
                                    <DataField
                                      getFormValue={getMemberFormValue}
                                      label="ZIP Code"
                                      value={null}
                                      isEditable={true}
                                      isEditing={isEditingMembers}
                                      fieldPath={`${member.id}.personal_info.address.home.postal_code`}
                                      onFieldChange={handleMemberFieldChange}
                                    />
                                    <DataField
                                      getFormValue={getMemberFormValue}
                                      label="Country of Citizenship"
                                      value={null}
                                      isEditable={true}
                                      isEditing={isEditingMembers}
                                      fieldPath={`${member.id}.personal_info.address.home.country`}
                                      onFieldChange={handleMemberFieldChange}
                                    />
                                    <DataField
                                      getFormValue={getMemberFormValue}
                                      label="Phone"
                                      value={null}
                                      isEditable={true}
                                      type="tel"
                                      isEditing={isEditingMembers}
                                      fieldPath={`${member.id}.personal_info.address.home.phone`}
                                      onFieldChange={handleMemberFieldChange}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          {selectedAddnMemberSection === "employment" && (
                            <div className="space-y-4">
                              {(() => {
                                const effAddnEmploymentStatus =
                                  getMemberFormValue(
                                    `${member.id}.employment_info.status`,
                                    member.employment_info?.status || "",
                                  );
                                const showAddnEmploymentWorkDetails =
                                  !shouldDisableEmploymentDetails(
                                    effAddnEmploymentStatus,
                                  );
                                return (
                                  <div className="grid grid-cols-2 gap-4">
                                    <DataField
                                      getFormValue={getMemberFormValue}
                                      label="Employment Status"
                                      value={member.employment_info?.status}
                                      isEditable={true}
                                      isEditing={isEditingMembers}
                                      fieldPath={`${member.id}.employment_info.status`}
                                      onFieldChange={handleMemberFieldChange}
                                      options={EMPLOYMENT_STATUS_OPTIONS}
                                    />
                                    {showAddnEmploymentWorkDetails && (
                                      <>
                                        <DataField
                                          getFormValue={getMemberFormValue}
                                          label="Occupation"
                                          value={
                                            member.employment_info?.occupation
                                          }
                                          isEditable={true}
                                          isEditing={isEditingMembers}
                                          fieldPath={`${member.id}.employment_info.occupation`}
                                          onFieldChange={handleMemberFieldChange}
                                        />
                                        {/* industry removed from UI */}
                                        {/* <DataField
                                          getFormValue={getMemberFormValue}
                                          label="Industry"
                                          value={
                                            member.employment_info?.industry
                                          }
                                          isEditable={true}
                                          isEditing={isEditingMembers}
                                          fieldPath={`${member.id}.employment_info.industry`}
                                          onFieldChange={handleMemberFieldChange}
                                        /> */}
                                        <DataField
                                          getFormValue={getMemberFormValue}
                                          label="Employer"
                                          value={
                                            member.employment_info?.employer
                                          }
                                          isEditable={true}
                                          isEditing={isEditingMembers}
                                          fieldPath={`${member.id}.employment_info.employer`}
                                          onFieldChange={handleMemberFieldChange}
                                        />
                                        <DataField
                                          getFormValue={getMemberFormValue}
                                          label="Job Title"
                                          value={
                                            member.employment_info?.title
                                          }
                                          isEditable={true}
                                          isEditing={isEditingMembers}
                                          fieldPath={`${member.id}.employment_info.title`}
                                          onFieldChange={handleMemberFieldChange}
                                        />
                                        <DataField
                                          getFormValue={getMemberFormValue}
                                          label="Employer TIN/EIN"
                                          value={
                                            member.employment_info?.tin
                                          }
                                          isEditable={true}
                                          isEditing={isEditingMembers}
                                          type="ssn"
                                          ssnLengthOnly
                                          fieldPath={`${member.id}.employment_info.tin`}
                                          onFieldChange={handleMemberFieldChange}
                                          error={
                                            validationErrors[
                                              `${member.id}.employment_info.tin`
                                            ]
                                          }
                                          onFieldError={handleFieldError}
                                        />
                                      </>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                          {showIdInfo(brokerageFlags) &&
                            selectedAddnMemberSection === "id" && (
                            <div className="space-y-4">
                              {member.id_info && member.id_info.length > 0 ? (
                                member.id_info.map((id, index) => (
                                  <div
                                    key={index}
                                    className="border border-gray-200 rounded-lg p-4"
                                  >
                                    <h5 className="font-medium text-gray-900 mb-2">
                                      ID #{index + 1}
                                    </h5>
                                    <div className="grid grid-cols-2 gap-4">
                                      <DataField
                                        getFormValue={getMemberFormValue}
                                        label="ID Type"
                                        value={id.type}
                                        isEditable={true}
                                        isEditing={isEditingMembers}
                                        fieldPath={`${member.id}.id_info.${index}.type`}
                                        onFieldChange={handleMemberFieldChange}
                                      />
                                      <DataField
                                        getFormValue={getMemberFormValue}
                                        label="Issue State"
                                        value={id.issue_state}
                                        isEditable={true}
                                        isEditing={isEditingMembers}
                                        fieldPath={`${member.id}.id_info.${index}.issue_state`}
                                        onFieldChange={handleMemberFieldChange}
                                      />
                                      <DataField
                                        getFormValue={getMemberFormValue}
                                        label="ID Number"
                                        value={id.number}
                                        isEditable={true}
                                        isEditing={isEditingMembers}
                                        fieldPath={`${member.id}.id_info.${index}.number`}
                                        onFieldChange={handleMemberFieldChange}
                                      />
                                      <DataField
                                        getFormValue={getMemberFormValue}
                                        label="Issuance Date"
                                        value={formatDate(id.issuance_date)}
                                        dateValue={id.issuance_date}
                                        isEditable={true}
                                        type="date"
                                        isEditing={isEditingMembers}
                                        fieldPath={`${member.id}.id_info.${index}.issuance_date`}
                                        onFieldChange={handleMemberFieldChange}
                                      />
                                      <DataField
                                        getFormValue={getMemberFormValue}
                                        label="Expiration Date"
                                        value={formatDate(id.expiration_date)}
                                        dateValue={id.expiration_date}
                                        isEditable={true}
                                        type="date"
                                        isEditing={isEditingMembers}
                                        fieldPath={`${member.id}.id_info.${index}.expiration_date`}
                                        onFieldChange={handleMemberFieldChange}
                                      />
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="border border-gray-200 rounded-lg p-4">
                                  <h5 className="font-medium text-gray-900 mb-2">
                                    ID #1
                                  </h5>
                                  <div className="grid grid-cols-2 gap-4">
                                    <DataField
                                      getFormValue={getMemberFormValue}
                                      label="ID Type"
                                      value={null}
                                      isEditable={true}
                                      isEditing={isEditingMembers}
                                      fieldPath={`${member.id}.id_info.0.type`}
                                      onFieldChange={handleMemberFieldChange}
                                    />
                                    <DataField
                                      getFormValue={getMemberFormValue}
                                      label="Issue State"
                                      value={null}
                                      isEditable={true}
                                      isEditing={isEditingMembers}
                                      fieldPath={`${member.id}.id_info.0.issue_state`}
                                      onFieldChange={handleMemberFieldChange}
                                    />
                                    <DataField
                                      getFormValue={getMemberFormValue}
                                      label="ID Number"
                                      value={null}
                                      isEditable={true}
                                      isEditing={isEditingMembers}
                                      fieldPath={`${member.id}.id_info.0.number`}
                                      onFieldChange={handleMemberFieldChange}
                                    />
                                    <DataField
                                      getFormValue={getMemberFormValue}
                                      label="Issuance Date"
                                      value={null}
                                      isEditable={true}
                                      type="date"
                                      isEditing={isEditingMembers}
                                      fieldPath={`${member.id}.id_info.0.issuance_date`}
                                      onFieldChange={handleMemberFieldChange}
                                    />
                                    <DataField
                                      getFormValue={getMemberFormValue}
                                      label="Expiration Date"
                                      value={null}
                                      isEditable={true}
                                      type="date"
                                      isEditing={isEditingMembers}
                                      fieldPath={`${member.id}.id_info.0.expiration_date`}
                                      onFieldChange={handleMemberFieldChange}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600">No additional members found</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === "accounts" && (
          <div className="space-y-4">
            {household.accounts && household.accounts.length > 0 ? (
              (() => {
                const accountIndex = household.accounts.findIndex(
                  (a, i) => (a.id || i) === selectedAccountId,
                );
                const account =
                  accountIndex >= 0
                    ? household.accounts[accountIndex]
                    : household.accounts[0];
                const index = accountIndex >= 0 ? accountIndex : 0;
                const aid = account?.id ?? index;
                const section =
                  selectedAccountSectionByAccount[aid] || "basic_details";
                const isSpecialReg = isSpecialRegistration(account.registration);
                const accountInfoTabs = [
                  { key: "basic_details", label: "Basic Details" },
                  ...(!isSpecialReg ? [{ key: "investment_strategy", label: "Investment Strategy" }] : []),
                  ...(!isSpecialReg ? [{ key: "transfer_details", label: "Transfer Details" }] : []),
                  ...(account.registration?.toLowerCase() === "trust"
                    ? [{ key: "trust_account", label: "Trust Info" }]
                    : []),
                  ...(account.registration?.toLowerCase() === "business"
                    ? [{ key: "business_info", label: "Business Info" }]
                    : []),
                  ...(account.registration?.toLowerCase() === "inherited retirement" &&
                  (account.type === "Inherited IRA" ||
                    account.type === "Inherited Roth IRA")
                    ? [{ key: "inheritance_info", label: "Inheritance Info" }]
                    : []),
                  ...(isSpecialRegistration(account.registration)
                    ? [{ key: "other_info", label: "Other Info" }]
                    : []),

                  ...(!isSpecialReg ? [{ key: "beneficiaries", label: "Beneficiaries" }] : []),
                  ...(!isSpecialReg ? [{ key: "trusted_contacts", label: "Trusted Contacts" }] : []),
                ];
                const moneyMovementTabs = isSpecialReg ? [] : [
                  { key: "contributions", label: "Contributions" },
                  { key: "withdrawals", label: "Withdrawals" },
                  {
                    key: "bank_account_details",
                    label: "Linked Bank Account",
                  },
                ];
                const tabBtn = (s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() =>
                      setSelectedAccountSectionByAccount((prev) => ({
                        ...prev,
                        [aid]: s.key,
                      }))
                    }
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      section === s.key
                        ? "bg-emerald-200 text-emerald-900 ring-1 ring-emerald-300"
                        : "text-gray-700 hover:bg-white/60"
                    }`}
                  >
                    {s.label}
                  </button>
                );
                return (
                  <>
                    <div className="flex items-center gap-2 border-b border-gray-200 pb-2">
                      <div
                        className="min-w-0 flex-1 overflow-x-auto overscroll-x-contain touch-pan-x [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                      >
                        <div className="flex w-max flex-nowrap gap-2 pb-0.5 pr-1">
                          {household.accounts.map((acc, idx) => {
                            const accId = acc.id ?? idx;
                            return (
                              <button
                                key={accId}
                                type="button"
                                onClick={() => {
                                  setSelectedAccountId(accId);
                                  if (
                                    selectedAccountSectionByAccount[accId] ===
                                    undefined
                                  ) {
                                    setSelectedAccountSectionByAccount(
                                      (prev) => ({
                                        ...prev,
                                        [accId]: "basic_details",
                                      }),
                                    );
                                  }
                                }}
                                className={`shrink-0 px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap ${
                                  selectedAccountId === accId
                                    ? "bg-emerald-100 text-emerald-800 border-b-2 border-emerald-600"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                              >
                                {formatAccountName(
                                  acc,
                                  idx + 1,
                                  household.members || [],
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2 border-l border-gray-200 pl-2">
                        {isEditingAccounts ? (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setIsEditingAccounts(false);
                                setValidationErrors({});
                                setAccountsFormData({});
                                setRemovedBeneficiaries({});
                                setRemovedTrustedContacts({});
                                setPendingNewDeposits({});
                                setPendingNewWithdrawals({});
                                setRemovedDeposits({});
                                setRemovedWithdrawals({});
                                setNewlyAddedWithdrawalBankAccounts({});
                                setBeneficiaryValidationErrorsByAccount({});
                              }}
                              className="btn-secondary text-xs px-3 py-2 flex items-center gap-1.5"
                            >
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={handleSaveAccounts}
                              disabled={
                                savingAccounts ||
                                Object.keys(validationErrors).length > 0
                              }
                              className="btn-primary text-xs px-3 py-2 flex items-center gap-1.5 disabled:opacity-50"
                            >
                              {savingAccounts ? (
                                <>
                                  <svg
                                    className="animate-spin w-3.5 h-3.5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <svg
                                    className="w-3.5 h-3.5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                  Save
                                </>
                              )}
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => setShowAddAccountModal(true)}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs px-3 py-2 flex items-center gap-1.5 transition-colors"
                            >
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 4v16m8-8H4"
                                />
                              </svg>
                              Add Account
                            </button>
                            {(household?.accounts || []).some(
                              (a) => a?.status === "nigo",
                            ) && (
                              <button
                                type="button"
                                onClick={() => setShowResolveNigoModal(true)}
                                className="bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-bold text-xs px-3 py-2 flex items-center gap-1.5 transition-colors"
                              >
                                <svg
                                  className="w-3.5 h-3.5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                                Resolve Accounts
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleDeleteAccount(account.id)}
                              disabled={deletingAccountId === account.id}
                              className="bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold text-xs px-3 py-2 flex items-center gap-1.5 transition-colors disabled:opacity-50"
                              title="Delete account"
                            >
                              {deletingAccountId === account.id ? (
                                <>
                                  <svg
                                    className="animate-spin w-3.5 h-3.5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  Deleting...
                                </>
                              ) : (
                                <>
                                  <svg
                                    className="w-3.5 h-3.5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                  </svg>
                                  Delete
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => setIsEditingAccounts(true)}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs px-3 py-2 flex items-center gap-1.5 transition-colors"
                            >
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                              Edit
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-gray-200 pb-3">
                      <div className="flex items-center gap-2 flex-wrap rounded-lg px-3 py-2 bg-emerald-50/90 border border-emerald-100">
                        <span className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mr-1">
                          Account Info
                        </span>
                        {accountInfoTabs.map(tabBtn)}
                      </div>
                      {!isSpecialReg && (
                        <div className="flex items-center gap-2 flex-wrap rounded-lg px-3 py-2 bg-green-50/90 border border-green-100">
                          <span className="text-xs font-semibold uppercase tracking-wide text-green-700 mr-1">
                            Money Movements
                          </span>
                          {moneyMovementTabs.map(tabBtn)}
                        </div>
                      )}

                    </div>
                    <div
                      data-account-id={aid}
                      className="border border-gray-200 rounded-lg p-4 bg-white"
                    >
                      {isEditingAccounts &&
                        section !== "bank_account_details" &&
                        advisorAccountSectionHasUnsavedChanges({
                          accountId: aid,
                          section,
                          account,
                          accountsFormData,
                          getAccountFormValue,
                          pendingNewDeposits,
                          pendingNewWithdrawals,
                          removedDeposits,
                          removedWithdrawals,
                          removedBeneficiaries,
                          removedTrustedContacts,
                          newlyAddedWithdrawalBankAccounts,
                        }) && (
                          <UnsavedChangesBanner
                            className="mb-4"
                            message={advisorAccountUnsavedMessage(section)}
                          />
                        )}
                      {section === "basic_details" && (
                        <div data-section={`basic_details_${aid}`}>
                          <div>
                            <div className="grid grid-cols-2 gap-4">
                              {(() => {
                                const isBusinessAccount =
                                  (account.registration || "").toLowerCase() === "business";
                                const slotCount = isBusinessAccount ? 4 : 2;
                                const ownerIds = Array.from({ length: slotCount }, (_, i) => {
                                  const ownerId = getAccountFormValue(
                                    `${account.id}.owners_info.${i}.id`,
                                    account.owners_info?.[i]?.id,
                                  );
                                  return ownerId ? String(ownerId) : "";
                                });
                                return ownerIds.map((ownerId, i) => (
                                  <OwnerDropdownField
                                    key={i}
                                    label={`Owner ${i + 1}`}
                                    value={ownerId ? getMemberNameById(ownerId) : ""}
                                    isEditable={true}
                                    isEditing={isEditingAccounts}
                                    disabled={
                                      i > 0 && !isBusinessAccount && !isTwoOwnerAccount(account)
                                    }
                                    excludeMemberIds={ownerIds.filter(
                                      (id, j) => j !== i && id,
                                    )}
                                    householdMembers={household.members || []}
                                    fieldPath={`${account.id}.owners_info.${i}.id`}
                                    onFieldChange={handleAccountFieldChange}
                                    getFormValue={getAccountFormValue}
                                  />
                                ));
                              })()}
                              {account.status === "nigo" && (
                                <>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Status
                                    </label>
                                    <div className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-red-800">
                                      NIGO
                                    </div>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      NIGO Reason
                                    </label>
                                    <div className="text-sm font-semibold text-red-700">
                                      {NIGO_REASON_LABELS[account.sub_status] ||
                                        account.sub_status ||
                                        "—"}
                                    </div>
                                  </div>
                                </>
                              )}
                              <DataField
                                getFormValue={getAccountFormValue}
                                label="New Account Number"
                                value={account.new_account_number}
                                isEditable={true}
                                isEditing={isEditingAccounts}
                                fieldPath={`${account.id}.new_account_number`}
                                onFieldChange={handleAccountFieldChange}
                              />
                              <DataField
                                getFormValue={getAccountFormValue}
                                label="Custodian"
                                value={account.custodian}
                                isEditable={true}
                                isEditing={isEditingAccounts}
                                fieldPath={`${account.id}.custodian`}
                                onFieldChange={handleAccountFieldChange}
                              />
                              <DataField
                                getFormValue={getAccountFormValue}
                                label="Account Registration"
                                value={account.registration}
                                isEditable={true}
                                isEditing={isEditingAccounts}
                                fieldPath={`${account.id}.registration`}
                                onFieldChange={(path, val) => {
                                  handleAccountFieldChange(path, val);
                                  const availableTypes = getAccountTypesForRegistration(val);
                                  handleAccountFieldChange(
                                    `${account.id}.type`,
                                    availableTypes?.[0] || ""
                                  );
                                }}
                                options={REGISTRATION_OPTIONS}
                              />
                              {(() => {
                                const currentRegistration =
                                  getAccountFormValue?.(
                                    `${account.id}.registration`,
                                    account.registration
                                  ) || account.registration;
                                const typeOptions =
                                  getAccountTypesForRegistration(currentRegistration);
                                return (
                                  <DataField
                                    getFormValue={getAccountFormValue}
                                    label="Type"
                                    value={account.type}
                                    isEditable={typeOptions.length > 1}
                                    isEditing={isEditingAccounts}
                                    fieldPath={`${account.id}.type`}
                                    onFieldChange={handleAccountFieldChange}
                                    options={typeOptions}
                                  />
                                );
                              })()}
                              <DataField
                                getFormValue={getAccountFormValue}
                                label="Fee Billed Percentage"
                                value={
                                  account.fee_billed_percentage != null
                                    ? `${account.fee_billed_percentage}%`
                                    : "N/A"
                                }
                                isEditable={true}
                                type="number"
                                min={0}
                                max={100}
                                isEditing={isEditingAccounts}
                                fieldPath={`${account.id}.fee_billed_percentage`}
                                onFieldChange={handleAccountFieldChange}
                                disableWheel={true}
                                error={
                                  validationErrors[
                                    `${account.id}.fee_billed_percentage`
                                  ]
                                }
                                onFieldError={handleFieldError}
                              />
                              {(() => {
                                const feeBasedEffective = getAccountFormValue(
                                  `${account.id}.fee_based_account`,
                                  account.fee_based_account,
                                );
                                if (
                                  !shouldShowAccountValueField(
                                    account,
                                    household,
                                    feeBasedEffective,
                                  )
                                ) {
                                  return null;
                                }
                                if (
                                  aumAvailability &&
                                  aumAvailability !== 1 &&
                                  aumAvailability !== 2
                                ) {
                                  return null;
                                }
                                return (
                              <DataField
                                getFormValue={getAccountFormValue}
                                label="Account Value"
                                value={formatCurrency(account.account_value)}
                                isEditable={true}
                                type="number"
                                min={0}
                                max={50000000}
                                isEditing={isEditingAccounts}
                                fieldPath={`${account.id}.account_value`}
                                onFieldChange={handleAccountFieldChange}
                                disableWheel={true}
                                error={
                                  validationErrors[
                                    `${account.id}.account_value`
                                  ]
                                }
                                onFieldError={handleFieldError}
                              />
                                );
                              })()}
                              <DataField
                                getFormValue={getAccountFormValue}
                                label="Proceeds"
                                value={account.proceeds}
                                isEditable={true}
                                isEditing={isEditingAccounts}
                                options={PROCEEDS_OPTIONS}
                                fieldPath={`${account.id}.proceeds`}
                                onFieldChange={handleAccountFieldChange}
                                error={
                                  validationErrors[`${account.id}.proceeds`]
                                }
                                onFieldError={handleFieldError}
                              />
                              <DataField
                                getFormValue={getAccountFormValue}
                                label="Security Purchases"
                                value={account.security_purchases}
                                isEditable={true}
                                isEditing={isEditingAccounts}
                                options={SECURITY_PURCHASES_OPTIONS}
                                fieldPath={`${account.id}.security_purchases`}
                                onFieldChange={handleAccountFieldChange}
                                error={
                                  validationErrors[
                                    `${account.id}.security_purchases`
                                  ]
                                }
                                onFieldError={handleFieldError}
                              />
                              <DataField
                                getFormValue={getAccountFormValue}
                                label="Dividend/Distribution Income"
                                value={account.distribution_income || DEFAULT_DIVIDEND_OPTION}
                                isEditable={true}
                                isEditing={isEditingAccounts}
                                options={DIVIDEND_OPTIONS}
                                fieldPath={`${account.id}.distribution_income`}
                                onFieldChange={handleAccountFieldChange}
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-4 mt-4">
                              {!isRetirementAccount(getAccountFormValue?.(`${account.id}.registration`, account.registration) || account.registration) && (
                                <ToggleField
                                  label="Are Margins enabled?"
                                  value={account.margin}
                                  isEditable={true}
                                  isEditing={isEditingAccounts}
                                  fieldPath={`${account.id}.margin`}
                                  onFieldChange={handleAccountFieldChange}
                                  formData={accountsFormData}
                                />
                              )}
                              <ToggleField
                                label="Are Options enabled?"
                                value={account.options}
                                isEditable={true}
                                isEditing={isEditingAccounts}
                                fieldPath={`${account.id}.options`}
                                onFieldChange={handleAccountFieldChange}
                                formData={accountsFormData}
                              />
                              {isFeeBasedEditable && (
                                <ToggleField
                                  label="Is this a Fee Based Account?"
                                  value={account.fee_based_account}
                                  isEditable={true}
                                  isEditing={isEditingAccounts}
                                  fieldPath={`${account.id}.fee_based_account`}
                                  onFieldChange={handleAccountFieldChange}
                                  formData={accountsFormData}
                                />
                              )}
                              <ToggleField
                                label="Is Check Writing enabled?"
                                value={account.check_writing}
                                isEditable={true}
                                isEditing={isEditingAccounts}
                                fieldPath={`${account.id}.check_writing`}
                                onFieldChange={handleAccountFieldChange}
                                formData={accountsFormData}
                              />
                              {brokerageFlags.isWaverly && (
                                <ToggleField
                                  label="Is this a Planning Account?"
                                  value={account.is_planned ?? true}
                                  isEditable={true}
                                  isEditing={isEditingAccounts}
                                  fieldPath={`${account.id}.is_planned`}
                                  onFieldChange={handleAccountFieldChange}
                                  formData={accountsFormData}
                                />
                              )}
                            </div>
                            <div className="mt-4">
                              {(() => {
                                const notesValue = getAccountFormValue(
                                  `${account.id}.notes`,
                                  account.notes || "",
                                );
                                const len = (notesValue || "").length;
                                const over = len > 100;
                                return (
                                  <>
                                    <div className="flex items-center justify-between mb-1">
                                      <label className="block text-sm font-medium text-gray-700">
                                        Notes
                                      </label>
                                      {isEditingAccounts && (
                                        <span
                                          className={`text-[10px] font-medium ${
                                            over ? "text-red-600" : "text-gray-500"
                                          }`}
                                        >
                                          {len}/100
                                        </span>
                                      )}
                                    </div>
                                    {isEditingAccounts ? (
                                      <textarea
                                        value={notesValue || ""}
                                        maxLength={100}
                                        rows={2}
                                        onChange={(e) =>
                                          handleAccountFieldChange(
                                            `${account.id}.notes`,
                                            e.target.value,
                                          )
                                        }
                                        placeholder="Add notes about this account"
                                        className={`w-full rounded-md border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                                          over
                                            ? "border-red-400 focus:ring-red-500"
                                            : "border-gray-300 focus:ring-emerald-500"
                                        }`}
                                      />
                                    ) : (
                                      <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800 whitespace-pre-wrap break-words min-h-[2.5rem]">
                                        {account.notes || (
                                          <span className="text-gray-400 italic">
                                            No notes added.
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      )}

                      {section === "investment_strategy" && (
                        <div data-section={`investment_strategy_${aid}`}>
                          <div className="grid grid-cols-2 gap-4">
                            {showAccountRiskDetails(brokerageFlags, account) && (
                              <>
                                <DataField
                                  getFormValue={getAccountFormValue}
                                  label="Primary Investment Objective"
                                  value={
                                    account.risk_tolerance?.invest_objectives?.[0]
                                  }
                                  isEditable={true}
                                  isEditing={isEditingAccounts}
                                  options={investmentObjectives}
                                  fieldPath={`${account.id}.risk_tolerance.invest_objectives.0`}
                                  onFieldChange={handleAccountFieldChange}
                                />
                                <DataField
                                  getFormValue={getAccountFormValue}
                                  label="Risk Tolerance"
                                  value={account.risk_tolerance?.risk_tolerance}
                                  isEditable={true}
                                  isEditing={isEditingAccounts}
                                  options={RISK_TOLERANCE_OPTIONS}
                                  fieldPath={`${account.id}.risk_tolerance.risk_tolerance`}
                                  onFieldChange={handleAccountFieldChange}
                                />
                                <DataField
                                  getFormValue={getTimeHorizonFormValueAcct(account.id)}
                                  label="Time Horizon"
                                  value={timeHorizonToDisplayAcct(account.risk_tolerance?.time_horizon)}
                                  isEditable={true}
                                  isEditing={isEditingAccounts}
                                  options={ACCOUNT_TIME_HORIZON_OPTIONS}
                                  fieldPath={`${account.id}.risk_tolerance.time_horizon_display`}
                                  onFieldChange={handleTimeHorizonChangeAcct(account.id)}
                                />
                              </>
                            )}
                            <DataField
                              getFormValue={getAccountFormValue}
                              label="Account Decision Making"
                              value={account.risk_tolerance?.decision_making}
                              isEditable={true}
                              isEditing={isEditingAccounts}
                              options={DECISION_MAKING_OPTIONS}
                              fieldPath={`${account.id}.risk_tolerance.decision_making`}
                              onFieldChange={handleAccountFieldChange}
                            />
                            {showSourceOfFunds(brokerageFlags) && (
                              <DataField
                                getFormValue={(path, fallback) => {
                                  const raw = getAccountFormValue(
                                    path,
                                    undefined,
                                  );
                                  if (raw !== undefined) {
                                    return Array.isArray(raw)
                                      ? raw[0] || ""
                                      : raw;
                                  }
                                  return fallback;
                                }}
                                label="Source of Funds"
                                value={
                                  Array.isArray(
                                    account.risk_tolerance?.source_of_funds,
                                  )
                                    ? account.risk_tolerance.source_of_funds.join(
                                        ", ",
                                      )
                                    : account.risk_tolerance?.source_of_funds
                                }
                                isEditable={true}
                                isEditing={isEditingAccounts}
                                options={SOURCE_OF_FUNDS_OPTIONS}
                                fieldPath={`${account.id}.risk_tolerance.source_of_funds`}
                                onFieldChange={handleAccountFieldChange}
                              />
                            )}
                            {showUseOfFunds(brokerageFlags) && (
                              <DataField
                                getFormValue={getAccountFormValue}
                                label="Purpose of Account"
                                value={account.risk_tolerance?.use_of_funds}
                                isEditable={true}
                                isEditing={isEditingAccounts}
                                options={PRIMARY_USE_OPTIONS}
                                fieldPath={`${account.id}.risk_tolerance.use_of_funds`}
                                onFieldChange={handleAccountFieldChange}
                              />
                            )}
                            {showLiquidityRequirements(brokerageFlags) && (
                              <DataField
                                getFormValue={getLiquidityNeedsFormValueAcct(account.id)}
                                label="Liquidity Needs ($)"
                                value={liquidityNeedsToDisplayAcct(account.risk_tolerance?.liquidity_requirements)}
                                isEditable={true}
                                isEditing={isEditingAccounts}
                                options={LIQUIDITY_NEEDS_OPTIONS}
                                fieldPath={`${account.id}.risk_tolerance.liquidity_requirements.amount_display`}
                                onFieldChange={handleLiquidityNeedsChangeAcct(account.id)}
                              />
                            )}
                            {showLiquidityRequirements(brokerageFlags) && (
                              <DataField
                                getFormValue={getLiquidityTimeHorizonFormValueAcct(account.id)}
                                label="Liquidity Time Horizon (years)"
                                value={liquidityTimeHorizonToDisplayAcct(account.risk_tolerance?.liquidity_requirements)}
                                isEditable={true}
                                isEditing={isEditingAccounts}
                                options={LIQUIDITY_TIME_HORIZON_OPTIONS}
                                fieldPath={`${account.id}.risk_tolerance.liquidity_requirements.time_horizon_display`}
                                onFieldChange={handleLiquidityTimeHorizonChangeAcct(account.id)}
                              />
                            )}
                          </div>
                        </div>
                      )}

                      {section === "transfer_details" && (() => {
                        const xferAddr = account.transfer_info?.address || {};
                        const countryReadDisplay =
                          !xferAddr.country ||
                          xferAddr.country === "USA" ||
                          String(xferAddr.country).trim() === ""
                            ? "United States"
                            : xferAddr.country;
                        return (
                        <div data-section={`transfer_details_${aid}`}>
                          <div>
                            <div className="grid grid-cols-2 gap-4">
                              {(() => {
                                const lplAccountNumberError = validateLplAccountNumber(
                                  getAccountFormValue(
                                    `${account.id}.transfer_info.old_account_number`,
                                    account.transfer_info?.old_account_number || "",
                                  ),
                                  getAccountFormValue(
                                    `${account.id}.transfer_info.delivering_firm_name`,
                                    account.transfer_info?.delivering_firm_name || "",
                                  ),
                                ).error;
                                return (
                                  <div>
                                    <DataField
                                      getFormValue={getAccountFormValue}
                                      label="Existing Account Number"
                                      value={
                                        account.transfer_info?.old_account_number
                                      }
                                      isEditable={true}
                                      isEditing={isEditingAccounts}
                                      fieldPath={`${account.id}.transfer_info.old_account_number`}
                                      onFieldChange={handleAccountFieldChange}
                                      commitOnChange={true}
                                      error={lplAccountNumberError}
                                    />
                                  </div>
                                );
                              })()}
                              <DataField
                                getFormValue={getAccountFormValue}
                                label="Delivering Firm Name"
                                value={
                                  account.transfer_info?.delivering_firm_name
                                }
                                options={deliveringFirmOptions(
                                  account.transfer_info?.delivering_firm_name,
                                )}
                                isEditable={true}
                                isEditing={isEditingAccounts}
                                fieldPath={`${account.id}.transfer_info.delivering_firm_name`}
                                onFieldChange={handleAccountFieldChange}
                              />
                              <DataField
                                getFormValue={getAccountFormValue}
                                label="Address Line 1"
                                value={
                                  account.transfer_info?.address?.address_line_1
                                }
                                isEditable={true}
                                isEditing={isEditingAccounts}
                                fieldPath={`${account.id}.transfer_info.address.address_line_1`}
                                onFieldChange={handleAccountFieldChange}
                              />
                              <DataField
                                getFormValue={getAccountFormValue}
                                label="Address Line 2"
                                value={
                                  account.transfer_info?.address?.address_line_2
                                }
                                isEditable={true}
                                isEditing={isEditingAccounts}
                                fieldPath={`${account.id}.transfer_info.address.address_line_2`}
                                onFieldChange={handleAccountFieldChange}
                              />
                              {isEditingAccounts ? (
                                <div className="col-span-2 bg-emerald-50/50 p-4 rounded-xl border border-emerald-100">
                                  <StateCitySelect
                                    countryValue={getAccountFormValue(
                                      `${account.id}.transfer_info.address.country`,
                                      account.transfer_info?.address?.country ||
                                        "USA",
                                    )}
                                    stateValue={getAccountFormValue(
                                      `${account.id}.transfer_info.address.state`,
                                      account.transfer_info?.address?.state || "",
                                    )}
                                    cityValue={getAccountFormValue(
                                      `${account.id}.transfer_info.address.city`,
                                      account.transfer_info?.address?.city || "",
                                    )}
                                    onCountryChange={(val) =>
                                      handleAccountFieldChange(
                                        `${account.id}.transfer_info.address.country`,
                                        val,
                                      )
                                    }
                                    onStateChange={(val) =>
                                      handleAccountFieldChange(
                                        `${account.id}.transfer_info.address.state`,
                                        val,
                                      )
                                    }
                                    onCityChange={(val) =>
                                      handleAccountFieldChange(
                                        `${account.id}.transfer_info.address.city`,
                                        val,
                                      )
                                    }
                                    isEditing={true}
                                    stateLabel="State"
                                    cityLabel="City"
                                    zipValue={getAccountFormValue(
                                      `${account.id}.transfer_info.address.postal_code`,
                                      account.transfer_info?.address?.postal_code ||
                                        "",
                                    )}
                                    onZipChange={(val) =>
                                      handleAccountFieldChange(
                                        `${account.id}.transfer_info.address.postal_code`,
                                        val,
                                      )
                                    }
                                    zipLabel="ZIP Code"
                                    zipRestrictUsFive
                                    zipError={
                                      validationErrors[
                                        `${account.id}.transfer_info.address.postal_code`
                                      ]
                                    }
                                    onZipValidationError={(msg) =>
                                      handleFieldError(
                                        `${account.id}.transfer_info.address.postal_code`,
                                        msg,
                                      )
                                    }
                                  />
                                </div>
                              ) : (
                                <>
                                  <DataField
                                    getFormValue={getAccountFormValue}
                                    label="Country"
                                    value={countryReadDisplay}
                                    isEditable={true}
                                    isEditing={isEditingAccounts}
                                    fieldPath={`${account.id}.transfer_info.address.country`}
                                    onFieldChange={handleAccountFieldChange}
                                  />
                                  <DataField
                                    getFormValue={getAccountFormValue}
                                    label="State"
                                    value={xferAddr.state}
                                    isEditable={true}
                                    isEditing={isEditingAccounts}
                                    fieldPath={`${account.id}.transfer_info.address.state`}
                                    onFieldChange={handleAccountFieldChange}
                                  />
                                  <DataField
                                    getFormValue={getAccountFormValue}
                                    label="City"
                                    value={xferAddr.city}
                                    isEditable={true}
                                    isEditing={isEditingAccounts}
                                    fieldPath={`${account.id}.transfer_info.address.city`}
                                    onFieldChange={handleAccountFieldChange}
                                  />
                                  <DataField
                                    getFormValue={getAccountFormValue}
                                    label="ZIP Code"
                                    value={xferAddr.postal_code}
                                    isEditable={true}
                                    isEditing={isEditingAccounts}
                                    fieldPath={`${account.id}.transfer_info.address.postal_code`}
                                    onFieldChange={handleAccountFieldChange}
                                  />
                                </>
                              )}
                              <DataField
                                getFormValue={getAccountFormValue}
                                label="Delivery Firm Phone"
                                value={account.transfer_info?.address?.phone}
                                isEditable={true}
                                type="tel"
                                isEditing={isEditingAccounts}
                                fieldPath={`${account.id}.transfer_info.address.phone`}
                                onFieldChange={handleAccountFieldChange}
                              />
                              <DataField
                                getFormValue={getAccountFormValue}
                                label="Transfer Type"
                                value={account.transfer_info?.transfer_type}
                                isEditable={true}
                                isEditing={isEditingAccounts}
                                options={TYPE_OF_TRANSFER_OPTIONS}
                                fieldPath={`${account.id}.transfer_info.transfer_type`}
                                onFieldChange={handleAccountFieldChange}
                              />
                              <div className="col-span-2">
                                <CheckboxField
                                  label="Full Transfer"
                                  value={
                                    account.transfer_info?.is_full_transfer ||
                                    false
                                  }
                                  isEditable={true}
                                  isEditing={isEditingAccounts}
                                  fieldPath={`${account.id}.transfer_info.is_full_transfer`}
                                  onFieldChange={handleAccountFieldChange}
                                  formData={accountsFormData}
                                  variant="field"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        );
                      })()}

                      {section === "trust_account" &&
                        account.registration?.toLowerCase() === "trust" && (
                          <div data-section={`trust_info_${aid}`}>
                            <div>
                              {(() => {
                                const ti = account.trust_info || {};
                                const trustRevocableCurrent =
                                  isRevocableTrust(account.type);
                                const trustGrantorIds = getAccountFormValue(
                                  `${account.id}.trust_info.grantorIds`,
                                  getTrustGrantorIds(ti),
                                );
                                const trustPrimaryGrantorId = (Array.isArray(trustGrantorIds) ? trustGrantorIds : []).find(Boolean) || "";
                                const trustTaxIdValue = trustRevocableCurrent
                                  ? getMemberSsnById(trustPrimaryGrantorId)
                                  : ti.ssn_or_tin;
                                const trustTaxIdFieldPath =
                                  trustRevocableCurrent && trustPrimaryGrantorId
                                    ? `${trustPrimaryGrantorId}.personal_info.ssn_or_tin`
                                    : `${account.id}.trust_info.ssn_or_tin`;
                                return (
                              <div className="grid grid-cols-2 gap-4">
                                <DataField
                                  getFormValue={getAccountFormValue}
                                  label="Trust Name"
                                  value={ti.trust_name ?? ti.trust_title}
                                  isEditable={true}
                                  isEditing={isEditingAccounts}
                                  fieldPath={`${account.id}.trust_info.trust_name`}
                                  onFieldChange={handleAccountFieldChange}
                                />
                                <DataField
                                  getFormValue={getAccountFormValue}
                                  label="Trust Title(as per document)"
                                  value={ti.trust_title}
                                  isEditable={true}
                                  isEditing={isEditingAccounts}
                                  fieldPath={`${account.id}.trust_info.trust_title`}
                                  onFieldChange={handleAccountFieldChange}
                                />
                                <DataField
                                  getFormValue={
                                    trustRevocableCurrent
                                      ? null
                                      : getAccountFormValue
                                  }
                                  label="Tax ID Number"
                                  value={trustTaxIdValue}
                                  isEditable={!trustRevocableCurrent}
                                  isEditing={isEditingAccounts}
                                  type="ssn"
                                  ssnLengthOnly
                                  fieldPath={trustTaxIdFieldPath}
                                  onFieldChange={handleAccountFieldChange}
                                  error={
                                    validationErrors[
                                      `${account.id}.trust_info.ssn_or_tin`
                                    ]
                                  }
                                  onFieldError={handleFieldError}
                                />
                                <DataField
                                  getFormValue={getAccountFormValue}
                                  label="Execution Date"
                                  value={formatDate(ti.execution_date)}
                                  dateValue={ti.execution_date}
                                  isEditable={true}
                                  type="date"
                                  isEditing={isEditingAccounts}
                                  fieldPath={`${account.id}.trust_info.execution_date`}
                                  onFieldChange={handleAccountFieldChange}
                                  error={
                                    validationErrors[
                                      `${account.id}.trust_info.execution_date`
                                    ]
                                  }
                                  onFieldError={handleFieldError}
                                  dateConstraint="past_only"
                                />
                                <DataField
                                  getFormValue={getAccountFormValue}
                                  label="Amended Date (optional)"
                                  value={formatDate(ti.amended_date)}
                                  dateValue={ti.amended_date}
                                  isEditable={true}
                                  type="date"
                                  isEditing={isEditingAccounts}
                                  fieldPath={`${account.id}.trust_info.amended_date`}
                                  onFieldChange={handleAccountFieldChange}
                                  error={
                                    validationErrors[
                                      `${account.id}.trust_info.amended_date`
                                    ]
                                  }
                                  onFieldError={handleFieldError}
                                  dateConstraint="past_only"
                                />
                                <DataField
                                  getFormValue={getAccountFormValue}
                                  label="State"
                                  value={resolveStateName(ti.trust_state) || ti.trust_state}
                                  isEditable={true}
                                  isEditing={isEditingAccounts}
                                  fieldPath={`${account.id}.trust_info.trust_state`}
                                  onFieldChange={handleAccountFieldChange}
                                  options={US_STATES.map(s => ({ label: `${s.name} (${s.abbreviation})`, value: s.abbreviation }))}
                                />
                                {(() => {
                                  const formGrantorIds = getAccountFormValue(
                                    `${account.id}.trust_info.grantorIds`,
                                    getTrustGrantorIds(ti),
                                  );
                                  const rows = (Array.isArray(formGrantorIds) ? formGrantorIds : []).length > 0
                                    ? formGrantorIds
                                    : [""];
                                  const setGrantorIds = (next) =>
                                    handleAccountFieldChange(`${account.id}.trust_info.grantorIds`, next);
                                  const memberOpts = (household.members || [])
                                    .map((m) => ({
                                      id: m.id,
                                      label: `${m.personal_info?.first_name || ""} ${m.personal_info?.last_name || ""}`.trim(),
                                    }))
                                    .filter((m) => m.label);
                                  return (
                                    <div className="col-span-2">
                                      <label className="block text-sm font-medium text-gray-700 mb-1">Grantors</label>
                                      {!isEditingAccounts ? (
                                        <div className="text-gray-900">
                                          {(rows.filter(Boolean).length > 0 ? rows.filter(Boolean) : [""]).map((gid, idx) => (
                                            <div key={`g-ro-${idx}`}>{getMemberNameById(gid) || "—"}</div>
                                          ))}
                                        </div>
                                      ) : (
                                        <div className="space-y-2">
                                          {rows.map((gid, idx) => (
                                            <div key={`g-${idx}-${gid || "empty"}`} className="flex gap-2 items-end">
                                              <select
                                                value={gid || ""}
                                                onChange={(e) => {
                                                  const next = [...rows];
                                                  next[idx] = e.target.value;
                                                  setGrantorIds(next);
                                                }}
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                              >
                                                <option value="">Select member...</option>
                                                {memberOpts
                                                  .filter((m) => !rows.some((r, i) => i !== idx && String(r || "") === String(m.id)))
                                                  .map((m) => (
                                                    <option key={m.id} value={m.id}>{m.label}</option>
                                                  ))}
                                              </select>
                                              {rows.length > 1 && (
                                                <button
                                                  type="button"
                                                  onClick={() => setGrantorIds(rows.filter((_, i) => i !== idx))}
                                                  className="px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-sm"
                                                >Remove</button>
                                              )}
                                            </div>
                                          ))}
                                          {memberOpts.filter((m) => !rows.some((r) => String(r || "") === String(m.id))).length > 0 && (
                                            <button
                                              type="button"
                                              onClick={() => setGrantorIds([...rows, ""])}
                                              className="px-3 py-2 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm"
                                            >+ Add Grantor</button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                                {showStatutoryTrust(brokerageFlags) && (
                                  <CheckboxField
                                    label="Statutory Trust"
                                    value={ti.statutory_trust}
                                    isEditable={true}
                                    isEditing={isEditingAccounts}
                                    fieldPath={`${account.id}.trust_info.statutory_trust`}
                                    onFieldChange={handleAccountFieldChange}
                                    formData={accountsFormData}
                                    variant="field"
                                  />
                                )}
                                <DataField
                                  getFormValue={getAccountFormValue}
                                  label="Tax ID Type"
                                  value={ti.tax_id_type}
                                  isEditable={true}
                                  isEditing={isEditingAccounts}
                                  options={TRUST_TAX_ID_TYPE_OPTIONS}
                                  fieldPath={`${account.id}.trust_info.tax_id_type`}
                                  onFieldChange={handleAccountFieldChange}
                                />
                              </div>
                                );
                              })()}
                              {(() => {
                                const ti = account.trust_info || {};
                                const trusteesBase = Array.isArray(ti.trustees)
                                  ? ti.trustees
                                  : [];
                                const tForm =
                                  accountsFormData[account.id]?.trust_info?.trustees;
                                const formIdxs =
                                  tForm && typeof tForm === "object"
                                    ? Object.keys(tForm)
                                        .map(Number)
                                        .filter(Number.isFinite)
                                    : [];
                                const maxForm = formIdxs.length
                                  ? Math.max(...formIdxs)
                                  : -1;
                                const rowCount = isEditingAccounts
                                  ? Math.max(
                                      trusteesBase.length,
                                      maxForm + 1,
                                      1,
                                    )
                                  : trusteesBase.length;
                                if (rowCount === 0) return null;
                                const grantorIdsForTrustees = getAccountFormValue(
                                  `${account.id}.trust_info.grantorIds`,
                                  getTrustGrantorIds(ti),
                                );
                                const grantorIdSet = new Set((grantorIdsForTrustees || []).map(String));
                                const memberIdList = (household.members || [])
                                  .map((m) => m.id)
                                  .filter(Boolean);
                                const assignedTrustees = new Set();
                                for (let j = 0; j < rowCount; j++) {
                                  const oid = getAccountFormValue(
                                    `${account.id}.trust_info.trustees.${j}.id`,
                                    trusteesBase[j]
                                      ? getTrusteeMemberId(trusteesBase[j])
                                      : "",
                                  );
                                  if (oid) assignedTrustees.add(String(oid));
                                }
                                const canAddMoreTrustees = memberIdList.some(
                                  (id) => !assignedTrustees.has(String(id)),
                                );
                                return (
                                  <div className="mt-4">
                                    <h5 className="font-medium text-gray-800 mb-2">
                                      Trustees
                                    </h5>
                                    <div className="space-y-2">
                                      {Array.from({ length: rowCount }, (_, idx) => {
                                        const baseRow = trusteesBase[idx];
                                        const trusteeMemberId = getAccountFormValue(
                                          `${account.id}.trust_info.trustees.${idx}.id`,
                                          baseRow
                                            ? getTrusteeMemberId(baseRow)
                                            : "",
                                        );
                                        const showRel =
                                          !trusteeMemberId ||
                                          !grantorIdSet.has(String(trusteeMemberId));
                                        const taken = new Set();
                                        for (let j = 0; j < rowCount; j++) {
                                          if (j === idx) continue;
                                          const oid = getAccountFormValue(
                                            `${account.id}.trust_info.trustees.${j}.id`,
                                            trusteesBase[j]
                                              ? getTrusteeMemberId(trusteesBase[j])
                                              : "",
                                          );
                                          if (oid) taken.add(String(oid));
                                        }
                                        const memberOpts =
                                          household.members
                                            ?.map((m) => ({
                                              value: m.id,
                                              label: `${m.personal_info?.first_name || ""} ${m.personal_info?.last_name || ""}`.trim(),
                                            }))
                                            .filter((o) => o.label)
                                            .filter(
                                              (o) =>
                                                !taken.has(String(o.value)) ||
                                                String(o.value) ===
                                                  String(trusteeMemberId || ""),
                                            ) || [];
                                        return (
                                          <div
                                            key={`${account.id}-trustee-${idx}-${trusteeMemberId || "empty"}`}
                                            className={`grid grid-cols-1 gap-4 ${showRel ? "md:grid-cols-2" : ""}`}
                                          >
                                            <DataField
                                              getFormValue={getAccountFormValue}
                                              label={`Trustee ${idx + 1}`}
                                              value={
                                                isEditingAccounts
                                                  ? trusteeMemberId || ""
                                                  : getMemberNameById(
                                                      trusteeMemberId,
                                                    )
                                              }
                                              isEditable={true}
                                              isEditing={isEditingAccounts}
                                              options={memberOpts}
                                              fieldPath={`${account.id}.trust_info.trustees.${idx}.id`}
                                              onFieldChange={
                                                handleAccountFieldChange
                                              }
                                            />
                                            {showRel && (
                                              <DataField
                                                getFormValue={getAccountFormValue}
                                                label="Relationship to Grantor"
                                                value={
                                                  baseRow?.relationship_to_grantor ||
                                                  ""
                                                }
                                                isEditable={true}
                                                isEditing={isEditingAccounts}
                                                options={RELATIONSHIP_OPTIONS}
                                                fieldPath={`${account.id}.trust_info.trustees.${idx}.relationship_to_grantor`}
                                                onFieldChange={
                                                  handleAccountFieldChange
                                                }
                                              />
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                    {isEditingAccounts && (
                                      <button
                                        type="button"
                                        disabled={!canAddMoreTrustees}
                                        title={
                                          canAddMoreTrustees
                                            ? ""
                                            : "Every household member is already assigned as a trustee."
                                        }
                                        onClick={() => {
                                          handleAccountFieldChange(
                                            `${account.id}.trust_info.trustees.${rowCount}.id`,
                                            "",
                                          );
                                          handleAccountFieldChange(
                                            `${account.id}.trust_info.trustees.${rowCount}.relationship_to_grantor`,
                                            "",
                                          );
                                        }}
                                        className="mt-3 px-3 py-2 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                                      >
                                        + Add trustee
                                      </button>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        )}

                      {/* ══ Business Info (LLC) ══ */}
                      {section === "business_info" &&
                        account.registration?.toLowerCase() === "business" && (
                          <div data-section={`business_info_${aid}`}>
                            <div>
                              {(() => {
                                const biz = account.business_info || {};
                                const bizAddr = biz.address || {};
                                const taxOptions = getTaxClassificationOptions(
                                  getAccountFormValue(`${account.id}.type`, account.type),
                                );
                                return (
                                  <>
                                    <div className="grid grid-cols-2 gap-4">
                                      <DataField
                                        getFormValue={getAccountFormValue}
                                        label="Business Name"
                                        value={biz.business_name}
                                        isEditable={true}
                                        isEditing={isEditingAccounts}
                                        fieldPath={`${account.id}.business_info.business_name`}
                                        onFieldChange={handleAccountFieldChange}
                                      />
                                      {taxOptions.length > 0 && (
                                        <DataField
                                          getFormValue={getAccountFormValue}
                                          label="Tax Classification"
                                          value={biz.tax_classification}
                                          isEditable={true}
                                          isEditing={isEditingAccounts}
                                          options={taxOptions}
                                          fieldPath={`${account.id}.business_info.tax_classification`}
                                          onFieldChange={handleAccountFieldChange}
                                        />
                                      )}
                                      <DataField
                                        getFormValue={getAccountFormValue}
                                        label="Is Operating"
                                        value={
                                          biz.is_operating === true
                                            ? "Yes"
                                            : biz.is_operating === false
                                              ? "No"
                                              : ""
                                        }
                                        isEditable={true}
                                        isEditing={isEditingAccounts}
                                        options={[
                                          { value: "true", label: "Yes" },
                                          { value: "false", label: "No" },
                                        ]}
                                        fieldPath={`${account.id}.business_info.is_operating`}
                                        onFieldChange={handleAccountFieldChange}
                                      />
                                      <DataField
                                        getFormValue={getAccountFormValue}
                                        label="Non-Profit"
                                        value={
                                          biz.is_non_profit === true
                                            ? "Yes"
                                            : biz.is_non_profit === false
                                              ? "No"
                                              : ""
                                        }
                                        isEditable={true}
                                        isEditing={isEditingAccounts}
                                        options={[
                                          { value: "true", label: "Yes" },
                                          { value: "false", label: "No" },
                                        ]}
                                        fieldPath={`${account.id}.business_info.is_non_profit`}
                                        onFieldChange={handleAccountFieldChange}
                                      />
                                      <DataField
                                        getFormValue={getAccountFormValue}
                                        label="Address Line 1"
                                        value={bizAddr.address_line_1}
                                        isEditable={true}
                                        isEditing={isEditingAccounts}
                                        fieldPath={`${account.id}.business_info.address.address_line_1`}
                                        onFieldChange={handleAccountFieldChange}
                                      />
                                      <DataField
                                        getFormValue={getAccountFormValue}
                                        label="Address Line 2"
                                        value={bizAddr.address_line_2}
                                        isEditable={true}
                                        isEditing={isEditingAccounts}
                                        fieldPath={`${account.id}.business_info.address.address_line_2`}
                                        onFieldChange={handleAccountFieldChange}
                                      />
                                    </div>
                                    {isEditingAccounts ? (
                                      <div className="mt-4 bg-emerald-50/50 p-4 rounded-xl border border-emerald-100">
                                        <StateCitySelect
                                          countryValue={getAccountFormValue(
                                            `${account.id}.business_info.address.country`,
                                            bizAddr.country || "USA",
                                          )}
                                          stateValue={getAccountFormValue(
                                            `${account.id}.business_info.address.province`,
                                            bizAddr.province || "",
                                          )}
                                          cityValue={getAccountFormValue(
                                            `${account.id}.business_info.address.city`,
                                            bizAddr.city || "",
                                          )}
                                          onCountryChange={(val) => {
                                            handleAccountFieldChange(
                                              `${account.id}.business_info.address.country`,
                                              val,
                                            );
                                            handleAccountFieldChange(
                                              `${account.id}.business_info.address.province`,
                                              "",
                                            );
                                            handleAccountFieldChange(
                                              `${account.id}.business_info.address.city`,
                                              "",
                                            );
                                          }}
                                          onStateChange={(val) => {
                                            handleAccountFieldChange(
                                              `${account.id}.business_info.address.province`,
                                              val,
                                            );
                                            handleAccountFieldChange(
                                              `${account.id}.business_info.address.country`,
                                              getAccountFormValue(
                                                `${account.id}.business_info.address.country`,
                                                bizAddr.country,
                                              ) || "USA",
                                            );
                                          }}
                                          onCityChange={(val) => {
                                            handleAccountFieldChange(
                                              `${account.id}.business_info.address.city`,
                                              val,
                                            );
                                            handleAccountFieldChange(
                                              `${account.id}.business_info.address.country`,
                                              getAccountFormValue(
                                                `${account.id}.business_info.address.country`,
                                                bizAddr.country,
                                              ) || "USA",
                                            );
                                          }}
                                          isEditing={true}
                                          stateLabel="Province/State"
                                          cityLabel="City"
                                          zipValue={getAccountFormValue(
                                            `${account.id}.business_info.address.postal_code`,
                                            bizAddr.postal_code || "",
                                          )}
                                          onZipChange={(val) =>
                                            handleAccountFieldChange(
                                              `${account.id}.business_info.address.postal_code`,
                                              val,
                                            )
                                          }
                                          zipLabel="Postal Code"
                                          zipRestrictUsFive
                                          zipError={
                                            validationErrors[
                                              `${account.id}.business_info.address.postal_code`
                                            ]
                                          }
                                          onZipValidationError={(msg) =>
                                            handleFieldError(
                                              `${account.id}.business_info.address.postal_code`,
                                              msg,
                                            )
                                          }
                                        />
                                      </div>
                                    ) : (
                                      <div className="grid grid-cols-2 gap-4">
                                        <DataField
                                          label="Country"
                                          value={bizAddr.country}
                                          isEditable={false}
                                          isEditing={false}
                                        />
                                        <DataField
                                          label="Province/State"
                                          value={bizAddr.province}
                                          isEditable={false}
                                          isEditing={false}
                                        />
                                        <DataField
                                          label="City"
                                          value={bizAddr.city}
                                          isEditable={false}
                                          isEditing={false}
                                        />
                                        <DataField
                                          label="Postal Code"
                                          value={bizAddr.postal_code}
                                          isEditable={false}
                                          isEditing={false}
                                        />
                                      </div>
                                    )}
                                    <div className="grid grid-cols-2 gap-4 mt-2">
                                      <DataField
                                        getFormValue={getAccountFormValue}
                                        label="Phone"
                                        value={bizAddr.phone || "N/A"}
                                        isEditable={true}
                                        isEditing={isEditingAccounts}
                                        type="phone"
                                        fieldPath={`${account.id}.business_info.address.phone`}
                                        onFieldChange={handleAccountFieldChange}
                                        error={
                                          validationErrors[
                                            `${account.id}.business_info.address.phone`
                                          ]
                                        }
                                        onFieldError={handleFieldError}
                                      />
                                      <DataField
                                        getFormValue={getAccountFormValue}
                                        label="Business Tax ID (EIN)"
                                        value={biz.ssn}
                                        isEditable={true}
                                        isEditing={isEditingAccounts}
                                        type="ssn"
                                        ssnLengthOnly
                                        fieldPath={`${account.id}.business_info.ssn`}
                                        onFieldChange={handleAccountFieldChange}
                                        error={
                                          validationErrors[
                                            `${account.id}.business_info.ssn`
                                          ]
                                        }
                                        onFieldError={handleFieldError}
                                      />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 mt-4 items-start">
                                      <MemberSelect
                                        multi
                                        label="Control Persons"
                                        members={household.members || []}
                                        isEditing={isEditingAccounts}
                                        value={getAccountFormValue(
                                          `${account.id}.business_info.control_persons`,
                                          Array.isArray(biz.control_persons)
                                            ? biz.control_persons
                                            : [],
                                        )}
                                        onChange={(next) =>
                                          handleAccountFieldChange(
                                            `${account.id}.business_info.control_persons`,
                                            next,
                                          )
                                        }
                                        addLabel="+ Add Control Person"
                                        maxEntries={4}
                                      />
                                      <div>
                                      <div className="flex items-center justify-between mb-1.5">
                                        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Notes</label>
                                        {isEditingAccounts && (
                                          <span className={`text-[11px] tabular-nums ${
                                            ((getAccountFormValue(`${account.id}.business_info.notes`, biz.notes) || "").length) >= 950
                                              ? "text-red-500 font-semibold"
                                              : "text-gray-400"
                                          }`}>
                                            {(getAccountFormValue(`${account.id}.business_info.notes`, biz.notes) || "").length}/1000
                                          </span>
                                        )}
                                      </div>
                                      {isEditingAccounts ? (
                                        <textarea
                                          value={getAccountFormValue(`${account.id}.business_info.notes`, biz.notes) || ""}
                                          onChange={(e) => handleAccountFieldChange(`${account.id}.business_info.notes`, e.target.value)}
                                          maxLength={1000}
                                          rows={5}
                                          placeholder="Add notes about this business account"
                                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 resize-y overflow-y-auto shadow-inner focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/25 placeholder:text-gray-400 min-h-[100px] max-h-[280px]"
                                        />
                                      ) : (
                                        <div className="min-h-[60px] max-h-[200px] overflow-y-auto rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2.5 text-sm text-gray-800 whitespace-pre-wrap">
                                          {biz.notes || <span className="text-gray-400 italic">—</span>}
                                        </div>
                                      )}
                                      </div>
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        )}

                      {/* ══ Inheritance Info (Inherited IRA) ══ */}
                      {section === "inheritance_info" &&
                        account.registration?.toLowerCase() === "inherited retirement" &&
                        (account.type === "Inherited IRA" ||
                          account.type === "Inherited Roth IRA") && (
                          <div data-section={`inheritance_info_${aid}`}>
                            {(() => {
                              const dec = account.inheritance_info?.decedent_info || {};
                              const decId = getAccountFormValue(
                                `${account.id}.inheritance_info.decedent_info.id`,
                                dec.id || "",
                              );
                              return (
                                <div className="grid grid-cols-2 gap-4">
                                  <MemberSelect
                                    label="Decedent"
                                    members={household.members || []}
                                    isEditing={isEditingAccounts}
                                    value={decId}
                                    onChange={(id) =>
                                      handleAccountFieldChange(
                                        `${account.id}.inheritance_info.decedent_info.id`,
                                        id,
                                      )
                                    }
                                  />
                                  <DataField
                                    getFormValue={getAccountFormValue}
                                    label="Owner's Relationship to Decedent"
                                    value={dec.relationship}
                                    isEditable={true}
                                    isEditing={isEditingAccounts}
                                    options={RELATIONSHIP_OPTIONS}
                                    fieldPath={`${account.id}.inheritance_info.decedent_info.relationship`}
                                    onFieldChange={handleAccountFieldChange}
                                  />
                                </div>
                              );
                            })()}
                          </div>
                        )}

                      {/* ══ Other Info ══ */}
                      {section === "other_info" &&
                        isSpecialRegistration(account.registration) && (

                          <div data-section={`other_info_${aid}`}>
                            <div>
                              {(() => {
                                const otherInfo = account.other_info || {};
                                const otherOwners = Array.isArray(otherInfo.owners) ? otherInfo.owners : [];
                                const memberNameOptions =
                                  household.members
                                    ?.map((m) => `${m.personal_info?.first_name || ""} ${m.personal_info?.last_name || ""}`.trim())
                                    .filter(Boolean) || [];
                                return (
                                  <>
                                    {/* Owners */}
                                    <div>
                                      <h5 className="font-medium text-gray-800 mb-2">
                                        Owners
                                      </h5>
                                      <div className="space-y-2">
                                        {(() => {
                                          const formOwners = getAccountFormValue(
                                            `${account.id}.other_info.owners`,
                                            null,
                                          );
                                          const displayOwners =
                                            formOwners && typeof formOwners === "object"
                                              ? (() => {
                                                  const formKeys = Object.keys(formOwners).map(Number).filter((n) => !isNaN(n));
                                                  const len = formOwners._length !== undefined
                                                    ? formOwners._length
                                                    : Math.max(otherOwners.length - 1, ...formKeys) + 1;
                                                  return Array.from(
                                                    { length: len },
                                                    (_, i) => ({
                                                      full_name:
                                                        formOwners[i]?.full_name !== undefined
                                                          ? formOwners[i].full_name
                                                          : otherOwners[i]?.full_name || "",
                                                    }),
                                                  );
                                                })()
                                              : otherOwners;
                                          return displayOwners.map((owner, idx) => (
                                            <div key={idx} className="flex items-end gap-2">
                                              <div className="flex-1">
                                                <DataField
                                                  getFormValue={getAccountFormValue}
                                                  label={`Owner ${idx + 1}`}
                                                  value={owner.full_name}
                                                  isEditable={true}
                                                  isEditing={isEditingAccounts}
                                                  options={memberNameOptions}
                                                  allowCustom
                                                  fieldPath={`${account.id}.other_info.owners.${idx}.full_name`}
                                                  onFieldChange={handleAccountFieldChange}
                                                />
                                              </div>
                                              {isEditingAccounts && displayOwners.length > 1 && (
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    const remaining = displayOwners.filter((_, i) => i !== idx);
                                                    const rebuilt = {};
                                                    remaining.forEach((o, i) => {
                                                      rebuilt[i] = { full_name: o.full_name };
                                                    });
                                                    rebuilt._length = remaining.length;
                                                    setAccountsFormData((prev) => {
                                                      const acct = { ...(prev[account.id] || {}) };
                                                      return { ...prev, [account.id]: { ...acct, other_info: { ...(acct.other_info || {}), owners: rebuilt } } };
                                                    });
                                                  }}
                                                  className="mb-3 p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded hover:bg-red-50"
                                                  title="Remove owner"
                                                >
                                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                  </svg>
                                                </button>
                                              )}
                                            </div>
                                          ));
                                        })()}
                                        {isEditingAccounts && (
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const currentOwners =
                                                getAccountFormValue(
                                                  `${account.id}.other_info.owners`,
                                                  null,
                                                );
                                              let newIdx;
                                              if (currentOwners && currentOwners._length !== undefined) {
                                                newIdx = currentOwners._length;
                                              } else {
                                                const baseLen = otherOwners.length;
                                                const maxExisting = currentOwners
                                                  ? Math.max(
                                                      baseLen - 1,
                                                      ...Object.keys(currentOwners)
                                                        .map(Number)
                                                        .filter((n) => !isNaN(n)),
                                                    )
                                                  : baseLen - 1;
                                                newIdx = maxExisting + 1;
                                              }
                                              handleAccountFieldChange(
                                                `${account.id}.other_info.owners.${newIdx}.full_name`,
                                                "",
                                              );
                                              setAccountsFormData((prev) => {
                                                const acct = { ...(prev[account.id] || {}) };
                                                const oi = { ...(acct.other_info || {}) };
                                                const ow = { ...(oi.owners || {}) };
                                                ow._length = newIdx + 1;
                                                return { ...prev, [account.id]: { ...acct, other_info: { ...oi, owners: ow } } };
                                              });
                                            }}
                                            className="text-sm text-emerald-600 hover:text-emerald-700 font-medium mt-1"
                                          >
                                            + Add Owner
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    {/* Estate Info (estate accounts only) */}
                                    {ESTATE_ACCOUNT_TYPES.includes(account.type) && (
                                      <div className="mt-4 pt-4 border-t border-gray-200">
                                        <h5 className="font-medium text-gray-800 mb-2">Executors (max 2)</h5>
                                        {(() => {
                                          const formExecutors = getAccountFormValue(
                                            `${account.id}.estate_info.executors`,
                                            account.estate_info?.executors,
                                          );
                                          const baseRows = Array.isArray(formExecutors) ? formExecutors : [];
                                          const rows = baseRows.length > 0 ? baseRows : [{ id: "" }];
                                          const setExec = (next) =>
                                            handleAccountFieldChange(
                                              `${account.id}.estate_info.executors`,
                                              next,
                                            );
                                          const updateAt = (idx, value) =>
                                            setExec(rows.map((r, i) => (i === idx ? { ...r, id: value } : r)));
                                          const removeAt = (idx) => setExec(rows.filter((_, i) => i !== idx));
                                          const addRow = () => setExec([...rows, { id: "" }]);
                                          const assignedIds = new Set(rows.map((r) => String(r?.id || "")).filter(Boolean));
                                          const canAdd =
                                            rows.length < 2 &&
                                            (household.members || []).filter((m) => !assignedIds.has(String(m.id))).length > 0;
                                          return !isEditingAccounts ? (
                                            <div className="text-gray-900">
                                              {rows.filter((r) => r?.id).length === 0 ? (
                                                <div className="text-gray-400">—</div>
                                              ) : (
                                                rows
                                                  .filter((r) => r?.id)
                                                  .map((r, idx) => (
                                                    <div key={`exec-ro-${idx}`}>{getMemberNameById(r.id) || "—"}</div>
                                                  ))
                                              )}
                                            </div>
                                          ) : (
                                            <div className="space-y-2">
                                              {rows.map((row, idx) => (
                                                <div key={`exec-${idx}`} className="flex gap-2 items-end">
                                                  <select
                                                    value={row.id || ""}
                                                    onChange={(e) => updateAt(idx, e.target.value)}
                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                                  >
                                                    <option value="">Select member...</option>
                                                    {(household.members || [])
                                                      .filter((m) => !rows.some((r, i) => i !== idx && String(r?.id || "") === String(m.id)))
                                                      .map((m) => (
                                                        <option key={m.id} value={m.id}>
                                                          {m.personal_info?.first_name || ""} {m.personal_info?.last_name || ""}
                                                        </option>
                                                      ))}
                                                  </select>
                                                  {rows.length > 1 && (
                                                    <button
                                                      type="button"
                                                      onClick={() => removeAt(idx)}
                                                      className="px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-sm"
                                                    >Remove</button>
                                                  )}
                                                </div>
                                              ))}
                                              {canAdd && (
                                                <button
                                                  type="button"
                                                  onClick={addRow}
                                                  className="px-3 py-2 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm"
                                                >+ Add Executor</button>
                                              )}
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    )}
                                    {/* Note */}
                                    <div className="mt-4">
                                      <div className="flex items-center justify-between mb-1.5">
                                        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Note</label>
                                        {isEditingAccounts && (
                                          <span className={`text-[11px] tabular-nums ${
                                            ((getAccountFormValue(`${account.id}.other_info.note`, otherInfo.note) || "").length) >= 950
                                              ? "text-red-500 font-semibold"
                                              : "text-gray-400"
                                          }`}>
                                            {(getAccountFormValue(`${account.id}.other_info.note`, otherInfo.note) || "").length}/1000
                                          </span>
                                        )}
                                      </div>
                                      {isEditingAccounts ? (
                                        <textarea
                                          value={getAccountFormValue(`${account.id}.other_info.note`, otherInfo.note) || ""}
                                          onChange={(e) => handleAccountFieldChange(`${account.id}.other_info.note`, e.target.value)}
                                          maxLength={1000}
                                          rows={5}
                                          placeholder="Add notes about this account"
                                          className="w-3/5 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 resize-y overflow-y-auto shadow-inner focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/25 placeholder:text-gray-400 min-h-[100px] max-h-[280px]"
                                        />
                                      ) : (
                                        <div className="min-h-[60px] max-h-[200px] overflow-y-auto rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2.5 text-sm text-gray-800 whitespace-pre-wrap">
                                          {otherInfo.note || <span className="text-gray-400 italic">—</span>}
                                        </div>
                                      )}
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        )}

                      {section === "beneficiaries" && (
                        <div data-section={`beneficiaries_${aid}`}>
                          {(beneficiaryValidationErrorsByAccount[aid]?.length > 0 ||
                            beneficiaryValidationErrorsByAccount[account.id]?.length > 0) && (
                            <div
                              className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg"
                              role="alert"
                            >
                              <p className="text-sm font-medium text-red-800 mb-1.5">
                                Cannot save
                              </p>
                              <ul className="text-sm text-red-700 list-disc list-inside space-y-0.5">
                                {(beneficiaryValidationErrorsByAccount[aid] ||
                                  beneficiaryValidationErrorsByAccount[account.id] ||
                                  []
                                ).map((line, i) => (
                                  <li key={i}>{line}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {(() => {
                            const visibleBeneficiaries = (
                              account.beneficiaries || []
                            )
                              .map((b, idx) => ({ b, originalIdx: idx }))
                              .filter(
                                ({ b }) =>
                                  !(
                                    removedBeneficiaries[account.id] || []
                                  ).includes(b.id),
                              );

                            const primaryTotal = visibleBeneficiaries.reduce(
                              (sum, { b, originalIdx }) => {
                                const formType =
                                  accountsFormData[account.id]?.beneficiaries?.[
                                    originalIdx
                                  ]?.type;
                                const type =
                                  formType !== undefined ? formType : b.type;
                                if (type !== "primary") return sum;
                                const formShare =
                                  accountsFormData[account.id]?.beneficiaries?.[
                                    originalIdx
                                  ]?.share;
                                const share =
                                  formShare !== undefined
                                    ? parseFloat(formShare)
                                    : b.share || 0;
                                return sum + (isNaN(share) ? 0 : share);
                              },
                              0,
                            );

                            const contingentTotal = visibleBeneficiaries.reduce(
                              (sum, { b, originalIdx }) => {
                                const formType =
                                  accountsFormData[account.id]?.beneficiaries?.[
                                    originalIdx
                                  ]?.type;
                                const type =
                                  formType !== undefined ? formType : b.type;
                                if (type !== "contingent") return sum;
                                const formShare =
                                  accountsFormData[account.id]?.beneficiaries?.[
                                    originalIdx
                                  ]?.share;
                                const share =
                                  formShare !== undefined
                                    ? parseFloat(formShare)
                                    : b.share || 0;
                                return sum + (isNaN(share) ? 0 : share);
                              },
                              0,
                            );

                            const hasPrimary = visibleBeneficiaries.some(
                              ({ b, originalIdx }) => {
                                const formType =
                                  accountsFormData[account.id]?.beneficiaries?.[
                                    originalIdx
                                  ]?.type;
                                return (
                                  (formType !== undefined
                                    ? formType
                                    : b.type) === "primary"
                                );
                              },
                            );
                            const hasContingent = visibleBeneficiaries.some(
                              ({ b, originalIdx }) => {
                                const formType =
                                  accountsFormData[account.id]?.beneficiaries?.[
                                    originalIdx
                                  ]?.type;
                                return (
                                  (formType !== undefined
                                    ? formType
                                    : b.type) === "contingent"
                                );
                              },
                            );

                            return (
                              <div key="beneficiaries-inner">
                                {visibleBeneficiaries.length > 0 ? (
                                  <span className="text-sm">
                                    {hasPrimary && (
                                      <span
                                        className={
                                          isBeneficiaryShareTotalOver100(primaryTotal)
                                            ? "text-red-500"
                                            : isBeneficiaryShareTotalComplete(primaryTotal)
                                              ? "text-emerald-600"
                                              : "text-amber-600"
                                        }
                                      >
                                        Primary: {primaryTotal}%
                                      </span>
                                    )}
                                    {hasPrimary && hasContingent && (
                                      <span className="text-gray-400 mx-1">
                                        |
                                      </span>
                                    )}
                                    {hasContingent && (
                                      <span
                                        className={
                                          isBeneficiaryShareTotalOver100(contingentTotal)
                                            ? "text-red-500"
                                            : isBeneficiaryShareTotalComplete(contingentTotal)
                                              ? "text-emerald-600"
                                              : "text-amber-600"
                                        }
                                      >
                                        Contingent: {contingentTotal}%
                                      </span>
                                    )}
                                  </span>
                                ) : null}
                                <div>
                                  {account.beneficiaries &&
                                  account.beneficiaries.length > 0 ? (
                                    <div className="space-y-4">
                                      {(() => {
                                        const visibleBeneficiaries =
                                          account.beneficiaries
                                            .map((beneficiary, idx) => ({
                                              beneficiary,
                                              originalIdx: idx,
                                            }))
                                            .filter(
                                              ({ beneficiary }) =>
                                                !(
                                                  removedBeneficiaries[
                                                    account.id
                                                  ] || []
                                                ).includes(beneficiary.id),
                                            );

                                        return visibleBeneficiaries.map(
                                          ({ beneficiary, originalIdx }) => (
                                            <BeneficiaryCard
                                              key={beneficiary.id}
                                              beneficiary={beneficiary}
                                              originalIdx={originalIdx}
                                              accountId={account.id}
                                              isEditingAccounts={
                                                isEditingAccounts
                                              }
                                              accountsFormData={
                                                accountsFormData
                                              }
                                              handleAccountFieldChange={
                                                handleAccountFieldChange
                                              }
                                              getMemberNameById={
                                                getMemberNameById
                                              }
                                              household={household}
                                              accountOwnersInfo={
                                                account.owners_info
                                              }
                                              handleRemoveBeneficiary={
                                                handleRemoveBeneficiary
                                              }
                                            />
                                          ),
                                        );
                                      })()}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setIsEditingAccounts(true);
                                          handleAddBeneficiaryForAccount(
                                            account.id,
                                          );
                                        }}
                                        className="w-full px-4 py-2 border-2 border-dashed border-teal-300 text-teal-600 hover:bg-teal-50 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                      >
                                        <svg
                                          className="w-5 h-5"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                          />
                                        </svg>
                                        Add Beneficiary
                                      </button>
                                    </div>
                                  ) : (
                                    <div>
                                      <div className="text-center py-4 text-gray-500 text-sm">
                                        No beneficiaries for this account.
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setIsEditingAccounts(true);
                                          handleAddBeneficiaryForAccount(
                                            account.id,
                                          );
                                        }}
                                        className="w-full mt-2 px-4 py-2 border-2 border-dashed border-teal-300 text-teal-600 hover:bg-teal-50 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                      >
                                        <svg
                                          className="w-5 h-5"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                          />
                                        </svg>
                                        Add Beneficiary
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {section === "trusted_contacts" && (
                        <div data-section={`trusted_contacts_${aid}`}>
                          {(beneficiaryValidationErrorsByAccount[aid]?.length > 0 ||
                            beneficiaryValidationErrorsByAccount[account.id]?.length > 0) && (
                            <div
                              className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg"
                              role="alert"
                            >
                              <p className="text-sm font-medium text-red-800 mb-1.5">
                                Cannot save
                              </p>
                              <ul className="text-sm text-red-700 list-disc list-inside space-y-0.5">
                                {(beneficiaryValidationErrorsByAccount[aid] ||
                                  beneficiaryValidationErrorsByAccount[account.id] ||
                                  []
                                ).map((line, i) => (
                                  <li key={i}>{line}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <div>
                            {account.trusted_contacts &&
                            account.trusted_contacts.length > 0 ? (
                              <div className="space-y-4">
                                {(() => {
                                  // Pre-calculate original indices to avoid recalculation on every render
                                  const visibleTrustedContacts =
                                    account.trusted_contacts
                                      .map((tc, idx) => ({
                                        tc,
                                        originalIdx: idx,
                                      }))
                                      .filter(
                                        ({ tc }) =>
                                          !(
                                            removedTrustedContacts[
                                              account.id
                                            ] || []
                                          ).includes(tc.id),
                                      );

                                  return visibleTrustedContacts.map(
                                    ({ tc, originalIdx }) => (
                                      <TrustedContactCard
                                        key={tc.id}
                                        tc={tc}
                                        originalIdx={originalIdx}
                                        accountId={account.id}
                                        isEditingAccounts={isEditingAccounts}
                                        accountsFormData={accountsFormData}
                                        handleAccountFieldChange={
                                          handleAccountFieldChange
                                        }
                                        getMemberNameById={getMemberNameById}
                                        household={household}
                                        accountOwnersInfo={account.owners_info}
                                        handleRemoveTrustedContact={
                                          handleRemoveTrustedContact
                                        }
                                      />
                                    ),
                                  );
                                })()}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setIsEditingAccounts(true);
                                    handleAddTrustedContactForAccount(
                                      account.id,
                                    );
                                  }}
                                  className="w-full px-4 py-2 border-2 border-dashed border-indigo-300 text-indigo-600 hover:bg-indigo-50 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                >
                                  <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                    />
                                  </svg>
                                  Add Trusted Contact
                                </button>
                              </div>
                            ) : (
                              <div>
                                <div className="text-center py-4 text-gray-500 text-sm">
                                  No trusted contacts for this account.
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setIsEditingAccounts(true);
                                    handleAddTrustedContactForAccount(
                                      account.id,
                                    );
                                  }}
                                  className="w-full mt-2 px-4 py-2 border-2 border-dashed border-indigo-300 text-indigo-600 hover:bg-indigo-50 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                >
                                  <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                    />
                                  </svg>
                                  Add Trusted Contact
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {section === "contributions" && (
                        <div data-section={`contributions_${aid}`}>
                          <div>
                            {(() => {
                              const accountId = account.id || index;
                              const serverDeposits = account.deposits || [];
                              const removedDepositSet = new Set(
                                removedDeposits[account.id] || [],
                              );
                              const filteredServerDeposits = serverDeposits
                                .map((d, originalIdx) => ({
                                  deposit: d,
                                  originalIdx,
                                }))
                                .filter(
                                  ({ deposit }) =>
                                    !removedDepositSet.has(deposit.id),
                                );
                              const pendingDeposits =
                                pendingNewDeposits[account.id] || [];
                              const displayList = [
                                ...filteredServerDeposits.map(
                                  ({ deposit, originalIdx }) => ({
                                    deposit,
                                    originalIdx,
                                    isPending: false,
                                  }),
                                ),
                                ...pendingDeposits.map(
                                  (deposit, pendingIndex) => ({
                                    deposit,
                                    pendingIndex,
                                    isPending: true,
                                  }),
                                ),
                              ];
                              return displayList.length > 0 ? (
                                <div className="space-y-2">
                                  {displayList.map((item, idx) => {
                                    const { deposit, isPending } = item;
                                    const originalIdx = item.originalIdx;
                                    const pendingIndex = item.pendingIndex;
                                    const sectionKey = `deposit_${idx}`;
                                    return (
                                      <CollapsibleSection
                                        key={
                                          isPending
                                            ? `pending-${pendingIndex}`
                                            : deposit.id
                                        }
                                        title={`Deposit #${idx + 1}${isPending ? " (unsaved)" : ""}`}
                                        isExpanded={
                                          expandedAccountSections[
                                            `${accountId}_${sectionKey}`
                                          ]
                                        }
                                        onToggle={() =>
                                          setExpandedAccountSections(
                                            (prev) => ({
                                              ...prev,
                                              [`${accountId}_${sectionKey}`]:
                                                !prev[
                                                  `${accountId}_${sectionKey}`
                                                ],
                                            }),
                                          )
                                        }
                                        icon={
                                          <svg
                                            className="w-4 h-4 text-green-600"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M7 16V4m0 0L3 8m4-4l4 4"
                                            />
                                          </svg>
                                        }
                                      >
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="text-sm text-gray-500" />
                                          {isEditingAccounts && (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                isPending
                                                  ? handleRemovePendingDeposit(
                                                      account.id,
                                                      pendingIndex,
                                                    )
                                                  : handleDeleteDeposit(
                                                      account.id,
                                                      deposit.id,
                                                    )
                                              }
                                              className="p-1 text-red-600 hover:bg-red-50 hover:text-red-700 rounded transition-colors"
                                              title={
                                                isPending
                                                  ? "Remove unsaved contribution"
                                                  : "Delete contribution"
                                              }
                                            >
                                              <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth={2}
                                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                />
                                              </svg>
                                            </button>
                                          )}
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                          {isPending ? (
                                            <>
                                              <DataField
                                                label="Amount"
                                                value={formatCurrencyPreservingScale(
                                                  deposit.amount,
                                                )}
                                                isEditable={false}
                                              />
                                              <DataField
                                                label="Frequency"
                                                value={deposit.frequency}
                                                isEditable={false}
                                              />
                                              <DataField
                                                label="Start Date"
                                                value={
                                                  deposit.start_date
                                                    ? formatDate(
                                                        deposit.start_date,
                                                      )
                                                    : "N/A"
                                                }
                                                isEditable={false}
                                              />
                                              <DataField
                                                label="Method"
                                                value={deposit.method}
                                                isEditable={false}
                                              />
                                            </>
                                          ) : (
                                            <>
                                              <DataField
                                                getFormValue={
                                                  getAccountFormValue
                                                }
                                                label="Amount"
                                                value={formatCurrencyPreservingScale(
                                                  deposit.amount,
                                                )}
                                                isEditable={true}
                                                type="number"
                                                isEditing={isEditingAccounts}
                                                fieldPath={`${account.id}.deposits.${originalIdx}.amount`}
                                                onFieldChange={
                                                  handleAccountFieldChange
                                                }
                                                disableWheel={true}
                                              />
                                              <DataField
                                                getFormValue={
                                                  getAccountFormValue
                                                }
                                                label="Frequency"
                                                value={deposit.frequency}
                                                isEditable={true}
                                                isEditing={isEditingAccounts}
                                                options={FREQUENCY_OPTIONS}
                                                fieldPath={`${account.id}.deposits.${originalIdx}.frequency`}
                                                onFieldChange={
                                                  handleAccountFieldChange
                                                }
                                              />
                                              <DataField
                                                getFormValue={
                                                  getAccountFormValue
                                                }
                                                label="Start Date"
                                                value={formatDate(
                                                  deposit.start_date,
                                                )}
                                                dateValue={deposit.start_date}
                                                isEditable={true}
                                                type="date"
                                                isEditing={isEditingAccounts}
                                                fieldPath={`${account.id}.deposits.${originalIdx}.start_date`}
                                                onFieldChange={
                                                  handleAccountFieldChange
                                                }
                                                error={
                                                  validationErrors[
                                                    `${account.id}.deposits.${originalIdx}.start_date`
                                                  ]
                                                }
                                                onFieldError={handleFieldError}
                                                dateConstraint="schedule_future"
                                              />
                                              <DataField
                                                getFormValue={
                                                  getAccountFormValue
                                                }
                                                label="Method"
                                                value={deposit.method}
                                                isEditable={true}
                                                isEditing={isEditingAccounts}
                                                options={DEPOSIT_METHOD_OPTIONS}
                                                fieldPath={`${account.id}.deposits.${originalIdx}.method`}
                                                onFieldChange={
                                                  handleAccountFieldChange
                                                }
                                              />
                                            </>
                                          )}
                                        </div>
                                      </CollapsibleSection>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="text-center py-6 text-gray-500">
                                  No contributions found for this account.
                                </div>
                              );
                            })()}
                            <div className="mt-4 text-center">
                              <button
                                type="button"
                                onClick={() => {
                                  setIsEditingAccounts(true);
                                  setAccountForMoneyMovement(account);
                                  setShowAddContributionModal(true);
                                }}
                                className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 4v16m8-8H4"
                                  />
                                </svg>
                                Add Contribution
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {section === "withdrawals" && (
                        <div data-section={`withdrawals_${aid}`}>
                          <div>
                            {(() => {
                              const accountId = account.id || index;
                              const serverWithdrawals =
                                account.withdrawals || [];
                              const removedWithdrawalSet = new Set(
                                removedWithdrawals[account.id] || [],
                              );
                              const filteredServerWithdrawals =
                                serverWithdrawals
                                  .map((w, originalIdx) => ({
                                    withdrawal: w,
                                    originalIdx,
                                  }))
                                  .filter(
                                    ({ withdrawal }) =>
                                      !removedWithdrawalSet.has(withdrawal.id),
                                  );
                              const pendingWithdrawals =
                                pendingNewWithdrawals[account.id] || [];
                              const displayList = [
                                ...filteredServerWithdrawals.map(
                                  ({ withdrawal, originalIdx }) => ({
                                    withdrawal,
                                    originalIdx,
                                    isPending: false,
                                  }),
                                ),
                                ...pendingWithdrawals.map(
                                  (withdrawal, pendingIndex) => ({
                                    withdrawal,
                                    pendingIndex,
                                    isPending: true,
                                  }),
                                ),
                              ];
                              return displayList.length > 0 ? (
                                <div className="space-y-2">
                                  {displayList.map((item, idx) => {
                                    const { withdrawal, isPending } = item;
                                    const originalIdx = item.originalIdx;
                                    const pendingIndex = item.pendingIndex;
                                    const sectionKey = `withdrawal_${idx}`;
                                    return (
                                      <CollapsibleSection
                                        key={
                                          isPending
                                            ? `pending-w-${pendingIndex}`
                                            : withdrawal.id
                                        }
                                        title={`Withdrawal #${idx + 1}${isPending ? " (unsaved)" : ""}`}
                                        isExpanded={
                                          expandedAccountSections[
                                            `${accountId}_${sectionKey}`
                                          ]
                                        }
                                        onToggle={() =>
                                          setExpandedAccountSections(
                                            (prev) => ({
                                              ...prev,
                                              [`${accountId}_${sectionKey}`]:
                                                !prev[
                                                  `${accountId}_${sectionKey}`
                                                ],
                                            }),
                                          )
                                        }
                                        icon={
                                          <svg
                                            className="w-4 h-4 text-red-600"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M17 8l4 4m0 0l-4 4m4-4H3"
                                            />
                                          </svg>
                                        }
                                        headerAction={
                                          isEditingAccounts && (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                isPending
                                                  ? handleRemovePendingWithdrawal(
                                                      account.id,
                                                      pendingIndex,
                                                    )
                                                  : handleDeleteWithdrawal(
                                                      account.id,
                                                      withdrawal.id,
                                                    )
                                              }
                                              className="p-1 text-red-600 hover:bg-red-50 hover:text-red-700 rounded transition-colors"
                                              title={
                                                isPending
                                                  ? "Remove unsaved withdrawal"
                                                  : "Delete withdrawal"
                                              }
                                            >
                                              <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth={2}
                                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                />
                                              </svg>
                                            </button>
                                          )
                                        }
                                      >
                                        <div className="grid grid-cols-2 gap-4">
                                          {isPending ? (
                                            <>
                                              <DataField
                                                label="Type"
                                                value={withdrawal.type || ""}
                                                isEditable={false}
                                              />
                                              <DataField
                                                label="Amount"
                                                value={formatCurrencyPreservingScale(
                                                  withdrawal.amount,
                                                )}
                                                isEditable={false}
                                              />
                                              <DataField
                                                label="Federal Tax Withholding"
                                                value={
                                                  withdrawal.federal_tax_withholding !=
                                                  null
                                                    ? `${withdrawal.federal_tax_withholding}%`
                                                    : "N/A"
                                                }
                                                isEditable={false}
                                              />
                                              <DataField
                                                label="State Tax Withholding"
                                                value={
                                                  withdrawal.state_tax_withholding !=
                                                  null
                                                    ? `${withdrawal.state_tax_withholding}%`
                                                    : "N/A"
                                                }
                                                isEditable={false}
                                              />
                                              <DataField
                                                label="Frequency"
                                                value={withdrawal.frequency}
                                                isEditable={false}
                                              />
                                              <DataField
                                                label="Start Date"
                                                value={
                                                  withdrawal.start_date
                                                    ? formatDate(
                                                        withdrawal.start_date,
                                                      )
                                                    : "N/A"
                                                }
                                                isEditable={false}
                                              />
                                              <DataField
                                                label="Method"
                                                value={withdrawal.method}
                                                isEditable={false}
                                              />
                                              <DataField
                                                label="Destination Type"
                                                value={
                                                  withdrawal.destination_type
                                                }
                                                isEditable={false}
                                              />
                                              <DataField
                                                label="Destination Identifier"
                                                value={getDestinationIdentifierLabel(
                                                  household,
                                                  withdrawal.destination_type,
                                                  withdrawal.destination_identifier,
                                                )}
                                                isEditable={false}
                                              />
                                            </>
                                          ) : (
                                            <>
                                              <DataField
                                                getFormValue={
                                                  getAccountFormValue
                                                }
                                                label="Type"
                                                value={withdrawal.type || ""}
                                                isEditable={true}
                                                isEditing={isEditingAccounts}
                                                options={[
                                                  "Fixed Amount",
                                                  "RMD",
                                                  "Interest",
                                                  "Income Only",
                                                ]}
                                                fieldPath={`${account.id}.withdrawals.${originalIdx}.type`}
                                                onFieldChange={
                                                  handleAccountFieldChange
                                                }
                                              />
                                              <DataField
                                                getFormValue={
                                                  getAccountFormValue
                                                }
                                                label="Amount"
                                                value={formatCurrencyPreservingScale(
                                                  withdrawal.amount,
                                                )}
                                                isEditable={true}
                                                type="number"
                                                isEditing={isEditingAccounts}
                                                fieldPath={`${account.id}.withdrawals.${originalIdx}.amount`}
                                                onFieldChange={
                                                  handleAccountFieldChange
                                                }
                                                disableWheel={true}
                                              />
                                              <DataField
                                                getFormValue={
                                                  getAccountFormValue
                                                }
                                                label="Federal Tax Withholding"
                                                value={
                                                  withdrawal.federal_tax_withholding
                                                    ? `${withdrawal.federal_tax_withholding}%`
                                                    : "N/A"
                                                }
                                                isEditable={true}
                                                type="number"
                                                min={0}
                                                max={100}
                                                isEditing={isEditingAccounts}
                                                fieldPath={`${account.id}.withdrawals.${originalIdx}.federal_tax_withholding`}
                                                onFieldChange={
                                                  handleAccountFieldChange
                                                }
                                                disableWheel={true}
                                              />
                                              <DataField
                                                getFormValue={
                                                  getAccountFormValue
                                                }
                                                label="State Tax Withholding"
                                                value={
                                                  withdrawal.state_tax_withholding
                                                    ? `${withdrawal.state_tax_withholding}%`
                                                    : "N/A"
                                                }
                                                isEditable={true}
                                                type="number"
                                                min={0}
                                                max={100}
                                                isEditing={isEditingAccounts}
                                                fieldPath={`${account.id}.withdrawals.${originalIdx}.state_tax_withholding`}
                                                onFieldChange={
                                                  handleAccountFieldChange
                                                }
                                                disableWheel={true}
                                              />
                                              <DataField
                                                getFormValue={
                                                  getAccountFormValue
                                                }
                                                label="Frequency"
                                                value={withdrawal.frequency}
                                                isEditable={true}
                                                isEditing={isEditingAccounts}
                                                options={FREQUENCY_OPTIONS}
                                                fieldPath={`${account.id}.withdrawals.${originalIdx}.frequency`}
                                                onFieldChange={
                                                  handleAccountFieldChange
                                                }
                                              />
                                              <DataField
                                                getFormValue={
                                                  getAccountFormValue
                                                }
                                                label="Start Date"
                                                value={formatDate(
                                                  withdrawal.start_date,
                                                )}
                                                dateValue={
                                                  withdrawal.start_date
                                                }
                                                isEditable={true}
                                                type="date"
                                                isEditing={isEditingAccounts}
                                                fieldPath={`${account.id}.withdrawals.${originalIdx}.start_date`}
                                                onFieldChange={
                                                  handleAccountFieldChange
                                                }
                                                error={
                                                  validationErrors[
                                                    `${account.id}.withdrawals.${originalIdx}.start_date`
                                                  ]
                                                }
                                                onFieldError={handleFieldError}
                                                dateConstraint="schedule_future"
                                              />
                                              <DataField
                                                getFormValue={
                                                  getAccountFormValue
                                                }
                                                label="Method"
                                                value={withdrawal.method}
                                                isEditable={true}
                                                isEditing={isEditingAccounts}
                                                options={WITHDRAWAL_METHOD_OPTIONS}
                                                fieldPath={`${account.id}.withdrawals.${originalIdx}.method`}
                                                onFieldChange={
                                                  handleAccountFieldChange
                                                }
                                              />
                                              <DataField
                                                getFormValue={
                                                  getAccountFormValue
                                                }
                                                label="Destination Type"
                                                value={
                                                  withdrawal.destination_type
                                                }
                                                isEditable={true}
                                                isEditing={isEditingAccounts}
                                                options={[
                                                  "Bank Account",
                                                  "Brokerage Account",
                                                ]}
                                                fieldPath={`${account.id}.withdrawals.${originalIdx}.destination_type`}
                                                onFieldChange={
                                                  handleAccountFieldChange
                                                }
                                              />
                                              <div className="py-2">
                                                <span className="text-sm font-medium text-gray-500">
                                                  Destination Identifier
                                                </span>
                                                {isEditingAccounts ? (
                                                  getAccountFormValue(
                                                    `${account.id}.withdrawals.${originalIdx}.destination_type`,
                                                    withdrawal.destination_type,
                                                  ) === "Bank Account" ? (
                                                    // Modal-based selection for Bank Account
                                                    <div className="mt-1">
                                                      {(() => {
                                                        const selectedBankId =
                                                          getAccountFormValue(
                                                            `${account.id}.withdrawals.${originalIdx}.destination_identifier`,
                                                            withdrawal.destination_identifier,
                                                          );
                                                        const selectedBankLabel =
                                                          getWithdrawalBankAccountLabel(
                                                            selectedBankId,
                                                          );
                                                        return selectedBankLabel ? (
                                                          <div className="flex items-center gap-2">
                                                            <div className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900 text-sm">
                                                              {
                                                                selectedBankLabel
                                                              }
                                                            </div>
                                                            <button
                                                              type="button"
                                                              onClick={() =>
                                                                handleWithdrawalBankSelect(
                                                                  account.id,
                                                                  originalIdx,
                                                                )
                                                              }
                                                              className="px-3 py-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium border border-emerald-300 rounded-md hover:bg-emerald-50 transition-colors"
                                                            >
                                                              Change
                                                            </button>
                                                          </div>
                                                        ) : (
                                                          <button
                                                            type="button"
                                                            onClick={() =>
                                                              handleWithdrawalBankSelect(
                                                                account.id,
                                                                originalIdx,
                                                              )
                                                            }
                                                            className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md text-gray-500 hover:border-emerald-500 hover:text-emerald-600 transition-colors flex items-center justify-center gap-2 text-sm"
                                                          >
                                                            <svg
                                                              className="w-4 h-4"
                                                              fill="none"
                                                              stroke="currentColor"
                                                              viewBox="0 0 24 24"
                                                            >
                                                              <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                                                              />
                                                            </svg>
                                                            Select Bank Account
                                                          </button>
                                                        );
                                                      })()}
                                                    </div>
                                                  ) : (
                                                    // Dropdown for Brokerage Account
                                                    <select
                                                      value={
                                                        getAccountFormValue(
                                                          `${account.id}.withdrawals.${originalIdx}.destination_identifier`,
                                                          withdrawal.destination_identifier,
                                                        ) ?? ""
                                                      }
                                                      onChange={(e) =>
                                                        handleAccountFieldChange(
                                                          `${account.id}.withdrawals.${originalIdx}.destination_identifier`,
                                                          e.target.value,
                                                        )
                                                      }
                                                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                                    >
                                                      <option value="">
                                                        Select destination
                                                      </option>
                                                      {getDestinationIdentifierOptions(
                                                        household,
                                                        getAccountFormValue(
                                                          `${account.id}.withdrawals.${originalIdx}.destination_type`,
                                                          withdrawal.destination_type,
                                                        ),
                                                        account.id,
                                                      ).map((opt) => (
                                                        <option
                                                          key={opt.value}
                                                          value={opt.value}
                                                        >
                                                          {opt.label}
                                                        </option>
                                                      ))}
                                                    </select>
                                                  )
                                                ) : (
                                                  <p className="text-sm text-gray-900 mt-1">
                                                    {getDestinationIdentifierLabel(
                                                      household,
                                                      withdrawal.destination_type,
                                                      withdrawal.destination_identifier,
                                                    ) || "N/A"}
                                                  </p>
                                                )}
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      </CollapsibleSection>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="text-center py-6 text-gray-500">
                                  No withdrawals found for this account.
                                </div>
                              );
                            })()}
                            <div className="mt-4 text-center">
                              <button
                                type="button"
                                onClick={() => {
                                  setIsEditingAccounts(true);
                                  setAccountForMoneyMovement(account);
                                  setShowAddWithdrawalModal(true);
                                }}
                                className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 4v16m8-8H4"
                                  />
                                </svg>
                                Add Withdrawal
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {section === "bank_account_details" && (
                        <div data-section={`bank_account_details_${aid}`}>
                          <div>
                            {(() => {
                              const ops = getBankOpsForAccount(account.id);
                              const isPendingChange = hasUnsavedBankChanges(ops);
                              const visibleBanks = resolveVisibleBanks(
                                account,
                                household?.bank_accounts || [],
                                ops,
                              );
                              const pendingIds = {
                                added: ops.addExisting || [],
                                removed: ops.remove || [],
                              };

                              return (
                                <div className="space-y-4">
                                  {isPendingChange && (
                                    <UnsavedChangesBanner
                                      message={advisorAccountUnsavedMessage(
                                        "bank_account_details",
                                      )}
                                    />
                                  )}
                                  {isEditingAccounts && !isPendingChange && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                                      <div className="flex items-start gap-2">
                                        <svg
                                          className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                          />
                                        </svg>
                                        <p>
                                          Use the Bank Accounts tab to edit bank details.
                                          You can link, unlink, or add bank accounts here.
                                        </p>
                                      </div>
                                    </div>
                                  )}
                                  <BankAccountAccordion
                                    banks={visibleBanks}
                                    pendingIds={pendingIds}
                                    isEditing={isEditingAccounts}
                                    fieldsReadOnly
                                    onUnlink={(baId) =>
                                      handleRemoveBankAccount(account.id, baId)
                                    }
                                    onAdd={() =>
                                      handleChooseOtherBankAccount(account.id)
                                    }
                                    addLabel="Link / Add Bank Account"
                                  />
                                  {!isEditingAccounts && (
                                    <div className="flex justify-center">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setIsEditingAccounts(true);
                                          handleChooseOtherBankAccount(
                                            account.id,
                                          );
                                        }}
                                        className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors"
                                      >
                                        <svg
                                          className="w-4 h-4"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 4v16m8-8H4"
                                          />
                                        </svg>
                                        Link / Add Bank Account
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()
            ) : (
              <>
                <div className="flex items-center justify-end gap-2 border-b border-gray-200 pb-2">
                  <div className="flex gap-2 shrink-0">
                    {isEditingAccounts ? (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditingAccounts(false);
                            setValidationErrors({});
                            setAccountsFormData({});
                            setRemovedBeneficiaries({});
                            setRemovedTrustedContacts({});
                            setPendingNewDeposits({});
                            setPendingNewWithdrawals({});
                            setRemovedDeposits({});
                            setRemovedWithdrawals({});
                            setNewlyAddedWithdrawalBankAccounts({});
                            setBeneficiaryValidationErrorsByAccount({});
                          }}
                          className="btn-secondary text-xs px-3 py-2 flex items-center gap-1.5"
                        >
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveAccounts}
                          disabled={
                            savingAccounts ||
                            Object.keys(validationErrors).length > 0
                          }
                          className="btn-primary text-xs px-3 py-2 flex items-center gap-1.5 disabled:opacity-50"
                        >
                          {savingAccounts ? (
                            <>
                              <svg
                                className="animate-spin w-3.5 h-3.5"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              Saving...
                            </>
                          ) : (
                            <>
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                              Save
                            </>
                          )}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setShowAddAccountModal(true)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs px-3 py-2 flex items-center gap-1.5 transition-colors"
                        >
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4v16m8-8H4"
                            />
                          </svg>
                          Add Account
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsEditingAccounts(true)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs px-3 py-2 flex items-center gap-1.5 transition-colors"
                        >
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                          Edit
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No Accounts Found
                  </h3>
                  <p className="text-gray-600">
                    This household doesn't have any accounts yet.
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === "bank_accounts" && (
          <div className="space-y-4">
            {(() => {
              const bankList = Array.isArray(householdBankAccounts)
                ? householdBankAccounts
                : householdBankAccounts
                  ? [householdBankAccounts]
                  : [];
              return (
                <>
                  <div className="flex items-center gap-2 border-b border-gray-200 pb-2">
                    {bankList.length > 0 ? (
                      <div
                        className="min-w-0 flex-1 overflow-x-auto overscroll-x-contain touch-pan-x [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                      >
                        <div className="flex w-max flex-nowrap gap-2 pb-0.5 pr-1">
                          {bankList.map((ba, chipIdx) => {
                            const chipId = ba.id ?? chipIdx;
                            const chipExpanded = expandedBankAccounts[chipId];
                            return (
                              <button
                                key={chipId}
                                type="button"
                                onClick={() => toggleBankAccount(chipId)}
                                className={`shrink-0 px-3 py-2 rounded-t-lg text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                                  chipExpanded
                                    ? "bg-emerald-100 text-emerald-800 border-emerald-600"
                                    : "border-transparent bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                              >
                                {ba.bank_name || "Bank"} – ****
                                {ba.account_number?.slice(-4) || "N/A"}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="min-w-0 flex-1" />
                    )}
                    <div className="flex shrink-0 items-center gap-2 border-l border-gray-200 pl-2">
                      <button
                        type="button"
                        onClick={() => setShowAddBankAccountModal(true)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs px-3 py-2 flex items-center gap-1.5 transition-colors"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                        Add Bank Account
                      </button>
                    </div>
                  </div>
                  {!bankList.length ? (
                    <div className="text-center py-8 text-gray-600 text-sm">
                      No bank accounts linked yet.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {bankList.map((bankAccount) => {
                    const bankAccountId = bankAccount.id;
                    const isExpanded = expandedBankAccounts[bankAccountId];
                    const accountTypeLabel =
                      canonicalBankAccountType(bankAccount.account_type) ||
                      "N/A";
                    const isPersonalLabel =
                      bankAccount.is_personal === true
                        ? "Personal"
                        : bankAccount.is_personal === false
                          ? "Business"
                          : "N/A";

                    return (
                      <div
                        key={bankAccountId}
                        className="border border-gray-200 rounded-lg"
                      >
                        <div className="flex items-center justify-between w-full">
                          <button
                            onClick={() => toggleBankAccount(bankAccountId)}
                            className="flex-1 px-4 py-3 bg-emerald-50 hover:bg-emerald-100 flex items-center justify-between transition-colors rounded-t-lg text-left"
                          >
                            <div className="flex items-center gap-2">
                              <svg
                                className="w-5 h-5 text-emerald-600 shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                                />
                              </svg>
                              <span className="font-medium text-gray-900">
                                {bankAccount.bank_name || "Bank"} – ****
                                {bankAccount.account_number?.slice(-4) || "N/A"}
                              </span>
                            </div>
                            <svg
                              className={`w-5 h-5 text-gray-500 transition-transform shrink-0 ${isExpanded ? "rotate-180" : ""}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setBankAccountToEdit(bankAccount);
                              setShowEditBankAccountModal(true);
                            }}
                            disabled={deletingBankAccountId === bankAccountId}
                            className="p-3 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 transition-colors shrink-0 disabled:opacity-50"
                            title="Edit bank account"
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                              />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteBankAccount(bankAccountId);
                            }}
                            disabled={deletingBankAccountId === bankAccountId}
                            className="p-3 text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors rounded-r-lg shrink-0 disabled:opacity-50"
                            title="Delete bank account"
                          >
                            {deletingBankAccountId === bankAccountId ? (
                              <svg
                                className="w-5 h-5 animate-spin"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                            ) : (
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            )}
                          </button>
                        </div>
                        {isExpanded && (
                          <div className="p-4 border-t border-gray-200 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-500 mb-0.5">
                                  Bank Name
                                </label>
                                <p className="text-gray-900">
                                  {bankAccount.bank_name || "N/A"}
                                </p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-500 mb-0.5">
                                  Account Name
                                </label>
                                <p className="text-gray-900">
                                  {bankAccount.account_name || "N/A"}
                                </p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-500 mb-0.5">
                                  Account Number
                                </label>
                                <p className="text-gray-900">
                                  {bankAccount.account_number || "N/A"}
                                </p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-500 mb-0.5">
                                  Routing Number
                                </label>
                                <p className="text-gray-900">
                                  {bankAccount.routing_number || "N/A"}
                                </p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-500 mb-0.5">
                                  Account Type
                                </label>
                                <p className="text-gray-900">
                                  {accountTypeLabel}
                                </p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-500 mb-0.5">
                                  Link Type
                                </label>
                                <p className="text-gray-900">
                                  {bankAccount.link_type || "N/A"}
                                </p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-500 mb-0.5">
                                  Account Category
                                </label>
                                <p className="text-gray-900">
                                  {isPersonalLabel}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Missing Household Field Modal */}
      <MissingHouseholdFieldModal
        isOpen={showMissingFieldModal}
        onClose={() => {
          setShowMissingFieldModal(false);
          setSelectedMissingField(null);
        }}
        selectedField={selectedMissingField}
        household={household}
        missingFields={missingFields}
        brokerageFlags={brokerageFlags}
        onHouseholdUpdate={onHouseholdUpdate}
        onSaveAndNext={() =>
          handleSaveAndNext("household", null, selectedMissingField)
        }
      />

      {/* Missing Member Field Modal */}
      {showMissingMemberFieldModal && selectedMemberId && (
        <MissingMemberFieldModal
          isOpen={showMissingMemberFieldModal}
          onClose={() => {
            setShowMissingMemberFieldModal(false);
            setSelectedMemberField(null);
            setSelectedMemberId(null);
          }}
          selectedField={selectedMemberField}
          memberId={selectedMemberId}
          member={household?.members?.find((m) => m.id === selectedMemberId)}
          household={household}
          brokerageFlags={brokerageFlags}
          onHouseholdUpdate={onHouseholdUpdate}
          onSaveAndNext={() =>
            handleSaveAndNext("member", selectedMemberId, selectedMemberField)
          }
        />
      )}

      {/* Missing Account Field Modal */}
      {showMissingAccountFieldModal && selectedAccountId && (
        <MissingAccountFieldModal
          isOpen={showMissingAccountFieldModal}
          onClose={() => {
            setShowMissingAccountFieldModal(false);
            setSelectedAccountField(null);
            setSelectedAccountId(null);
            setSelectedBankAccountId(null);
          }}
          selectedField={selectedAccountField}
          account={household?.accounts?.find((a) => a.id === selectedAccountId)}
          household={household}
          custodian={custodian}
          brokerageFlags={brokerageFlags}
          bankAccountId={selectedBankAccountId}
          onHouseholdUpdate={onHouseholdUpdate}
          onSaveAndNext={() =>
            handleSaveAndNext("account", selectedAccountId, selectedAccountField)
          }
        />
      )}

      {/* Select Member Type Modal */}
      <SelectMemberTypeModal
        isOpen={showSelectMemberTypeModal}
        onClose={() => {
          setShowSelectMemberTypeModal(false);
          setAccountForBeneficiary(null);
          setMemberTypeModalType(null);
          setBeneficiaryPreSelectRole(null);
        }}
        onSelectExisting={handleSelectExistingMember}
        onAddNew={handleAddNewMember}
        existingMembers={household?.members || []}
        accountOwnerIds={
          household?.accounts
            ?.find((acc) => acc.id === accountForBeneficiary)
            ?.owners_info?.map((owner) => owner.id) || []
        }
        excludeMemberIds={
          memberTypeModalType === "beneficiary"
            ? beneficiaryMemberIdsExcludedFromSelectModal
            : memberTypeModalType === "trusted_contact"
              ? trustedContactMemberIdsExcludedFromSelectModal
              : []
        }
        type={memberTypeModalType}
      />

      {/* Add Member Modal */}
      <AddMemberModal
        isOpen={showAddMemberModal}
        onClose={() => {
          setShowAddMemberModal(false);
          setAccountForBeneficiary(null);
        }}
        household={household}
        onSuccess={(memberId) => {
          setShowAddMemberModal(false);
          setNewlyCreatedMemberId(memberId);
          setShowBeneficiaryTrustedModal(true);
          // Don't clear accountForBeneficiary here - it will be used by the next modal
        }}
        onHouseholdUpdate={onHouseholdUpdate}
      />

      {/* Add Account Modal */}
      <AddAccountModal
        isOpen={showAddAccountModal}
        onClose={() => setShowAddAccountModal(false)}
        household={household}
        custodianTypes={custodianTypes}
        brokerageFlags={brokerageFlags}
        onAccountAdded={(updatedHousehold) => {
          setShowAddAccountModal(false);
          if (onHouseholdUpdate) onHouseholdUpdate(updatedHousehold || null);
        }}
      />

      <ResolveNigoAccountsModal
        isOpen={showResolveNigoModal}
        onClose={() => setShowResolveNigoModal(false)}
        householdId={household?.id}
        accounts={household?.accounts}
        members={household?.members}
        onSuccess={() => {
          setShowResolveNigoModal(false);
          if (onHouseholdUpdate) onHouseholdUpdate(null);
        }}
      />

      {/* Beneficiary/Trusted Contact Modal */}
      <AddMemberBeneficiaryTrustedModal
        isOpen={showBeneficiaryTrustedModal}
        onClose={() => {
          setShowBeneficiaryTrustedModal(false);
          setNewlyCreatedMemberId(null);
          setAccountForBeneficiary(null);
          setBeneficiaryPreSelectRole(null);
        }}
        household={household}
        newlyCreatedMemberId={newlyCreatedMemberId}
        onHouseholdUpdate={onHouseholdUpdate}
        preSelectedAccountId={accountForBeneficiary}
        preSelectRole={beneficiaryPreSelectRole}
      />

      {/* Add Bank Account Modal */}
      <AddBankAccountModal
        isOpen={showAddBankAccountModal}
        onClose={() => {
          setShowAddBankAccountModal(false);
          // If we were in the middle of linking, reopen the select modal
          if (accountForBankLinking) {
            setShowSelectBankAccountModal(true);
          }
        }}
        household={household}
        onSuccess={(bankAccountId, bankAccountName) => {
          setShowAddBankAccountModal(false);
          if (bankAccountId) {
            // Check if we're adding from bank linking flow or from Bank Accounts tab
            if (accountForBankLinking) {
              handleBankAccountAdded(bankAccountId, bankAccountName);
            } else {
              setNewBankAccountId(bankAccountId);
              setNewBankAccountName(bankAccountName);
              setShowLinkBankAccountModal(true);
            }
          } else {
            if (onHouseholdUpdate) {
              onHouseholdUpdate(null);
            }
          }
        }}
      />

      {/* Edit Bank Account Modal */}
      <EditBankAccountModal
        isOpen={showEditBankAccountModal}
        onClose={() => {
          setShowEditBankAccountModal(false);
          setBankAccountToEdit(null);
        }}
        household={household}
        bankAccount={bankAccountToEdit}
        onSuccess={() => {
          if (onHouseholdUpdate) onHouseholdUpdate(null);
        }}
      />

      {/* Link Bank Account Modal */}
      <LinkBankAccountModal
        isOpen={showLinkBankAccountModal}
        onClose={() => {
          setShowLinkBankAccountModal(false);
          setNewBankAccountId(null);
          setNewBankAccountName("");
          setAccountForBankLinking(null);
        }}
        household={household}
        bankAccountId={newBankAccountId}
        bankAccountName={newBankAccountName}
        preSelectedAccountId={accountForBankLinking}
        onSuccess={handleBankAccountLinked}
      />

      {/* Select Bank Account Modal */}
      <SelectBankAccountModal
        isOpen={showSelectBankAccountModal}
        onClose={() => {
          setShowSelectBankAccountModal(false);
          setAccountForBankLinking(null);
        }}
        onSelectExisting={handleSelectExistingBankAccount}
        onAddNew={handleAddNewBankAccount}
        bankAccounts={household?.bank_accounts || []}
      />

      {/* Add Contribution Modal */}
      <AddContributionModal
        isOpen={showAddContributionModal}
        onClose={() => {
          setShowAddContributionModal(false);
          setAccountForMoneyMovement(null);
        }}
        account={accountForMoneyMovement}
        onSuccess={(payload) => {
          if (accountForMoneyMovement?.id) {
            handleAddContributionLocal(accountForMoneyMovement.id, payload);
          }
        }}
      />

      {/* Add Withdrawal Modal */}
      <AddWithdrawalModal
        isOpen={showAddWithdrawalModal}
        onClose={() => {
          setShowAddWithdrawalModal(false);
          setAccountForMoneyMovement(null);
        }}
        household={household}
        account={accountForMoneyMovement}
        onSuccess={(payload) => {
          if (accountForMoneyMovement?.id) {
            handleAddWithdrawalLocal(accountForMoneyMovement.id, payload);
          }
        }}
        onHouseholdUpdate={onHouseholdUpdate}
      />

      {/* Withdrawal Bank Account Selection Modal */}
      <SelectBankAccountModal
        isOpen={showWithdrawalBankSelectModal}
        onClose={() => {
          setShowWithdrawalBankSelectModal(false);
          setWithdrawalForBankSelection(null);
        }}
        onSelectExisting={handleWithdrawalSelectExistingBank}
        onAddNew={handleWithdrawalAddNewBank}
        bankAccounts={household?.bank_accounts || []}
      />

      {/* Withdrawal Add Bank Account Modal */}
      <AddBankAccountModal
        isOpen={showWithdrawalAddBankModal}
        onClose={() => {
          setShowWithdrawalAddBankModal(false);
          // Reopen select modal if user cancels
          setShowWithdrawalBankSelectModal(true);
        }}
        household={household}
        onSuccess={handleWithdrawalBankAdded}
      />

      {/* Link Unlinked Bank Account Modal */}
      <LinkBankAccountModal
        isOpen={showLinkUnlinkedBankModal}
        onClose={() => setShowLinkUnlinkedBankModal(false)}
        household={household}
        bankAccountId={selectedUnlinkedBankAccountId}
        bankAccountName={selectedUnlinkedBankName}
        onSuccess={(data) => {
          setShowLinkUnlinkedBankModal(false);
          if (onHouseholdUpdate) onHouseholdUpdate(data ?? null);
        }}
      />

      {/* Link Unlinked Member Modal */}
      <AddMemberBeneficiaryTrustedModal
        isOpen={showLinkUnlinkedMemberModal}
        onClose={() => {
          setShowLinkUnlinkedMemberModal(false);
          setSelectedUnlinkedMemberId(null);
        }}
        household={household}
        newlyCreatedMemberId={selectedUnlinkedMemberId}
        onHouseholdUpdate={(data) => {
          setShowLinkUnlinkedMemberModal(false);
          setSelectedUnlinkedMemberId(null);
          if (onHouseholdUpdate) onHouseholdUpdate(data ?? null);
        }}
      />
    </div>
  );
}

export { DataField };
export default HouseholdContent;
